import http from "./http";
import {
  cachedTender360LookupGet,
  cachedTender360LookupPost,
  cachedTender360Post,
  TENDER360_FORCE_REFRESH_KEY,
} from "../store/tender360Client";
import { fetchUserActivePlanDetails } from "./auth";
import { VALUE_FILTER_OPTIONS, WORD_SEARCH_TYPE, ZONE_OPTIONS } from "../utils/constants";
import { parseBriefInputToList } from "../utils/helperFn";
import { indianStatesAndCities, indianStates } from "../utils/locationConstants";

/** Default category value; do not send to similar-tenders (or other APIs that should not filter by it). */
const DEFAULT_CATEGORY_WORKS = "Works";

/**
 * Returns category array with default "Works" and empty values removed. Use for similar-tenders etc.
 * @param {string|string[]|null|undefined} category
 * @returns {string[]|undefined} Filtered array or undefined if nothing left
 */
export function filterDefaultCategory(category) {
  if (category == null) return undefined;
  const arr = Array.isArray(category) ? category : [category];
  const filtered = arr.filter((c) => {
    if (c == null) return false;
    const s = String(c).trim();
    return s !== "" && s.toLowerCase() !== DEFAULT_CATEGORY_WORKS.toLowerCase();
  });
  return filtered.length > 0 ? filtered : undefined;
}

/**
 * Advanced search "Sub-Category" (payload.subcategory / URL subCategory) maps to API `category`.
 * Used as a string for POST /tenders/advanced-filter; wrap in `[value]` for POST /statistics/summary/.
 * Falls back to `category` when sub-category is empty.
 * @param {object} cleanPayload
 * @returns {string|undefined}
 */
function resolveAdvancedFilterCategoryString(cleanPayload) {
  if (!cleanPayload || typeof cleanPayload !== "object") return undefined;
  const rawSubcategoryForCategory =
    cleanPayload.subcategory ?? cleanPayload.sub_category ?? cleanPayload.subCategory;
  const stringFromSubcategoryField = (() => {
    const v = rawSubcategoryForCategory;
    if (v == null || v === "") return "";
    if (Array.isArray(v)) {
      const first = v.find((x) => x != null && String(x).trim() !== "");
      if (first == null) return "";
      if (typeof first === "string") return first.trim();
      if (first && typeof first === "object") {
        return String(first.value ?? first.label ?? first).trim();
      }
      return String(first).trim();
    }
    if (typeof v === "object") {
      return String(v.value ?? v.label ?? v).trim();
    }
    if (typeof v === "string") return v.trim();
    return String(v).trim();
  })();
  const stringFromCategoryField = (() => {
    const c = cleanPayload.category;
    if (c == null || c === "") return "";
    if (typeof c === "string") return c.trim();
    if (Array.isArray(c)) {
      const first = c.find((x) => x != null && String(x).trim() !== "");
      if (first == null) return "";
      if (typeof first === "string") return first.trim();
      if (first && typeof first === "object") {
        return String(first.value ?? first.label ?? first).trim();
      }
      return String(first).trim();
    }
    if (typeof c === "object") {
      return String(c.value ?? c.label ?? c).trim();
    }
    return String(c).trim();
  })();
  const merged = stringFromSubcategoryField || stringFromCategoryField;
  return merged || undefined;
}

function normalizeAdvancedFilterStringValue(value) {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeAdvancedFilterStringValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    return String(value.value ?? value.label ?? value).trim();
  }
  return String(value).trim();
}

function normalizeAdvancedFilterStringList(value) {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeAdvancedFilterStringList(item))
      .filter(Boolean);
  }
  if (typeof value === "object") {
    const normalized = String(value.value ?? value.label ?? "").trim();
    return normalized ? [normalized] : [];
  }
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Tender value range for APIs: coerces to non-negative finite numbers; omits when unset or both zero.
 * @param {object} cleanPayload
 * @returns {{ tender_value_min: number, tender_value_max: number } | null}
 */
function normalizedNonNegativeTenderValuePair(cleanPayload) {
  const minRaw = cleanPayload?.tender_value_min;
  const maxRaw = cleanPayload?.tender_value_max;
  if (typeof minRaw !== "number" || typeof maxRaw !== "number") return null;
  if (!Number.isFinite(minRaw) || !Number.isFinite(maxRaw)) return null;
  const tender_value_min = Math.max(0, minRaw);
  const tender_value_max = Math.max(0, maxRaw);
  if (tender_value_min <= 0 && tender_value_max <= 0) return null;
  return { tender_value_min, tender_value_max };
}

function normalizeOptionalNonNegativeNumber(value) {
  if (value === null || value === undefined || value === "") return undefined;
  const numericValue =
    typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(numericValue) || numericValue < 0) return undefined;
  return numericValue;
}

export async function fetchTenders(options = {}) {
  // Default uses search endpoint with a generic category
  let params = { page: 1, limit: 100, search_term: "" };
  let shouldSliceResult = false;

  if (typeof options === "number") {
    params.limit = options;
    shouldSliceResult = true;
  } else if (options && typeof options === "object") {
    params = {
      ...params,
      ...options,
    };

    // Map any provided category/searchTerm to search_term for the API
    if (options.category && !options.search_term) {
      params.search_term = options.category;
    }
    if (options.searchTerm && !options.search_term) {
      params.search_term = options.searchTerm;
    }
  }

  // Use the new search endpoint
  const response = await http.get("/tenders/search", { params });
  const rawData = response.data;

  let items = [];
  if (Array.isArray(rawData)) {
    items = rawData;
  } else if (Array.isArray(rawData?.results)) {
    items = rawData.results;
  } else if (Array.isArray(rawData?.data)) {
    items = rawData.data;
  }

  if (shouldSliceResult) {
    return items.slice(0, params.limit);
  }

  return items;
}

export async function fetchSellerDetailsByCompany(companyName) {
  const cname = String(companyName || "").trim();
  if (!cname) return null;
  const response = await http.get("https://api.tenderapex.com/seller-details/by-company", {
    params: { cname },
  });
  return response?.data ?? null;
}

function normalizeTenderSummaryRows(items, criteria) {
  if (!Array.isArray(items) || items.length === 0) return [];
  if (!["ownership", "state", "department"].includes(criteria)) return items;

  const hasSplitCounts = items.some(
    (item) => item && ("live_count" in item || "past_year_count" in item),
  );

  const amountFor = (item, key, hasSplit) => {
    if (hasSplit) {
      return Number(item?.[key === "live_count" ? "live_total_amount" : "past_year_total_amount"]) || 0;
    }
    return Number(item?.total_amount) || 0;
  };

  const bestBy = (key, matchRow = () => true) =>
    items.reduce((best, item, index) => {
      const count = Number(item?.[key]) || 0;
      if (!matchRow(item) || count <= 0) return best;
      const amount = amountFor(item, key, hasSplitCounts);
      if (
        !best ||
        count > best.count ||
        (count === best.count && amount > best.amount)
      ) {
        return { item, index, count, amount };
      }
      return best;
    }, null);

  const toRow = (match, isLive) => {
    if (!match) return null;
    const { item, index, count } = match;
    const label =
      criteria === "state"
        ? item.state
        : criteria === "department"
          ? item.department
        : item.organization || item.ownership;
    const rowLabel = label || `summary-${index + 1}`;
    const p = isLive ? "live" : "past_year";
    const categoryField =
      criteria === "ownership" ? "organization" : criteria;
    return {
      ...item,
      id: `${rowLabel}-${isLive ? "live" : "past-year"}-${index}`,
      results: isLive ? "Live" : "Last Year Tenders",
      count,
      total_amount: hasSplitCounts
        ? item[`${p}_total_amount`] ?? item.total_amount ?? 0
        : item.total_amount ?? 0,
      highest_amount: hasSplitCounts
        ? item[`${p}_highest_amount`] ?? item.highest_amount ?? 0
        : item.highest_amount ?? 0,
      l1_winner: isLive ? "TBA" : item.past_year_l1_winner ?? item.l1_winner ?? "N/A",
      [categoryField]: rowLabel,
    };
  };

  if (hasSplitCounts) {
    return [toRow(bestBy("live_count"), true), toRow(bestBy("past_year_count"), false)].filter(Boolean);
  }

  const isLiveRow = (item) => String(item?.results || "").trim().toLowerCase() === "live";
  const isPastYearRow = (item) => {
    const result = String(item?.results || "").trim().toLowerCase();
    return result.includes("past") || result.includes("last year");
  };

  return [toRow(bestBy("count", isLiveRow), true), toRow(bestBy("count", isPastYearRow), false)].filter(Boolean);
}

// Fetch summary statistics based on category (keyword) and criteria
// Uses the same payload structure as advancedFilterTenders
export async function fetchTenderSummary(category, criteria, options = {}) {
  if (!criteria) {
    return [];
  }

  // Remove internal tracking fields (like _timestamp) before building body
  const { _timestamp, ...cleanPayload } = options;

  // Build payload matching advancedFilterTenders structure
  const apexidValue = cleanPayload.apexid || cleanPayload.apex_id || cleanPayload.apexId;
  const globalSearchValue =
    cleanPayload.global_search && typeof cleanPayload.global_search === "string"
      ? cleanPayload.global_search.trim()
      : (category && String(category).trim() !== "" ? String(category).trim() : "");
  const rawKeywordValue = cleanPayload.keyword ?? cleanPayload.search_term ?? "";
  const keywordList = Array.isArray(rawKeywordValue)
    ? rawKeywordValue
        .map((k) => String(k ?? "").trim())
        .filter(Boolean)
    : String(rawKeywordValue)
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
  const searchWithinValue = cleanPayload.search_within && typeof cleanPayload.search_within === "string" && cleanPayload.search_within.trim() !== "" ? cleanPayload.search_within.trim() : undefined;
  // /statistics/summary/ expects category as string[] (same values as advanced-filter category string).
  const categoryResolved = resolveAdvancedFilterCategoryString(cleanPayload);
  const categoryForSummary = categoryResolved ? [categoryResolved] : undefined;

  // Helper function to capitalize zone names
  const capitalizeZone = (zone) => {
    if (!zone) return undefined;
    const zoneStr = typeof zone === 'string' ? zone : (zone.value || zone.label || String(zone));
    if (!zoneStr) return undefined;
    const zoneLower = zoneStr.toLowerCase().trim();
    const zoneMap = {
      'east': 'East',
      'west': 'West',
      'north': 'North',
      'south': 'South'
    };
    return zoneMap[zoneLower] || zoneStr.charAt(0).toUpperCase() + zoneStr.slice(1).toLowerCase();
  };

  // Helper to extract ownership value and convert to array if needed
  const extractOwnership = () => {
    const ownershipValue = cleanPayload.ownership || cleanPayload.authorityOwnership;
    if (!ownershipValue) return undefined;

    if (Array.isArray(ownershipValue)) {
      const arr = ownershipValue
        .filter(Boolean)
        .map((o) => (typeof o === "string" ? o : o?.value ?? o?.label ?? String(o)).trim())
        .filter((o) => o !== "");
      return arr.length > 0 ? arr : undefined;
    }

    let ownershipStr;
    if (typeof ownershipValue === 'string' && ownershipValue.trim() !== "") {
      ownershipStr = ownershipValue.trim();
    } else if (ownershipValue && typeof ownershipValue === 'object') {
      ownershipStr = (ownershipValue.value || ownershipValue.label || String(ownershipValue)).trim();
    } else {
      ownershipStr = String(ownershipValue).trim();
    }

    // Return as array (as per API spec)
    return ownershipStr ? [ownershipStr] : undefined;
  };

  // Helper to extract statezones and convert to array
  const extractStatezones = () => {
    const zoneValue = cleanPayload.statezones || cleanPayload.zone;
    if (!zoneValue) return undefined;

    let zoneStr;
    if (typeof zoneValue === 'string') {
      zoneStr = capitalizeZone(zoneValue);
    } else if (zoneValue && typeof zoneValue === 'object') {
      const z = zoneValue.value || zoneValue.label;
      zoneStr = z ? capitalizeZone(z) : undefined;
    } else {
      zoneStr = capitalizeZone(String(zoneValue));
    }

    // Return as array (as per API spec)
    return zoneStr ? [zoneStr] : undefined;
  };

  const body = {
    // Criteria is required for summary endpoint
    criteria: criteria,
    // Keep search_term only for non-global-search flows.
    search_term:
      !globalSearchValue && keywordList.length > 0
        ? keywordList.join(", ")
        : undefined,
    // New global search field for home/global search flows.
    global_search: globalSearchValue || undefined,
    // Basic fields matching advancedFilterTenders
    tender_type: cleanPayload.tender_type || cleanPayload.type || cleanPayload.select_type || cleanPayload.selectType,
    apexid: apexidValue,
    // If search_within is provided, always include keyword (if provided) along with search_within
    // Otherwise, if apexid is provided, don't include keyword (apexid takes priority)
    ...(!globalSearchValue && searchWithinValue ? (
      keywordList.length > 0 ? {
        keyword: keywordList,
        ...(cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type ? {
          keyword_search_type: cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type
        } : {})
      } : {}
    ) : (
      globalSearchValue || apexidValue ? {} : (keywordList.length > 0 ? {
        keyword: keywordList,
        ...(cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type ? {
          keyword_search_type: cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type
        } : {})
      } : {})
    )),
    // string[] â€” summary API contract; values match advanced-filter `category` (Sub-Category mapping).
    category: categoryForSummary,
    product:
      normalizeAdvancedFilterProductToArray(cleanPayload.product ?? cleanPayload.products) || undefined,
    tendering_authority: cleanPayload.tendering_authority || cleanPayload.authority,
    departments: Array.isArray(cleanPayload.departments) ? cleanPayload.departments :
      (Array.isArray(cleanPayload.department) ? cleanPayload.department : []),
    // Extract values from states array if they are objects (react-select)
    states: Array.isArray(cleanPayload.states)
      ? cleanPayload.states
        .filter(s => s !== null && s !== undefined)
        .map(s => {
          if (typeof s === 'string') return s.trim();
          if (s && typeof s === 'object') return (s.value || s.label || String(s)).trim();
          return String(s).trim();
        })
        .filter(s => s && s.length > 0)
      : [],
    // Extract values from cities array if they are objects (react-select)
    cities: Array.isArray(cleanPayload.cities)
      ? cleanPayload.cities
        .filter(c => c !== null && c !== undefined)
        .map(c => {
          if (typeof c === 'string') return c.trim();
          if (c && typeof c === 'object') return (c.value || c.label || String(c)).trim();
          return String(c).trim();
        })
        .filter(c => c && c.length > 0)
      : [],
    // statezones as array
    statezones: extractStatezones(),
    // ownership as array
    ownership: extractOwnership(),
    // Optional GEM flag; only include when explicitly provided
    isGem: typeof cleanPayload.isGem === "boolean" ? cleanPayload.isGem : undefined,
    // Only include tender_value_min/max when set; never send negatives.
    ...(normalizedNonNegativeTenderValuePair(cleanPayload) ?? {}),
    // Publication date range (new API fields)
    publication_start:
      cleanPayload.publication_date_start ||
      cleanPayload.publication_start ||
      cleanPayload.publication_date_from ||
      cleanPayload.publicationDateFrom,
    publication_end:
      cleanPayload.publication_date_end ||
      cleanPayload.publication_end ||
      cleanPayload.publication_date_to ||
      cleanPayload.publicationDateTo,
    closing_start: cleanPayload.closing_start || cleanPayload.closing_date_start || cleanPayload.closing_date_from || cleanPayload.closingDateFrom || cleanPayload.start_date,
    closing_end: cleanPayload.closing_end || cleanPayload.closing_date_end || cleanPayload.closing_date_to || cleanPayload.closingDateTo || cleanPayload.end_date,
    quantity_min: normalizeOptionalNonNegativeNumber(
      cleanPayload.quantity_min ?? cleanPayload.qtyMin,
    ),
    quantity_max: normalizeOptionalNonNegativeNumber(
      cleanPayload.quantity_max ?? cleanPayload.qtyMax,
    ),
    msme_exemption: typeof cleanPayload.msme_exemption === "boolean" ? cleanPayload.msme_exemption : undefined,
    startup_exemption: typeof cleanPayload.startup_exemption === "boolean" ? cleanPayload.startup_exemption : undefined,
    tender_documents_available: typeof cleanPayload.tender_documents_available === "boolean" ? cleanPayload.tender_documents_available : undefined,
    // Status filter: include only when explicitly provided by UI
    status_filter:
      typeof cleanPayload.status_filter === "number"
        ? cleanPayload.status_filter
        : undefined,
    // start_date and end_date for backward compatibility
    start_date: cleanPayload.start_date || cleanPayload.closing_start || cleanPayload.closing_date_from || cleanPayload.closingDateFrom,
    end_date: cleanPayload.end_date || cleanPayload.closing_end || cleanPayload.closing_date_to || cleanPayload.closingDateTo,
    search_within: searchWithinValue,
  };

  // Remove null/undefined/empty string values to keep payload clean
  // But preserve required fields: criteria, status_filter
  Object.keys(body).forEach(key => {
    // Always keep these required fields
    if (key === "criteria" || key === "status_filter") {
      if (key === "status_filter" && typeof body[key] !== "number") {
        body[key] = 2;
      }
    } else if (body[key] === null || body[key] === undefined || body[key] === "" ||
      (Array.isArray(body[key]) && body[key].length === 0)) {
      delete body[key];
    }
  });

  // Ensure required fields are always present with defaults
  if (!body.criteria) {
    body.criteria = criteria;
  }
  if (typeof body.status_filter !== "number") {
    body.status_filter = 2;
  }

  try {
    const response = await http.post("/statistics/summary/", body);
    const rawData = response?.data;

    if (Array.isArray(rawData)) {
      return normalizeTenderSummaryRows(rawData, criteria);
    }
    if (Array.isArray(rawData?.results)) {
      return normalizeTenderSummaryRows(rawData.results, criteria);
    }
    if (Array.isArray(rawData?.data)) {
      return normalizeTenderSummaryRows(rawData.data, criteria);
    }
    if (Array.isArray(rawData?.data?.data)) {
      return normalizeTenderSummaryRows(rawData.data.data, criteria);
    }

    return [];
  } catch (error) {
    console.error("Error fetching tender summary:", error);
    return [];
  }
}

/**
 * Fetch statistics from /statistics/dashboard/ for the dashboard Tender Statistics section.
 * POST body carries filters; page, limit, and optional userid are sent as query params.
 * @param {string} criteria - "state" | "department" | "value" | "ownership"
 * @param {object} options - Filter options (category, states, userid / user_id, etc.)
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Page size (default 50)
 * @returns {Promise<Array>} Array of rows (raw API data array)
 */
export async function fetchStatisticsDashboard(criteria, options = {}, page = 1, limit = 50) {
  if (!criteria) return [];
  const opt = options || {};
  const body = { criteria };
  const keywordList = normalizeStatisticsDashboardKeywordList(opt.keyword);

  if (
    opt.tender_type &&
    String(opt.tender_type).trim() !== "" &&
    String(opt.tender_type).trim() !== "string" &&
    String(opt.tender_type).trim().toLowerCase() !== "tenders"
  ) {
    body.tender_type = opt.tender_type;
  }
  if (opt.apexid && String(opt.apexid).trim() !== "") {
    body.apexid = opt.apexid.trim();
  }
  if (keywordList.length > 0) {
    body.keyword = keywordList;
  }
  if (
    keywordList.length > 0 &&
    opt.keyword_search_type &&
    String(opt.keyword_search_type).trim() !== ""
  ) {
    body.keyword_search_type = opt.keyword_search_type;
  }
  if (opt.search_within && String(opt.search_within).trim() !== "") {
    body.search_within = opt.search_within.trim();
  }
  const categoryInput = opt.category;
  if (Array.isArray(categoryInput) && categoryInput.length > 0) {
    body.category = categoryInput
      .map((c) => (typeof c === "string" ? c.trim() : (c?.value ?? c?.label ?? String(c)).trim()))
      .filter(Boolean);
  } else if (categoryInput && String(categoryInput).trim() !== "") {
    const single = typeof categoryInput === "string" ? categoryInput.trim() : String(categoryInput || "").trim();
    if (single) body.category = [single];
  }
  if (!body.category && !opt.__omitDefaultCategory) body.category = ["string"];
  if (opt.subcategory && String(opt.subcategory).trim() !== "") {
    body.subcategory = opt.subcategory.trim();
  }
  const statsProductList = normalizeAdvancedFilterProductToArray(opt.product ?? opt.products);
  if (statsProductList.length > 0) {
    body.product = statsProductList;
  }
  const briefList = parseBriefInputToList(opt.brief);
  if (briefList.length > 0) {
    body.brief = briefList;
  }
  if (opt.tendering_authority && String(opt.tendering_authority).trim() !== "") {
    body.tendering_authority = opt.tendering_authority.trim();
  }
  const departments = Array.isArray(opt.departments) ? opt.departments.filter(Boolean) : [];
  if (departments.length > 0) body.departments = departments;
  const states = Array.isArray(opt.states) ? opt.states.filter((s) => s != null && String(s).trim() !== "") : [];
  if (states.length > 0) body.states = states.map((s) => (typeof s === "object" && s?.value != null ? s.value : s));
  const cities = Array.isArray(opt.cities) ? opt.cities.filter(Boolean) : [];
  if (cities.length > 0) body.cities = cities;
  const statezones = Array.isArray(opt.statezones)
    ? opt.statezones.filter(Boolean)
    : (opt.statezones && String(opt.statezones).trim() !== "" ? [String(opt.statezones).trim()] : []);
  if (statezones.length > 0) body.statezones = statezones;
  const ownership = Array.isArray(opt.ownership)
    ? opt.ownership.filter(Boolean)
    : (opt.ownership && String(opt.ownership).trim() !== "" ? [String(opt.ownership).trim()] : []);
  if (ownership.length > 0) body.ownership = ownership;
  if (typeof opt.isGem === "boolean") body.isGem = opt.isGem;
  if (typeof opt.tender_value_min === "number" && opt.tender_value_min > 0) body.tender_value_min = opt.tender_value_min;
  if (typeof opt.tender_value_max === "number" && opt.tender_value_max > 0) body.tender_value_max = opt.tender_value_max;
  if (opt.publication_start && String(opt.publication_start).trim() !== "") body.publication_start = opt.publication_start.trim();
  if (opt.publication_end && String(opt.publication_end).trim() !== "") body.publication_end = opt.publication_end.trim();
  if (opt.closing_start && String(opt.closing_start).trim() !== "") body.closing_start = opt.closing_start.trim();
  if (opt.closing_end && String(opt.closing_end).trim() !== "") body.closing_end = opt.closing_end.trim();
  if (typeof opt.quantity_min === "number" && opt.quantity_min > 0) body.quantity_min = opt.quantity_min;
  if (typeof opt.quantity_max === "number" && opt.quantity_max > 0) body.quantity_max = opt.quantity_max;
  if (opt.msme_exemption === true) body.msme_exemption = true;
  if (opt.startup_exemption === true) body.startup_exemption = true;
  if (opt.tender_documents_available === true) body.tender_documents_available = true;
  if (opt.start_date && String(opt.start_date).trim() !== "") body.start_date = opt.start_date.trim();
  if (opt.end_date && String(opt.end_date).trim() !== "") body.end_date = opt.end_date.trim();

  const statsUserId = opt.userid ?? opt.user_id;
  const query = new URLSearchParams();
  query.set("page", String(page));
  query.set("limit", String(limit));
  if (statsUserId != null && String(statsUserId).trim() !== "") {
    query.set("userid", String(statsUserId).trim());
  }

  try {
    const response = await http.post(`/statistics/dashboard/?${query.toString()}`, body);
    const raw = response?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.results)) return raw.results;
    return [];
  } catch (error) {
    console.error("Error fetching statistics dashboard:", error);
    return [];
  }
}

function statisticsDashboardItemIsLegacyShape(item) {
  if (!item || typeof item !== "object") return false;
  return (
    "live_count" in item ||
    "past_year_count" in item ||
    "live_total_amount" in item ||
    "past_year_total_amount" in item ||
    "live_highest_amount" in item ||
    "past_year_highest_amount" in item
  );
}

/**
 * Map /statistics/dashboard/ API rows to table rows.
 * Supports legacy payloads (live_* / past_year_* on one object per category) and
 * current API (one object per row with results, count, total_amount, highest_amount).
 */
export function mapStatisticsDashboardToRows(data, criteria) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const categoryKey =
    criteria === "state"
      ? "state"
      : criteria === "department"
        ? "department"
        : criteria === "ownership"
          ? "organization"
          : "results";
  const legacy = data.some(statisticsDashboardItemIsLegacyShape);

  if (!legacy) {
    const rows = data.map((item, index) => {
      const name =
        item.state ??
        item.department ??
        item.organization ??
        item.ownership ??
        item.results ??
        `Row ${index + 1}`;
      const slug = String(item.results ?? name).replace(/\s+/g, "-");
      const resultsLabel = item.results ?? name;
      const isPastYearCategory =
        typeof resultsLabel === "string" && resultsLabel.toLowerCase().includes("past");
      const isLiveCategory =
        typeof resultsLabel === "string" && resultsLabel.trim().toLowerCase() === "live";
      const ownershipOrOrg =
        item.organization != null && String(item.organization).trim() !== ""
          ? String(item.organization).trim()
          : item.ownership != null && String(item.ownership).trim() !== ""
            ? String(item.ownership).trim()
            : "";
      const categoryValue =
        criteria === "state"
          ? item.state ?? resultsLabel
          : criteria === "department"
            ? item.department ?? resultsLabel
            : criteria === "ownership"
              ? ownershipOrOrg || undefined
              : resultsLabel;
      return {
        id: item.id != null ? String(item.id) : `${slug}-${index}`,
        results: resultsLabel,
        count: Number(item.count) || 0,
        total_amount: Number(item.total_amount) || 0,
        highest_amount: Number(item.highest_amount) || 0,
        state: item.state,
        department: item.department,
        organization: ownershipOrOrg || undefined,
        [categoryKey]: categoryValue,
        l1_winner: (() => {
          const rawL1 = item.l1_winner;
          const hasL1 =
            rawL1 != null &&
            rawL1 !== "" &&
            String(rawL1).trim().toLowerCase() !== "n/a";
          if (hasL1) return rawL1;
          if (isPastYearCategory) return "N/A";
          if (isLiveCategory) return "TBA";
          return null;
        })(),
      };
    });
    if (criteria === "value") {
      rows.sort((a, b) => {
        const ra = String(a.results || "").toLowerCase();
        const rb = String(b.results || "").toLowerCase();
        if (ra === "live") return rb === "live" ? 0 : -1;
        if (rb === "live") return 1;
        if (ra.includes("past")) return rb.includes("past") ? 0 : -1;
        if (rb.includes("past")) return 1;
        return 0;
      });
    }
    if (criteria === "ownership" || criteria === "state" || criteria === "department") {
      return normalizeTenderSummaryRows(rows, criteria);
    }
    return rows;
  }

  if (criteria === "value") {
    let liveCount = 0;
    let liveTotal = 0;
    let liveHighest = 0;
    let pastCount = 0;
    let pastTotal = 0;
    let pastHighest = 0;
    let pastL1 = null;
    data.forEach((item) => {
      liveCount += Number(item.live_count) || 0;
      liveTotal += Number(item.live_total_amount) || 0;
      liveHighest = Math.max(liveHighest, Number(item.live_highest_amount) || 0);
      pastCount += Number(item.past_year_count) || 0;
      pastTotal += Number(item.past_year_total_amount) || 0;
      pastHighest = Math.max(pastHighest, Number(item.past_year_highest_amount) || 0);
      if (
        item.past_year_l1_winner != null &&
        item.past_year_l1_winner !== "" &&
        item.past_year_l1_winner !== "N/A"
      ) {
        pastL1 = item.past_year_l1_winner;
      }
    });
    return [
      {
        id: "value-live",
        results: "Live",
        count: liveCount,
        total_amount: liveTotal,
        highest_amount: liveHighest,
        [categoryKey]: "Live",
        l1_winner: "TBA",
      },
      {
        id: "value-pastyear",
        results: "Past Year Results",
        count: pastCount,
        total_amount: pastTotal,
        highest_amount: pastHighest,
        [categoryKey]: "Past Year Results",
        l1_winner: pastL1 ?? "N/A",
      },
    ];
  }

  if (criteria === "ownership" || criteria === "state" || criteria === "department") {
    return normalizeTenderSummaryRows(data, criteria);
  }

  const rows = [];
  data.forEach((item, index) => {
    const name =
      criteria === "ownership"
        ? item.organization ??
          item.ownership ??
          item.state ??
          item.department ??
          item.results ??
          `Row ${index + 1}`
        : item.state ?? item.department ?? item.results ?? `Row ${index + 1}`;
    const idBase = `${String(name).replace(/\s+/g, "-")}-${index}`;
    const orgLabel =
      criteria === "ownership"
        ? item.organization != null && String(item.organization).trim() !== ""
          ? String(item.organization).trim()
          : item.ownership != null && String(item.ownership).trim() !== ""
            ? String(item.ownership).trim()
            : name
        : undefined;

    const liveRow = {
      id: `${idBase}-live`,
      results: "Live",
      count: item.live_count ?? 0,
      total_amount: item.live_total_amount ?? 0,
      highest_amount: item.live_highest_amount ?? 0,
      [categoryKey]: criteria === "ownership" ? orgLabel : name,
      state: item.state,
      department: item.department,
      l1_winner: "TBA",
    };
    const pastRow = {
      id: `${idBase}-pastyear`,
      results: "Past Year Results",
      count: item.past_year_count ?? 0,
      total_amount: item.past_year_total_amount ?? 0,
      highest_amount: item.past_year_highest_amount ?? 0,
      [categoryKey]: criteria === "ownership" ? orgLabel : name,
      state: item.state,
      department: item.department,
      l1_winner: item.past_year_l1_winner ?? "N/A",
    };
    if (criteria === "ownership" && orgLabel != null) {
      liveRow.organization = orgLabel;
      pastRow.organization = orgLabel;
    }
    rows.push(liveRow, pastRow);
  });
  return rows;
}

// Result statistics dashboard API criteria mapping (frontend -> API)
const RESULT_STATISTICS_CRITERIA_MAP = {
  value: "contract_value",
  state: "who_won_max",
  department: "state",
  ownership: "department",
};

/**
 * Fetch result statistics from /result-statistics/dashboard/ for the Result Statistics page.
 * Only sends fields that have values (no empty arrays/strings).
 * @param {string} criteria - "value" | "state" | "department" | "ownership"
 * @param {object} options - Filter options (category, states, departments, etc.)
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Page size (default 50)
 * @returns {Promise<Array>} Array of rows (raw API data)
 */
export async function fetchResultStatisticsDashboard(criteria, options = {}, page = 1, limit = 50) {
  if (!criteria) return [];

  const apiCriteria = RESULT_STATISTICS_CRITERIA_MAP[criteria] || criteria;
  const opt = options || {};
  const body = { criteria: apiCriteria };
  const keywordStr = Array.isArray(opt.keyword)
    ? opt.keyword
        .map((k) => String(k ?? "").trim())
        .filter(Boolean)
        .join(", ")
    : String(opt.keyword ?? "").trim();

  if (keywordStr !== "") body.keyword = keywordStr;
  if (opt.search_within && String(opt.search_within).trim() !== "") body.search_within = opt.search_within.trim();
  const departments = Array.isArray(opt.departments) ? opt.departments.filter(Boolean).map((d) => (typeof d === "object" ? d?.value ?? d?.label : d)?.trim?.() ?? String(d)).filter(Boolean) : [];
  if (departments.length > 0) body.departments = departments;
  const states = Array.isArray(opt.states) ? opt.states.filter(Boolean).map((s) => (typeof s === "object" ? s?.value ?? s?.label : s)?.trim?.() ?? String(s)).filter(Boolean) : [];
  if (states.length > 0) body.states = states;
  const cities = Array.isArray(opt.cities) ? opt.cities.filter(Boolean) : [];
  if (cities.length > 0) body.cities = cities;
  const categoryInput = opt.category;
  if (Array.isArray(categoryInput) && categoryInput.length > 0) {
    body.category = categoryInput.map((c) => (typeof c === "string" ? c.trim() : (c?.value ?? c?.label ?? String(c)).trim())).filter(Boolean);
  } else if (categoryInput && String(categoryInput).trim() !== "") {
    const single = typeof categoryInput === "string" ? categoryInput.trim() : String(categoryInput || "").trim();
    if (single) body.category = [single];
  }
  if (!body.category) body.category = ["string"];
  if (opt.tendersource && String(opt.tendersource).trim() !== "") body.tendersource = opt.tendersource.trim();
  const l1Bidders = Array.isArray(opt.l1_bidders) ? opt.l1_bidders.filter(Boolean) : [];
  if (l1Bidders.length > 0) body.l1_bidders = l1Bidders;
  const bidders = Array.isArray(opt.bidders) ? opt.bidders.filter(Boolean) : [];
  if (bidders.length > 0) body.bidders = bidders;
  if (typeof opt.contract_value_min === "number" && opt.contract_value_min > 0) body.contract_value_min = opt.contract_value_min;
  if (typeof opt.contract_value_max === "number" && opt.contract_value_max > 0) body.contract_value_max = opt.contract_value_max;
  if (opt.contract_date_start && String(opt.contract_date_start).trim() !== "") body.contract_date_start = opt.contract_date_start.trim();
  if (opt.contract_date_end && String(opt.contract_date_end).trim() !== "") body.contract_date_end = opt.contract_date_end.trim();
  if (typeof opt.year === "number") body.year = opt.year;
  if (typeof opt.aocstatus === "number") body.aocstatus = opt.aocstatus;
  if (opt.apexrid && String(opt.apexrid).trim() !== "") body.apexrid = opt.apexrid.trim();

  try {
    const response = await http.post(
      `/result-statistics/dashboard/?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`,
      body
    );
    const raw = response?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.results)) return raw.results;
    return [];
  } catch (error) {
    console.error("Error fetching result statistics dashboard:", error);
    return [];
  }
}

// Coalesce identical summary requests while they are in flight. This protects
// the results page from duplicate effects/callbacks without caching completed
// responses, so a later intentional refresh still reaches the API.
const inFlightResultStatisticsRequests = new Map();

// Fetch result statistics based on criteria
// Maps frontend criteria to API criteria:
// "value" -> "contract_value"
// "state" -> "who_won_max"
// "department" -> "state"
// "ownership" -> "department"
export async function fetchResultStatistics(criteria, options = {}) {
  if (!criteria) {
    return [];
  }

  // Map frontend criteria to API criteria
  const criteriaMap = {
    "value": "contract_value",
    "state": "who_won_max",
    "department": "state",
    "ownership": "department"
  };

  const apiCriteria = criteriaMap[criteria] || criteria;

  // Remove internal tracking fields
  const { _timestamp, ...cleanPayload } = options;

  // Build body using the same structure as advancedFilterTenderResults
  // Filter out "agency name" from departments (same as advanced filter)
  const departments = Array.isArray(cleanPayload.departments)
    ? cleanPayload.departments
      .map(dept => {
        // Handle react-select objects
        if (dept && typeof dept === 'object') {
          return dept.value || dept.label || String(dept);
        }
        // Handle strings
        return typeof dept === 'string' ? dept : String(dept);
      })
      .filter(dept => dept && typeof dept === 'string' && dept.trim() !== '')
      .map(dept => dept.trim())
    : [];

  // Handle category field - can be array or single value; never send default "Works"
  let categoryArray = [];
  if (cleanPayload.category) {
    if (Array.isArray(cleanPayload.category)) {
      categoryArray = cleanPayload.category;
    } else {
      categoryArray = [cleanPayload.category];
    }
  } else if (cleanPayload.categories) {
    if (Array.isArray(cleanPayload.categories)) {
      categoryArray = cleanPayload.categories;
    } else {
      categoryArray = [cleanPayload.categories];
    }
  }
  const categoryFiltered = filterDefaultCategory(categoryArray);

  const globalSearchValue =
    cleanPayload.global_search && typeof cleanPayload.global_search === "string"
      ? cleanPayload.global_search.trim()
      : "";
  const summaryKeywordList = normalizeAdvancedFilterStringList(
    cleanPayload.keyword || cleanPayload.search_term
  );
  const summarySubcategoryList = normalizeAdvancedFilterStringList(
    cleanPayload.subcategory ?? cleanPayload.subCategory ?? cleanPayload.sub_category
  );
  const isKeywordSubcategorySummaryRequest =
    summaryKeywordList.length > 0 &&
    summarySubcategoryList.length > 0 &&
    cleanPayload.keyword_search_scope === "keywords";
  const effectiveGlobalSearchValue = isKeywordSubcategorySummaryRequest
    ? ""
    : globalSearchValue;

  const extractStatezonesArray = () => {
    const zoneValue = cleanPayload.statezones ?? cleanPayload.zone;
    if (!zoneValue) return undefined;

    const normalizeZone = (z) => {
      const zoneStr =
        typeof z === "string" ? z : (z?.value ?? z?.label ?? String(z ?? ""));
      const normalized = String(zoneStr || "").trim().toLowerCase();
      if (!normalized) return "";
      const zoneMap = {
        east: "East",
        west: "West",
        north: "North",
        south: "South",
      };
      return zoneMap[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const list = Array.isArray(zoneValue) ? zoneValue : [zoneValue];
    const cleaned = [...new Set(list.map(normalizeZone).filter(Boolean))];
    return cleaned.length > 0 ? cleaned : undefined;
  };

  const body = {
    // Basic fields - same as advancedFilterTenderResults
    global_search: effectiveGlobalSearchValue || undefined,
    keyword:
      !effectiveGlobalSearchValue && summaryKeywordList.length > 0
        ? summaryKeywordList
        : undefined,
    category: categoryFiltered,
    subcategory: summarySubcategoryList,
    departments: departments,
    states: Array.isArray(cleanPayload.states) ? cleanPayload.states : [],
    cities: Array.isArray(cleanPayload.cities) ? cleanPayload.cities : [],
    statezones: extractStatezonesArray(),
    tendersource: cleanPayload.tendersource || cleanPayload.tender_source || cleanPayload.tenderSource || undefined,
    l1_bidders: Array.isArray(cleanPayload.l1_bidders) ? cleanPayload.l1_bidders : [],
    bidders: Array.isArray(cleanPayload.bidders) ? cleanPayload.bidders : [],
    contract_value_min: typeof cleanPayload.contract_value_min === "number" ? cleanPayload.contract_value_min : undefined,
    contract_value_max: typeof cleanPayload.contract_value_max === "number" ? cleanPayload.contract_value_max : undefined,
    publication_date_start: cleanPayload.publication_date_start || cleanPayload.publication_start || cleanPayload.publication_date_from || cleanPayload.publicationDateFrom || undefined,
    publication_date_end: cleanPayload.publication_date_end || cleanPayload.publication_end || cleanPayload.publication_date_to || cleanPayload.publicationDateTo || undefined,
    contract_date_start: cleanPayload.contract_date_start || cleanPayload.contractDateStart || undefined,
    contract_date_end: cleanPayload.contract_date_end || cleanPayload.contractDateEnd || undefined,
    year: typeof cleanPayload.year === "number" ? cleanPayload.year : undefined,
    aocstatus: typeof cleanPayload.aocstatus === "number" ? cleanPayload.aocstatus : undefined,
    sort_by: cleanPayload.sort_by || cleanPayload.sortBy || undefined,
    apexrid: cleanPayload.apexrid || cleanPayload.apex_rid || cleanPayload.apexRid || undefined,
    page: typeof cleanPayload.page === "number" ? cleanPayload.page : undefined,
    limit: typeof cleanPayload.limit === "number" ? cleanPayload.limit : undefined,
    search_within: cleanPayload.search_within && typeof cleanPayload.search_within === "string" && cleanPayload.search_within.trim() !== "" ? cleanPayload.search_within.trim() : undefined,
    // Add criteria only if provided
    criteria: apiCriteria,
  };

  // Remove null/undefined/empty string values
  // Keep criteria always (it's required by the API)
  // Keep page, limit, and sort_by even if they are default values
  Object.keys(body).forEach(key => {
    // Always keep criteria (required by API)
    if (key === "criteria") {
      return;
    }
    // Remove null/undefined/empty string values
    if (body[key] === null || body[key] === undefined || body[key] === "" ||
      (Array.isArray(body[key]) && body[key].length === 0)) {
      delete body[key];
    }
  });

  // Ensure criteria is always present (required by API)
  if (!body.criteria) {
    body.criteria = apiCriteria;
  }

  // Keep summary payload aligned with advanced-filter payload shape:
  // merge every explicit caller field unless it's an alternate alias already normalized above.
  const mergeSkip = new Set([
    "_timestamp",
    "search_term",
    "tender_source",
    "tenderSource",
    "publicationDateFrom",
    "publicationDateTo",
    "contractDateStart",
    "contractDateEnd",
    "sortBy",
    "apex_rid",
    "apexRid",
    "criteria",
    "statezones",
    "zone",
    "keyword",
    "category",
    "categories",
    "subcategory",
    "departments",
    "states",
    "cities",
    "l1_bidders",
    "bidders",
  ]);
  Object.keys(cleanPayload || {}).forEach((key) => {
    if (mergeSkip.has(key)) return;
    const value = cleanPayload[key];
    if (value === undefined) return;
    body[key] = value;
  });

  // Re-clean body after merge while always preserving mapped criteria.
  Object.keys(body).forEach((key) => {
    if (key === "criteria") return;
    if (
      body[key] === null ||
      body[key] === undefined ||
      body[key] === "" ||
      (Array.isArray(body[key]) && body[key].length === 0)
    ) {
      delete body[key];
    }
  });
  body.criteria = apiCriteria;

  if (isKeywordSubcategorySummaryRequest) {
    body.keyword = summaryKeywordList;
    body.subcategory = summarySubcategoryList;
    delete body.global_search;
    delete body.keyword_search_scope;
    delete body.search_term;
  }

  if (body.global_search) {
    delete body.keyword;
    delete body.search_term;
  }

  const requestKey = JSON.stringify(body);
  const existingRequest = inFlightResultStatisticsRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const request = (async () => {
    try {
      console.log("Calling /result-statistics/summary/ with payload:", body);
      const response = await http.post("/result-statistics/summary/", body);
      const rawData = response?.data;

      if (Array.isArray(rawData)) {
        return rawData;
      }
      if (Array.isArray(rawData?.results)) {
        return rawData.results;
      }
      if (Array.isArray(rawData?.data)) {
        return rawData.data;
      }

      return [];
    } catch (error) {
      console.error("Error fetching result statistics:", error);
      return [];
    } finally {
      inFlightResultStatisticsRequests.delete(requestKey);
    }
  })();

  inFlightResultStatisticsRequests.set(requestKey, request);
  return request;
}

// Function specifically for keyword-based tender searches
export async function searchTenders(keyword, options = {}) {
  let params = {
    page: 1,
    limit: 100,
    search_term: "" // default search term/category
  };

  // Use keyword as search_term if provided
  if (keyword && keyword.trim()) {
    params.search_term = keyword.trim();
  }

  if (options && typeof options === "object") {
    params = {
      ...params,
      ...options,
    };

    // Allow overriding via category/searchTerm fields as well
    if (options.category && !options.search_term) {
      params.search_term = options.category;
    }
    if (options.searchTerm && !options.search_term) {
      params.search_term = options.searchTerm;
    }
  }

  const response = await http.get("/tenders/search", { params });
  const rawData = response.data;

  let items = [];
  if (Array.isArray(rawData)) {
    items = rawData;
  } else if (Array.isArray(rawData?.results)) {
    items = rawData.results;
  } else if (Array.isArray(rawData?.data)) {
    items = rawData.data;
  }

  return items;
}

/**
 * Advanced filter for tenders based on multiple criteria (POST /tenders/advanced-filter).
 * @param {object} payload - Filter payload
 * @param {string[]|string|object|object[]} [payload.ownership] - Ownership filter; sent as string[] in the JSON body
 * @param {string|number|null|undefined} userId - Optional; when provided (e.g. after login), sent as user_id query param
 * @returns {Promise<Array>} List of tenders
 */
export async function advancedFilterTenders(payload = {}, userId) {
  // Remove internal tracking fields (like _timestamp) before building body
  const { _timestamp, ...cleanPayload } = payload;

  // Debug: Log ownership if present
  if (cleanPayload.ownership || cleanPayload.authorityOwnership) {
    console.log("ðŸ”µ Ownership in cleanPayload:", {
      ownership: cleanPayload.ownership,
      authorityOwnership: cleanPayload.authorityOwnership
    });
  }

  // Build a clean payload following the new API contract
  const apexidValue = cleanPayload.apexid || cleanPayload.apex_id || cleanPayload.apexId;
  const searchWithinValue = cleanPayload.search_within && typeof cleanPayload.search_within === "string" && cleanPayload.search_within.trim() !== "" ? cleanPayload.search_within.trim() : undefined;
  const globalSearchValue =
    cleanPayload.global_search && typeof cleanPayload.global_search === "string"
      ? cleanPayload.global_search.trim()
      : "";

  // Extract keyword value - CRITICAL: ensure it's NOT the same as search_within
  // If search_within is provided and keyword matches it, that means search_within was incorrectly set as keyword
  // In that case, we should NOT use it as keyword - keyword should come from a different source
  let keywordValue = normalizeAdvancedFilterStringValue(
    cleanPayload.keyword || cleanPayload.search_term,
  );

  // Debug: Log incoming payload values
  console.log("ðŸ”µ advancedFilterTenders - Incoming payload:", {
    keyword: cleanPayload.keyword,
    search_term: cleanPayload.search_term,
    global_search: cleanPayload.global_search,
    search_within: cleanPayload.search_within,
    keywordValue: keywordValue,
    globalSearchValue: globalSearchValue,
    searchWithinValue: searchWithinValue,
    apexidValue: apexidValue
  });

  // CRITICAL FIX: If search_within is provided and keyword matches it, ignore the keyword
  // This prevents search_within from being incorrectly used as keyword
  // The keyword should be separate from search_within
  if (searchWithinValue && keywordValue && keywordValue.trim() === searchWithinValue.trim()) {
    console.log("ðŸ”µ CRITICAL FIX: keyword matches search_within - this is incorrect!");
    console.log("ðŸ”µ search_within should be separate from keyword. Ignoring keyword to prevent duplication.");
    console.log("ðŸ”µ Expected: keyword='a', search_within='b'");
    console.log("ðŸ”µ Received: keyword='" + keywordValue + "', search_within='" + searchWithinValue + "'");
    keywordValue = undefined;
  }

  // Additional safeguard: If search_within exists but no valid keyword, try to get keyword from search_term
  // But only if search_term is different from search_within
  if (searchWithinValue && (!keywordValue || keywordValue.trim() === "")) {
    const searchTermValue = cleanPayload.search_term;
    if (searchTermValue && searchTermValue.trim() !== "" && searchTermValue.trim() !== searchWithinValue.trim()) {
      console.log("ðŸ”µ Using search_term as keyword since it's different from search_within");
      keywordValue = searchTermValue;
    } else {
      console.log("ðŸ”µ WARNING: No valid keyword found when search_within is provided. search_term also matches search_within or is empty.");
    }
  }

  // Prepare keyword and keyword_search_type (only if apexid/global_search is NOT provided)
  const keywordFields = apexidValue || globalSearchValue ? {} : (
    keywordValue && keywordValue.trim() !== "" && keywordValue.trim() !== "" ? {
      keyword: keywordValue.trim(),
      ...(cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type ? {
        keyword_search_type: cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type
      } : {})
    } : {}
  );

  // POST /tenders/advanced-filter expects ownership: string[]
  let ownershipArray = [];
  const rawOwnership = cleanPayload.ownership || cleanPayload.authorityOwnership;
  if (rawOwnership) {
    if (Array.isArray(rawOwnership)) {
      ownershipArray = rawOwnership
        .filter(Boolean)
        .map((o) => (typeof o === "string" ? o : o?.value ?? o?.label ?? String(o)).trim())
        .filter((o) => o !== "");
    } else if (typeof rawOwnership === "string" && rawOwnership.trim()) {
      ownershipArray = [rawOwnership.trim()];
    } else if (rawOwnership && typeof rawOwnership === "object") {
      const value = rawOwnership.value || rawOwnership.label;
      if (value != null && String(value).trim() !== "") {
        ownershipArray = [String(value).trim()];
      }
    }
  }
  const ownershipForApi = ownershipArray.length > 0 ? ownershipArray : undefined;

  const subcategoryForAdvancedFilter = normalizeAdvancedFilterStringValue(
    cleanPayload.subcategory ??
      cleanPayload.sub_category ??
      cleanPayload.subCategory,
  );
  const preserveSubcategoryForKeyword =
    cleanPayload.__preserveSubcategory === true &&
    keywordValue &&
    subcategoryForAdvancedFilter;
  const keywordSearchScope =
    cleanPayload.keyword_search_scope &&
    typeof cleanPayload.keyword_search_scope === "string" &&
    cleanPayload.keyword_search_scope.trim() !== ""
      ? cleanPayload.keyword_search_scope.trim()
      : preserveSubcategoryForKeyword
        ? "keywords"
        : undefined;
  const categoryForAdvancedFilter = preserveSubcategoryForKeyword
    ? undefined
    : resolveAdvancedFilterCategoryString(cleanPayload);

  const body = {
    // Basic fields
    tender_type: cleanPayload.tender_type || cleanPayload.type || cleanPayload.select_type || cleanPayload.selectType,
    apexid: apexidValue,
    global_search: globalSearchValue || undefined,
    // Keyword fields (only if apexid is NOT provided)
    ...keywordFields,
    keyword_search_scope: keywordSearchScope,
    // search_within: Always include if provided (separate from keyword)
    // Set directly like in advancedFilterTenderResults - this ensures it's always included when provided
    search_within: searchWithinValue,
    // Category: API string; Sub-Category field maps here (resolveAdvancedFilterCategoryString).
    category: categoryForAdvancedFilter,
    subcategory: preserveSubcategoryForKeyword
      ? subcategoryForAdvancedFilter
      : undefined,
    product:
      normalizeAdvancedFilterProductToString(cleanPayload.product ?? cleanPayload.products) || undefined,
    tendering_authority: cleanPayload.tendering_authority || cleanPayload.authority,
    ownership: ownershipForApi,
    departments: Array.isArray(cleanPayload.departments) ? cleanPayload.departments : [],
    // Bidders - array of strings (e.g. from URL bidders param from dashboard)
    bidders: Array.isArray(cleanPayload.bidders)
      ? cleanPayload.bidders
        .filter((b) => b !== null && b !== undefined)
        .map((b) => (typeof b === "string" ? b.trim() : String(b).trim()))
        .filter((b) => b.length > 0)
      : typeof cleanPayload.bidders === "string" && cleanPayload.bidders.trim()
        ? cleanPayload.bidders.split(",").map((b) => b.trim()).filter(Boolean)
        : [],
    // Extract values from states array if they are objects (react-select)
    states: Array.isArray(cleanPayload.states)
      ? cleanPayload.states
        .filter(s => s !== null && s !== undefined)
        .map(s => {
          if (typeof s === 'string') return s.trim();
          if (s && typeof s === 'object') return (s.value || s.label || String(s)).trim();
          return String(s).trim();
        })
        .filter(s => s && s.length > 0)
      : [],
    // Extract values from cities array if they are objects (react-select)
    cities: Array.isArray(cleanPayload.cities)
      ? cleanPayload.cities
        .filter(c => c !== null && c !== undefined)
        .map(c => {
          if (typeof c === 'string') return c.trim();
          if (c && typeof c === 'object') return (c.value || c.label || String(c)).trim();
          return String(c).trim();
        })
        .filter(c => c && c.length > 0)
      : [],
    // Extract value from statezones if it's an object (react-select)
    // Capitalize zone names (east -> East, west -> West, north -> North, south -> South)
    statezones: (() => {
      const zoneValue = cleanPayload.statezones || cleanPayload.zone;
      if (!zoneValue) return undefined;

      // Helper function to capitalize zone names
      const capitalizeZone = (zone) => {
        if (!zone) return undefined;
        const zoneStr = typeof zone === 'string' ? zone : (zone.value || zone.label || String(zone));
        if (!zoneStr) return undefined;
        const zoneLower = zoneStr.toLowerCase().trim();
        const zoneMap = {
          'east': 'East',
          'west': 'West',
          'north': 'North',
          'south': 'South'
        };
        return zoneMap[zoneLower] || zoneStr.charAt(0).toUpperCase() + zoneStr.slice(1).toLowerCase();
      };

      if (typeof zoneValue === 'string') {
        return capitalizeZone(zoneValue);
      }
      if (zoneValue && typeof zoneValue === 'object') {
        const zoneStr = zoneValue.value || zoneValue.label;
        return zoneStr ? capitalizeZone(zoneStr) : undefined;
      }
      return capitalizeZone(String(zoneValue));
    })(),
    // Optional GEM flag; only include when explicitly provided
    isGem: typeof cleanPayload.isGem === "boolean" ? cleanPayload.isGem : undefined,
    // tender_value range only when meaningful; never send negatives.
    ...(normalizedNonNegativeTenderValuePair(cleanPayload) ?? {}),
    publication_start:
      cleanPayload.publication_start ||
      cleanPayload.publication_date_start ||
      cleanPayload.publication_date_from ||
      cleanPayload.publicationDateFrom,
    publication_end:
      cleanPayload.publication_end ||
      cleanPayload.publication_date_end ||
      cleanPayload.publication_date_to ||
      cleanPayload.publicationDateTo,
    submission_start:
      cleanPayload.submission_start ||
      cleanPayload.submission_date_start ||
      cleanPayload.submission_date_from ||
      cleanPayload.submissionDateFrom,
    submission_end:
      cleanPayload.submission_end ||
      cleanPayload.submission_date_end ||
      cleanPayload.submission_date_to ||
      cleanPayload.submissionDateTo,
    closing_start: cleanPayload.closing_start || cleanPayload.closing_date_start || cleanPayload.closing_date_from || cleanPayload.closingDateFrom || cleanPayload.start_date,
    closing_end: cleanPayload.closing_end || cleanPayload.closing_date_end || cleanPayload.closing_date_to || cleanPayload.closingDateTo || cleanPayload.end_date,
    quantity_min: normalizeOptionalNonNegativeNumber(
      cleanPayload.quantity_min ?? cleanPayload.qtyMin,
    ),
    quantity_max: normalizeOptionalNonNegativeNumber(
      cleanPayload.quantity_max ?? cleanPayload.qtyMax,
    ),
    gem_type: payload.gem_type || payload.gemType,
    tender_value_unit: payload.tender_value_unit || payload.tenderValueUnit,
    // Exemption flags
    msme_exemption: typeof cleanPayload.msme_exemption === "boolean" ? cleanPayload.msme_exemption : undefined,
    startup_exemption: typeof cleanPayload.startup_exemption === "boolean" ? cleanPayload.startup_exemption : undefined,
    tender_documents_available: typeof cleanPayload.tender_documents_available === "boolean" ? cleanPayload.tender_documents_available : undefined,
    // Status filter: numeric value (2 for default) - always include
    status_filter:
      typeof cleanPayload.status_filter === "number"
        ? cleanPayload.status_filter
        : undefined,
    // Pagination and sorting
    sort_order: cleanPayload.sort_order || cleanPayload.sortOrder || undefined,
    page: typeof cleanPayload.page === "number" ? cleanPayload.page : 1,
    limit: typeof cleanPayload.limit === "number" ? cleanPayload.limit : 50,
  };

  // Debug: Log ownership and search fields in body immediately after creation
  console.log("ðŸ”µ advancedFilterTenders - Body created:", {
    ownership: body.ownership,
    global_search: body.global_search,
    keyword: body.keyword,
    search_within: body.search_within,
    apexid: body.apexid,
    keyword_search_type: body.keyword_search_type,
    wordsearch_type: body.wordsearchtype,
    allKeys: Object.keys(body)
  });

  // Remove null/undefined/empty string values to keep payload clean
  // Preserve page/limit as required fields; keep status_filter only when explicitly set
  Object.keys(body).forEach(key => {
    // Always keep pagination fields
    if (key === "page" || key === "limit") {
      if (key === "page" && typeof body[key] !== "number") {
        body[key] = 1;
      } else if (key === "limit" && typeof body[key] !== "number") {
        body[key] = 50;
      }
    } else if (key === "status_filter") {
      if (typeof body[key] !== "number") {
        delete body[key];
      }
    } else if (key === "search_within") {
      // Preserve search_within if it's a non-empty string
      if (body[key] === null || body[key] === undefined || body[key] === "") {
        delete body[key];
      }
    } else if (body[key] === null || body[key] === undefined || body[key] === "" ||
      (Array.isArray(body[key]) && body[key].length === 0)) {
      delete body[key];
    }
  });

  // Ensure pagination fields are always present with defaults
  if (typeof body.page !== "number") {
    body.page = 1;
  }
  if (typeof body.limit !== "number") {
    body.limit = 50;
  }

  // Debug: Log final payload before API call
  console.log("ðŸ”µ advancedFilterTenders - Final payload before API call:", {
    keyword: body.keyword,
    search_within: body.search_within,
    keyword_search_type: body.keyword_search_type,
    wordsearchtype: body.wordsearchtype,
    apexid: body.apexid,
    status_filter: body.status_filter,
    page: body.page,
    limit: body.limit,
    fullPayload: JSON.stringify(body, null, 2)
  });

  // Debug: subcategory form field is mapped onto body.category for this endpoint
  if (body.category) {
    console.log("ðŸ”µ Category filter (from Sub-Category / category payload):", {
      category: body.category,
    });
  }

  // Debug: Log ownership in final body before API call
  console.log("ðŸ”µ Final API body before request:", JSON.stringify(body, null, 2));
  if (body.ownership) {
    console.log("ðŸ”µ Ownership in final API body:", body.ownership);
  } else {
    console.log("ðŸ”µ Ownership NOT in final API body. cleanPayload had:", {
      ownership: cleanPayload.ownership,
      authorityOwnership: cleanPayload.authorityOwnership
    });
  }

  try {
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const response = await http.post("/tenders/advanced-filter", body, { params });
    const rawData = response?.data;

    console.log("ðŸ”µ API Response - Full rawData structure:", {
      type: typeof rawData,
      isArray: Array.isArray(rawData),
      keys: rawData && typeof rawData === 'object' && !Array.isArray(rawData) ? Object.keys(rawData) : [],
      hasStatusFilter: rawData?.status_filter !== undefined,
      statusFilterValue: rawData?.status_filter,
      hasTenders: rawData?.tenders !== undefined,
      hasResults: rawData?.results !== undefined,
      hasData: rawData?.data !== undefined
    });

    // Extract status_filter from response (if present)
    let responseStatusFilter = null;
    if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
      if (rawData.status_filter !== undefined && rawData.status_filter !== null) {
        responseStatusFilter = Number(rawData.status_filter);
        console.log("ðŸ”µ API Response - Extracted status_filter:", responseStatusFilter);
      }
    }

    // Handle new response structure: { status_filter: 0, tenders: [...] }
    if (rawData && typeof rawData === 'object' && !Array.isArray(rawData) && Array.isArray(rawData.tenders)) {
      const statusFilter = responseStatusFilter;

      console.log("ðŸ”µ API Response - New structure detected: { status_filter, tenders }");
      console.log("ðŸ”µ API Response - status_filter from top level:", statusFilter);
      console.log("ðŸ”µ API Response - tenders count:", rawData.tenders.length);

      // Attach status_filter to each tender object
      const tendersWithStatus = rawData.tenders.map(tender => ({
        ...tender,
        status_filter: statusFilter
      }));

      // Log first tender's status_filter for debugging
      if (tendersWithStatus.length > 0) {
        console.log("ðŸ”µ First tender after attaching status_filter:", {
          id: tendersWithStatus[0].id,
          status_filter: tendersWithStatus[0].status_filter,
          tendernumber: tendersWithStatus[0].tendernumber,
          submissiondate: tendersWithStatus[0].submissiondate,
          openingdate: tendersWithStatus[0].openingdate
        });
      }

      // Return object with tenders and status_filter for dropdown update
      return {
        tenders: tendersWithStatus,
        status_filter: statusFilter
      };
    }

    // Handle response structure: { status_filter: 0, results: [...] } or { status_filter: 0, data: [...] }
    let tenderArray = null;
    let statusFilter = responseStatusFilter;

    if (rawData && typeof rawData === 'object' && !Array.isArray(rawData)) {
      // Check for tenders array in various possible locations
      if (Array.isArray(rawData.results)) {
        tenderArray = rawData.results;
        console.log("ðŸ”µ API Response - Found tenders in 'results' array");
      } else if (Array.isArray(rawData.data)) {
        tenderArray = rawData.data;
        console.log("ðŸ”µ API Response - Found tenders in 'data' array");
      } else if (Array.isArray(rawData.tenders)) {
        tenderArray = rawData.tenders;
        console.log("ðŸ”µ API Response - Found tenders in 'tenders' array");
      }

      // If we found a tender array and status_filter, attach status_filter to each tender
      if (tenderArray && statusFilter !== null) {
        console.log("ðŸ”µ API Response - Attaching status_filter:", statusFilter, "to", tenderArray.length, "tenders");
        const tendersWithStatus = tenderArray.map(tender => ({
          ...tender,
          status_filter: statusFilter
        }));
        return {
          tenders: tendersWithStatus,
          status_filter: statusFilter
        };
      }

      // If we found a tender array but no status_filter, return as is
      if (tenderArray) {
        console.log("ðŸ”µ API Response - Returning tenders array without status_filter");
        return {
          tenders: tenderArray,
          status_filter: null
        };
      }
    }

    // Fallback to old response structures for backward compatibility
    if (Array.isArray(rawData)) {
      console.log("ðŸ”µ API Response - Direct array response (no status_filter)");
      return {
        tenders: rawData,
        status_filter: null
      };
    }

    console.log("ðŸ”µ API Response - No valid tender array found, returning empty array");
    return {
      tenders: [],
      status_filter: null
    };
  } catch (error) {
    console.error("Error fetching advanced filtered tenders:", error);
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
    }
    return {
      tenders: [],
      status_filter: null
    };
  }
}

/**
 * Dashboard/filter APIs expect `product` as a string; forms may send string[] from multi-select.
 * @param {*} raw - product or products from payload
 * @returns {string} Comma-separated names or ""
 */
function normalizeAdvancedFilterProductToString(raw) {
  const rawProduct = raw;
  if (rawProduct == null || rawProduct === "") return "";
  let productList = [];
  if (Array.isArray(rawProduct)) {
    productList = rawProduct
      .filter(Boolean)
      .map((p) => (typeof p === "string" ? p.trim() : String(p?.value ?? p?.label ?? p ?? "").trim()))
      .filter(Boolean);
  } else if (typeof rawProduct === "string" && rawProduct.trim()) {
    productList = [rawProduct.trim()];
  } else if (rawProduct && typeof rawProduct === "object" && (rawProduct.value ?? rawProduct.label)) {
    const s = String(rawProduct.value ?? rawProduct.label).trim();
    if (s) productList = [s];
  }
  return productList.length > 0 ? productList.join(", ") : "";
}

/**
 * Statistics dashboard `/statistics/dashboard/` expects `product` as a string array when multiple values.
 * @param {*} raw - product or products from payload
 * @returns {string[]}
 */
function normalizeAdvancedFilterProductToArray(raw) {
  const rawProduct = raw;
  if (rawProduct == null || rawProduct === "") return [];
  if (Array.isArray(rawProduct)) {
    return rawProduct
      .filter(Boolean)
      .map((p) => (typeof p === "string" ? p.trim() : String(p?.value ?? p?.label ?? p ?? "").trim()))
      .filter(Boolean);
  }
  if (typeof rawProduct === "string" && rawProduct.trim()) {
    return [rawProduct.trim()];
  }
  if (rawProduct && typeof rawProduct === "object" && (rawProduct.value ?? rawProduct.label)) {
    const s = String(rawProduct.value ?? rawProduct.label).trim();
    return s ? [s] : [];
  }
  return [];
}

/** Keywords for statistics dashboard body: always string[]. */
function normalizeStatisticsDashboardKeywordList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((k) => String(k ?? "").trim()).filter(Boolean);
  }
  const s = String(raw).trim();
  return s ? [s] : [];
}

/**
 * Normalize list responses from advanced-filter style endpoints (array, results, data, or first array value).
 * @param {*} rawData
 * @returns {Array}
 */
function normalizeAdvancedFilterDashboardList(rawData) {
  if (Array.isArray(rawData)) return rawData;
  if (rawData && Array.isArray(rawData.results)) return rawData.results;
  if (rawData && Array.isArray(rawData.data)) return rawData.data;
  if (rawData && typeof rawData === "object") {
    for (const key of Object.keys(rawData)) {
      if (Array.isArray(rawData[key])) return rawData[key];
    }
  }
  return [];
}

/**
 * User-scoped tender dashboard list: POST /tenders/advanced-filter-dashboard?userid=
 * Production API expects a JSON body (e.g. page, limit, and any active filters), not GET.
 * @param {string|number} userId
 * @param {number} [page=1]
 * @param {number} [limit=50]
 * @param {Object} [mergePayload] - Extra filter fields (category, states, status_filter, etc.)
 * @returns {Promise<Array>}
 */
export async function fetchTendersAdvancedFilterDashboardGet(userId, page = 1, limit = 50, mergePayload = {}) {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId) : "";
  if (!uid) return [];
  return fetchTendersAdvancedFilterDashboard({
    userid: uid,
    page,
    limit,
    ...mergePayload,
  });
}

/**
 * Advanced filter for tenders dashboard - uses /tenders/advanced-filter-dashboard endpoint
 * @param {Object} payload - Search parameters matching API payload structure
 * @param {string|number} [payload.userid] - Sent as userid query param when set (with POST body)
 * @param {string|number} [payload.user_id] - Alias for userid query param
 * @returns {Promise<Array>} Array of tender items
 */
export async function fetchTendersAdvancedFilterDashboard(payload = {}) {
  // Remove internal tracking fields
  const { _timestamp, _useDashboardAdvanceSearch, ...cleanPayload } = payload;

  // Build payload matching the API contract
  const apexidValue = cleanPayload.apexid || cleanPayload.apex_id || cleanPayload.apexId || "";
  const rawKeywordValue = cleanPayload.keyword ?? cleanPayload.search_term ?? "";
  const keywordValues = Array.isArray(rawKeywordValue)
    ? rawKeywordValue
        .map((k) => String(k ?? "").trim())
        .filter(Boolean)
    : (String(rawKeywordValue).trim() ? [String(rawKeywordValue).trim()] : []);

  // Handle states and cities as arrays
  let statesArray = [];
  if (cleanPayload.states) {
    if (Array.isArray(cleanPayload.states)) {
      statesArray = cleanPayload.states
        .filter(Boolean)
        .map((s) => (typeof s === "string" ? s : s?.value ?? s?.label ?? String(s)).trim())
        .filter((s) => s !== "");
    } else if (typeof cleanPayload.states === "string" && cleanPayload.states.trim()) {
      statesArray = [cleanPayload.states.trim()];
    }
  }

  let citiesArray = [];
  if (cleanPayload.cities) {
    if (Array.isArray(cleanPayload.cities)) {
      citiesArray = cleanPayload.cities
        .filter(Boolean)
        .map((c) => (typeof c === "string" ? c : c?.value ?? c?.label ?? String(c)).trim())
        .filter((c) => c !== "");
    } else if (typeof cleanPayload.cities === "string" && cleanPayload.cities.trim()) {
      citiesArray = [cleanPayload.cities.trim()];
    }
  }

  // API expects statezones as a list, not a single string (e.g. "Central" -> ["Central"]).
  let statezonesArray = [];
  const rawStatezones =
    cleanPayload.statezones ??
    cleanPayload.state_zones ??
    cleanPayload.zone ??
    cleanPayload.statezone;
  if (Array.isArray(rawStatezones)) {
    statezonesArray = rawStatezones
      .filter(Boolean)
      .map((z) => (typeof z === "string" ? z : z?.value ?? z?.label ?? String(z)).trim())
      .filter((z) => z !== "");
  } else if (typeof rawStatezones === "string" && rawStatezones.trim()) {
    statezonesArray = [rawStatezones.trim()];
  } else if (rawStatezones && typeof rawStatezones === "object") {
    const z = rawStatezones.value ?? rawStatezones.label;
    if (z != null && String(z).trim() !== "") {
      statezonesArray = [String(z).trim()];
    }
  }

  // Handle departments as array
  let departmentsArray = [];
  if (cleanPayload.departments) {
    if (Array.isArray(cleanPayload.departments)) {
      departmentsArray = cleanPayload.departments
        .filter(Boolean)
        .map((d) => String(d).trim())
        .filter((d) => d !== "");
    } else if (typeof cleanPayload.departments === "string" && cleanPayload.departments.trim()) {
      departmentsArray = [cleanPayload.departments.trim()];
    }
  }

  // Handle ownership as array
  let ownershipArray = [];
  if (cleanPayload.ownership || cleanPayload.authorityOwnership) {
    const ownershipValue = cleanPayload.ownership || cleanPayload.authorityOwnership;
    if (Array.isArray(ownershipValue)) {
      ownershipArray = ownershipValue
        .filter(Boolean)
        .map((o) => (typeof o === "string" ? o : o?.value ?? o?.label ?? String(o)).trim())
        .filter((o) => o !== "");
    } else if (typeof ownershipValue === "string" && ownershipValue.trim()) {
      ownershipArray = [ownershipValue.trim()];
    } else if (ownershipValue && typeof ownershipValue === "object") {
      const value = ownershipValue.value || ownershipValue.label;
      if (value && String(value).trim()) {
        ownershipArray = [String(value).trim()];
      }
    }
  }

  // Format dates to YYYY-MM-DD
  const formatDate = (dateStr) => {
    if (!dateStr) return undefined;
    if (typeof dateStr === "string" && dateStr.includes("-")) {
      // Already in YYYY-MM-DD format or similar
      return dateStr.split("T")[0];
    }
    return dateStr;
  };

  const rawCategory = cleanPayload.category ?? cleanPayload.searchCategory;
  let categoryList = [];
  if (Array.isArray(rawCategory)) {
    categoryList = rawCategory
      .filter(Boolean)
      .map((c) => (typeof c === "string" ? c.trim() : String(c?.value ?? c?.label ?? c ?? "").trim()))
      .filter(Boolean);
  } else if (typeof rawCategory === "string" && rawCategory.trim()) {
    categoryList = [rawCategory.trim()];
  } else if (rawCategory && typeof rawCategory === "object" && (rawCategory.value ?? rawCategory.label)) {
    const s = String(rawCategory.value ?? rawCategory.label).trim();
    if (s) categoryList = [s];
  }

  const categoryStr = categoryList.length > 0 ? categoryList.join(", ") : "";

  const rawProductValue = cleanPayload.product ?? cleanPayload.products;
  const productValues = Array.isArray(rawProductValue)
    ? rawProductValue
        .map((p) => String(p ?? "").trim())
        .filter(Boolean)
    : (String(rawProductValue ?? "").trim() ? [String(rawProductValue).trim()] : []);

  let briefList = parseBriefInputToList(cleanPayload.brief);
  if (briefList.length === 0) {
    briefList = parseBriefInputToList(
      cleanPayload.newkeywords ?? cleanPayload.newkeyword,
    );
  }
  const rawWordSearchType =
    cleanPayload.wordsearchtype ??
    cleanPayload.wordsearch_type ??
    cleanPayload.word_search_type ??
    cleanPayload.wordSearchType ??
    cleanPayload.wordsearchype;
  const wordSearchType =
    rawWordSearchType != null && String(rawWordSearchType).trim() !== ""
      ? normalizeOptionalWordSearchType(rawWordSearchType)
      : undefined;

  const body = {
    tender_type: cleanPayload.tender_type || cleanPayload.type || cleanPayload.select_type || cleanPayload.selectType || "",
    apexid: apexidValue,
    brief: briefList,
    keyword: keywordValues,
    keyword_search_type: cleanPayload.keyword_search_type || cleanPayload.keywordType || cleanPayload.keyword_type || "Relevant Word",
    wordsearchtype: wordSearchType,
    search_within: cleanPayload.search_within || "",
    category: categoryStr,
    subcategory: cleanPayload.subcategory || cleanPayload.sub_category || cleanPayload.subCategory || "",
    product: productValues,
    tendering_authority: cleanPayload.tendering_authority || cleanPayload.authority || cleanPayload.tenderingAuthority || "",
    departments: departmentsArray,
    states: statesArray,
    cities: citiesArray,
    statezones: statezonesArray,
    ownership: ownershipArray,
    isGem: cleanPayload.isGem !== undefined ? cleanPayload.isGem : null,
    tender_value_min: cleanPayload.tender_value_min || cleanPayload.valueMin || cleanPayload.tender_value_min || 0,
    tender_value_max: cleanPayload.tender_value_max || cleanPayload.valueMax || cleanPayload.tender_value_max || 0,
    publication_start: formatDate(cleanPayload.publication_start || cleanPayload.publicationDateFrom) || "",
    publication_end: formatDate(cleanPayload.publication_end || cleanPayload.publicationDateTo) || "",
    submission_start: formatDate(cleanPayload.submission_start || cleanPayload.submissionDateFrom) || "",
    submission_end: formatDate(cleanPayload.submission_end || cleanPayload.submissionDateTo) || "",
    closing_start: formatDate(cleanPayload.closing_start || cleanPayload.closingDateFrom) || "",
    closing_end: formatDate(cleanPayload.closing_end || cleanPayload.closingDateTo) || "",
    quantity_min: cleanPayload.quantity_min || cleanPayload.quantityMin || 0,
    quantity_max: cleanPayload.quantity_max || cleanPayload.quantityMax || 0,
    msme_exemption: cleanPayload.msme_exemption || cleanPayload.msmeExemption || false,
    startup_exemption: cleanPayload.startup_exemption || cleanPayload.startupExemption || false,
    tender_documents_available: cleanPayload.tender_documents_available || cleanPayload.tenderDocumentAvailable || false,
    status_filter: cleanPayload.status_filter || 0,
    sort_order: cleanPayload.sort_order || "",
    page: cleanPayload.page || 1,
    limit: cleanPayload.limit || 50,
  };

  // Remove empty/default values to keep payload clean
  // Only send fields that have meaningful values (not defaults)
  const cleanedBody = {};
  Object.keys(body).forEach((key) => {
    const value = body[key];

    // Skip if:
    // - Empty string
    // - Empty array
    // - null
    // - Zero (for numeric fields that shouldn't be zero)
    // - false (for boolean fields that shouldn't be false)

    // Always keep these fields
    if (key === 'page' || key === 'limit') {
      cleanedBody[key] = value;
      return;
    }

    // Skip tender_type - not needed in payload
    if (key === 'tender_type') {
      return;
    }

    // Keep keyword and keyword_search_type if keyword exists
    if (key === "keyword" && Array.isArray(value) && value.length > 0) {
      cleanedBody[key] = value;
      if (body.keyword_search_type) {
        cleanedBody.keyword_search_type = body.keyword_search_type;
      }
      return;
    }

    if (key === "keyword_search_type") {
      if (!Array.isArray(body.keyword) || body.keyword.length === 0) {
        return;
      }
    }

    if (
      key === "wordsearch_type" ||
      key === "wordsearchtype" ||
      key === "wordserchtype"
    ) {
      if (value === undefined) {
        return;
      }
    }

    // Skip empty strings
    if (value === "") {
      return;
    }

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    // Skip null
    if (value === null) {
      return;
    }

    // Skip undefined
    if (value === undefined) {
      return;
    }

    // Skip zero for numeric filter fields (they mean "no filter")
    if (value === 0 && (
      key === 'tender_value_min' ||
      key === 'tender_value_max' ||
      key === 'quantity_min' ||
      key === 'quantity_max' ||
      key === 'status_filter'
    )) {
      return;
    }

    // Skip false for boolean fields (they mean "not filtered")
    if (value === false && (
      key === 'msme_exemption' ||
      key === 'startup_exemption' ||
      key === 'tender_documents_available'
    )) {
      return;
    }

    // Keep everything else
    cleanedBody[key] = value;
  });

  const userIdForQuery = cleanPayload.userid ?? cleanPayload.user_id;
  const postConfig =
    userIdForQuery != null && String(userIdForQuery).trim() !== ""
      ? { params: { userid: String(userIdForQuery) } }
      : undefined;

  try {
    // Legacy dashboard endpoint kept commented for quick rollback:
    // const endpoint = "/tenders/advanced-filter-dashboard";
    const endpoint = _useDashboardAdvanceSearch
      ? "/dashboard/advance-search/tenders"
      : "/tenders/advanced-filter-dashboard";
    const response = await http.post(endpoint, cleanedBody, postConfig);
    return normalizeAdvancedFilterDashboardList(response?.data);
  } catch (error) {
    console.error("Error fetching advanced filtered tenders for dashboard:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    return [];
  }
}

/**
 * Advanced filter for results dashboard - uses /results/advanced-filter-dashboard endpoint
 * @param {Object} payload - Search parameters matching API payload structure
 * @param {string|number} [payload.userid] - Sent as userid query param when set (with POST body)
 * @param {string|number} [payload.user_id] - Alias for userid query param
 * @returns {Promise<Array>} Array of result items
 */
export async function fetchResultsAdvancedFilterDashboard(payload = {}) {
  // Remove internal tracking fields
  const { _timestamp, _useDashboardAdvanceSearch, _dashboardAdvanceSearchResultTab, ...cleanPayload } = payload;

  // Handle arrays for departments, states, cities, category, l1_bidders, bidders
  let departmentsArray = [];
  if (cleanPayload.departments) {
    if (Array.isArray(cleanPayload.departments)) {
      departmentsArray = cleanPayload.departments
        .filter(Boolean)
        .map((d) => String(d).trim())
        .filter((d) => d !== "");
    } else if (typeof cleanPayload.departments === "string" && cleanPayload.departments.trim()) {
      departmentsArray = [cleanPayload.departments.trim()];
    }
  }

  let statesArray = [];
  if (cleanPayload.states) {
    if (Array.isArray(cleanPayload.states)) {
      statesArray = cleanPayload.states
        .filter(Boolean)
        .map((s) => (typeof s === "string" ? s : s?.value ?? s?.label ?? String(s)).trim())
        .filter((s) => s !== "");
    } else if (typeof cleanPayload.states === "string" && cleanPayload.states.trim()) {
      statesArray = [cleanPayload.states.trim()];
    }
  }

  let citiesArray = [];
  if (cleanPayload.cities) {
    if (Array.isArray(cleanPayload.cities)) {
      citiesArray = cleanPayload.cities
        .filter(Boolean)
        .map((c) => (typeof c === "string" ? c : c?.value ?? c?.label ?? String(c)).trim())
        .filter((c) => c !== "");
    } else if (typeof cleanPayload.cities === "string" && cleanPayload.cities.trim()) {
      citiesArray = [cleanPayload.cities.trim()];
    }
  }

  let categoryArray = [];
  const rawCategory = cleanPayload.category ?? cleanPayload.searchCategory;
  if (rawCategory) {
    if (Array.isArray(rawCategory)) {
      categoryArray = rawCategory
        .filter(Boolean)
        .map((c) => (typeof c === "string" ? c : (c?.value ?? c?.label ?? String(c))).trim())
        .filter((c) => c !== "");
    } else if (typeof rawCategory === "string" && rawCategory.trim()) {
      categoryArray = [rawCategory.trim()];
    } else if (rawCategory && typeof rawCategory === "object" && (rawCategory.value ?? rawCategory.label)) {
      categoryArray = [String(rawCategory.value ?? rawCategory.label).trim()];
    }
  }

  let l1BiddersArray = [];
  if (cleanPayload.l1_bidders) {
    if (Array.isArray(cleanPayload.l1_bidders)) {
      l1BiddersArray = cleanPayload.l1_bidders
        .filter(Boolean)
        .map((b) => String(b).trim())
        .filter((b) => b !== "");
    } else if (typeof cleanPayload.l1_bidders === "string" && cleanPayload.l1_bidders.trim()) {
      l1BiddersArray = [cleanPayload.l1_bidders.trim()];
    }
  }

  let biddersArray = [];
  if (cleanPayload.bidders) {
    if (Array.isArray(cleanPayload.bidders)) {
      biddersArray = cleanPayload.bidders
        .filter(Boolean)
        .map((b) => String(b).trim())
        .filter((b) => b !== "");
    } else if (typeof cleanPayload.bidders === "string" && cleanPayload.bidders.trim()) {
      biddersArray = [cleanPayload.bidders.trim()];
    }
  }

  // Format dates to YYYY-MM-DD
  const formatDate = (dateStr) => {
    if (!dateStr) return undefined;
    if (typeof dateStr === "string" && dateStr.includes("-")) {
      return dateStr.split("T")[0];
    }
    return dateStr;
  };

  const subcategoryStr =
    cleanPayload.subcategory && String(cleanPayload.subcategory).trim()
      ? String(cleanPayload.subcategory).trim()
      : "";
  const rawProduct = cleanPayload.product ?? cleanPayload.products;
  const productList = Array.isArray(rawProduct)
    ? rawProduct
        .map((p) => (typeof p === "string" ? p : p?.value ?? p?.label ?? String(p)).trim())
        .filter(Boolean)
    : typeof rawProduct === "string" && rawProduct.trim()
      ? [rawProduct.trim()]
      : rawProduct && typeof rawProduct === "object" && (rawProduct.value ?? rawProduct.label)
        ? [String(rawProduct.value ?? rawProduct.label).trim()]
        : [];
  const rawKeyword = cleanPayload.keyword ?? cleanPayload.search_term;
  const keywordList = Array.isArray(rawKeyword)
    ? rawKeyword
        .map((k) => (typeof k === "string" ? k : k?.value ?? k?.label ?? String(k)).trim())
        .filter(Boolean)
    : typeof rawKeyword === "string" && rawKeyword.trim()
      ? [rawKeyword.trim()]
      : rawKeyword && typeof rawKeyword === "object" && (rawKeyword.value ?? rawKeyword.label)
        ? [String(rawKeyword.value ?? rawKeyword.label).trim()]
        : [];

  const briefResultsList = parseBriefInputToList(cleanPayload.brief);
  const rawWordSearchType =
    cleanPayload.wordsearchtype ??
    cleanPayload.wordsearch_type ??
    cleanPayload.word_search_type ??
    cleanPayload.wordSearchType;
  const wordSearchType =
    rawWordSearchType != null && String(rawWordSearchType).trim() !== ""
      ? normalizeOptionalWordSearchType(rawWordSearchType)
      : undefined;

  const body = {
    keyword: keywordList,
    brief: briefResultsList,
    wordsearchtype: wordSearchType,
    search_within: cleanPayload.search_within || "",
    departments: departmentsArray,
    states: statesArray,
    cities: citiesArray,
    category: categoryArray,
    subcategory: subcategoryStr,
    product: productList,
    tendersource: cleanPayload.tendersource || cleanPayload.tender_source || cleanPayload.tenderSource || "",
    l1_bidders: l1BiddersArray,
    bidders: biddersArray,
    contract_value_min: cleanPayload.contract_value_min || cleanPayload.contractValueMin || 0,
    contract_value_max: cleanPayload.contract_value_max || cleanPayload.contractValueMax || 0,
    publication_date_start: formatDate(cleanPayload.publication_date_start || cleanPayload.publicationDateStart) || "",
    publication_date_end: formatDate(cleanPayload.publication_date_end || cleanPayload.publicationDateEnd) || "",
    contract_date_start: formatDate(cleanPayload.contract_date_start || cleanPayload.contractDateStart) || "",
    contract_date_end: formatDate(cleanPayload.contract_date_end || cleanPayload.contractDateEnd) || "",
    year: cleanPayload.year || 0,
    aocstatus: cleanPayload.aocstatus !== undefined ? cleanPayload.aocstatus : undefined,
    sort_by: cleanPayload.sort_by || cleanPayload.sortBy || "",
    apexrid: cleanPayload.apexrid || cleanPayload.apex_rid || cleanPayload.apexRid || "",
    page: cleanPayload.page || 1,
    limit: cleanPayload.limit || 50,
  };

  // Remove empty/default values to keep payload clean
  const cleanedBody = {};
  Object.keys(body).forEach((key) => {
    const value = body[key];

    // Always keep page and limit
    if (key === 'page' || key === 'limit') {
      cleanedBody[key] = value;
      return;
    }

    // Skip empty strings
    if (value === "") {
      return;
    }

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    // Skip zero for numeric filter fields (they mean "no filter")
    if (value === 0 && (
      key === 'contract_value_min' ||
      key === 'contract_value_max' ||
      key === 'year'
    )) {
      return;
    }

    // Keep aocstatus even if 0 (it's a valid filter value)
    if (key === 'aocstatus' && value !== undefined) {
      cleanedBody[key] = value;
      return;
    }

    // Keep everything else
    cleanedBody[key] = value;
  });

  const resultsUserIdForQuery = cleanPayload.userid ?? cleanPayload.user_id;
  const resultsParams = {};
  if (resultsUserIdForQuery != null && String(resultsUserIdForQuery).trim() !== "") {
    resultsParams.userid = String(resultsUserIdForQuery);
  }
  if (_useDashboardAdvanceSearch) {
    resultsParams.tab = String(_dashboardAdvanceSearchResultTab || "today");
  }
  const resultsPostConfig = Object.keys(resultsParams).length > 0 ? { params: resultsParams } : undefined;

  try {
    // Legacy dashboard endpoint kept commented for quick rollback:
    // const endpoint = "/results/advanced-filter-dashboard";
    const endpoint = _useDashboardAdvanceSearch
      ? "/dashboard/advance-search/results"
      : "/results/advanced-filter-dashboard";
    const response = await http.post(endpoint, cleanedBody, resultsPostConfig);
    return normalizeAdvancedFilterDashboardList(response?.data);
  } catch (error) {
    console.error("Error fetching advanced filtered results for dashboard:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    return [];
  }
}

/**
 * Result TBA dashboard list - uses /results/result-tba-dashboard endpoint
 * @param {Object} payload - Search parameters for TBA dashboard
 * @param {string|number} [payload.userid] - Sent as userid query param when set
 * @param {string|number} [payload.user_id] - Alias for userid query param
 * @returns {Promise<Array>} Array of result items
 */
export async function fetchResultTbaDashboard(payload = {}) {
  const { _timestamp, ...cleanPayload } = payload;

  const toStringArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value
        .filter((v) => v != null && String(v).trim() !== "")
        .map((v) => (typeof v === "string" ? v : v?.value ?? v?.label ?? String(v)).trim())
        .filter(Boolean);
    }
    if (typeof value === "string" && value.trim()) return [value.trim()];
    if (typeof value === "object" && (value?.value ?? value?.label)) {
      return [String(value.value ?? value.label).trim()];
    }
    return [];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    if (typeof dateStr === "string" && dateStr.includes("-")) return dateStr.split("T")[0];
    return dateStr;
  };

  const briefTbaList = parseBriefInputToList(cleanPayload.brief);

  const body = {
    criteria: cleanPayload.criteria || "",
    keyword: cleanPayload.keyword || "",
    brief: briefTbaList,
    search_within: cleanPayload.search_within || "",
    departments: toStringArray(cleanPayload.departments),
    states: toStringArray(cleanPayload.states),
    cities: toStringArray(cleanPayload.cities),
    category: toStringArray(cleanPayload.category ?? cleanPayload.searchCategory),
    product: toStringArray(cleanPayload.product ?? cleanPayload.products),
    tendersource:
      cleanPayload.tendersource || cleanPayload.tender_source || cleanPayload.tenderSource || "",
    ownership: toStringArray(cleanPayload.ownership),
    tender_type: cleanPayload.tender_type || cleanPayload.tenderType || "",
    l1_bidders: toStringArray(cleanPayload.l1_bidders),
    bidders: toStringArray(cleanPayload.bidders),
    contract_value_min: cleanPayload.contract_value_min || cleanPayload.contractValueMin || 0,
    contract_value_max: cleanPayload.contract_value_max || cleanPayload.contractValueMax || 0,
    publication_date_start:
      formatDate(cleanPayload.publication_date_start || cleanPayload.publicationDateStart) || "",
    publication_date_end:
      formatDate(cleanPayload.publication_date_end || cleanPayload.publicationDateEnd) || "",
    contract_date_start:
      formatDate(cleanPayload.contract_date_start || cleanPayload.contractDateStart) || "",
    contract_date_end:
      formatDate(cleanPayload.contract_date_end || cleanPayload.contractDateEnd) || "",
    year: cleanPayload.year || 0,
    aocstatus: cleanPayload.aocstatus !== undefined ? cleanPayload.aocstatus : 3,
    sort_by: cleanPayload.sort_by || cleanPayload.sortBy || "",
    apexrid: cleanPayload.apexrid || cleanPayload.apex_rid || cleanPayload.apexRid || "",
    page: cleanPayload.page || 1,
    limit: cleanPayload.limit || 50,
  };

  const cleanedBody = {};
  Object.keys(body).forEach((key) => {
    const value = body[key];

    if (key === "page" || key === "limit" || key === "aocstatus") {
      cleanedBody[key] = value;
      return;
    }

    if (value === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    if (
      value === 0 &&
      (key === "contract_value_min" || key === "contract_value_max" || key === "year")
    ) {
      return;
    }

    cleanedBody[key] = value;
  });

  const userIdForQuery = cleanPayload.userid ?? cleanPayload.user_id;
  const postConfig =
    userIdForQuery != null && String(userIdForQuery).trim() !== ""
      ? { params: { userid: String(userIdForQuery) } }
      : undefined;

  try {
    const response = await http.post("/results/advanced-filter-dashboard", cleanedBody, postConfig);
    return normalizeAdvancedFilterDashboardList(response?.data);
  } catch (error) {
    console.error("Error fetching result TBA dashboard data:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    return [];
  }
}

export async function fetchTenderResults(options = {}) {
  let params = { page: 1, limit: 100 };

  if (options && typeof options === "object") {
    params = {
      ...params,
      ...options,
    };

    // Map keyword to search_term for the new API endpoint
    if (options.keyword && !options.search_term) {
      params.search_term = options.keyword;
      // Remove keyword as it's not used by the new endpoint
      delete params.keyword;
    }
  }

  const response = await http.get("/tender-results/search", {
    params,
  });

  const raw = response?.data;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (Array.isArray(raw?.results)) {
    return raw.results;
  }
  if (Array.isArray(raw?.data)) {
    return raw.data;
  }

  return [];
}

/**
 * Advanced filter for tender results based on multiple criteria
 * @param {Object} payload - Filter payload
 * @param {string|number|null|undefined} userId - Optional; when provided (e.g. after login), sent as user_id query param
 * @returns {Promise<Array>} Array of result items
 */
export async function advancedFilterTenderResults(payload = {}, userId) {
  // Build a clean payload following the new API contract structure
  // Departments should ONLY come from Tendering Authority (NOT from department filters)
  // Handle both string arrays and objects (react-select)
  const departments = Array.isArray(payload.departments)
    ? payload.departments
      .map(dept => {
        // Handle react-select objects
        if (dept && typeof dept === 'object') {
          return dept.value || dept.label || String(dept);
        }
        // Handle strings
        return typeof dept === 'string' ? dept : String(dept);
      })
      .filter(dept => dept && typeof dept === 'string' && dept.trim() !== '')
      .map(dept => dept.trim())
    : [];

  console.log("ðŸ”µ API - Received payload.departments (from Tendering Authority):", payload.departments);
  console.log("ðŸ”µ API - Processed departments:", departments);

  // Handle category - API expects "category": list (array). Never send default "Works".
  const categoryArray = Array.isArray(payload.category)
    ? payload.category
      .map(cat => {
        if (cat && typeof cat === 'object') return cat.value || cat.label || String(cat);
        return typeof cat === 'string' ? cat : String(cat);
      })
      .filter(cat => cat && typeof cat === 'string' && cat.trim() !== '')
      .map(cat => cat.trim())
    : (payload.category && typeof payload.category === 'string' && payload.category.trim() !== ''
      ? [payload.category.trim()]
      : []);
  const categoryFiltered = filterDefaultCategory(categoryArray);

  const globalSearchValue =
    payload.global_search && typeof payload.global_search === "string"
      ? payload.global_search.trim()
      : "";

  const extractStatezonesString = () => {
    const zoneValue = payload.statezones ?? payload.zone;
    if (!zoneValue) return undefined;
    const raw = Array.isArray(zoneValue) ? zoneValue[0] : zoneValue;
    const zoneStr =
      typeof raw === "string" ? raw : (raw?.value ?? raw?.label ?? String(raw ?? ""));
    const normalized = String(zoneStr || "").trim().toLowerCase();
    if (!normalized) return undefined;
    const zoneMap = {
      east: "East",
      west: "West",
      north: "North",
      south: "South",
    };
    return zoneMap[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const briefTenderResultsList = parseBriefInputToList(payload.brief);
  const briefTenderResults =
    briefTenderResultsList.length > 0 ? briefTenderResultsList : undefined;

  const keywordInput = payload.keyword ?? payload.search_term;
  const keywordList = Array.isArray(keywordInput)
    ? keywordInput
        .map((k) => String(k ?? "").trim())
        .filter(Boolean)
    : String(keywordInput ?? "")
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
  const subcategoryInput =
    payload.subcategory ?? payload.subCategory ?? payload.sub_category;
  const subcategoryList = Array.isArray(subcategoryInput)
    ? subcategoryInput
        .map((s) => String(s ?? "").trim())
        .filter(Boolean)
    : String(subcategoryInput ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  const body = {
    // Basic fields
    global_search: globalSearchValue || undefined,
    brief: briefTenderResults,
    keyword:
      !globalSearchValue && keywordList.length > 0
        ? keywordList
        : undefined,
    keyword_search_scope:
      payload.keyword_search_scope && typeof payload.keyword_search_scope === "string"
        ? payload.keyword_search_scope.trim()
        : undefined,
    subcategory: subcategoryList,
    category: categoryFiltered,
    departments: departments,
    states: Array.isArray(payload.states) ? payload.states : [],
    cities: Array.isArray(payload.cities) ? payload.cities : [],
    statezones: extractStatezonesString(),
    tendersource: payload.tendersource || payload.tender_source || payload.tenderSource || undefined,
    l1_bidders: Array.isArray(payload.l1_bidders) ? payload.l1_bidders : [],
    bidders: Array.isArray(payload.bidders) ? payload.bidders : [],
    contract_value_min: typeof payload.contract_value_min === "number" ? payload.contract_value_min : undefined,
    contract_value_max: typeof payload.contract_value_max === "number" ? payload.contract_value_max : undefined,
    publication_date_start: payload.publication_date_start || payload.publicationDateStart || undefined,
    publication_date_end: payload.publication_date_end || payload.publicationDateEnd || undefined,
    contract_date_start: payload.contract_date_start || payload.contractDateStart || undefined,
    contract_date_end: payload.contract_date_end || payload.contractDateEnd || undefined,
    year: typeof payload.year === "number" ? payload.year : undefined,
    aocstatus: typeof payload.aocstatus === "number" ? payload.aocstatus : undefined,
    sort_by: payload.sort_by || payload.sortBy || undefined,
    apexrid: payload.apexrid || payload.apex_rid || payload.apexRid || undefined,
    page: typeof payload.page === "number" ? payload.page : 1,
    limit: typeof payload.limit === "number" ? payload.limit : 50,
    search_within: payload.search_within && typeof payload.search_within === "string" && payload.search_within.trim() !== "" ? payload.search_within.trim() : undefined,
  };

  // Remove null/undefined/empty string values to keep payload clean
  Object.keys(body).forEach(key => {
    if (body[key] === null || body[key] === undefined || body[key] === "" ||
      (Array.isArray(body[key]) && body[key].length === 0)) {
      delete body[key];
    }
  });

  // Merge any extra fields from caller (e.g. saved query-module object) onto the request body.
  // Skip only keys that are alternate spellings already folded into `body` above.
  const mergeSkip = new Set([
    "keyword",
    "search_term",
    "subcategory",
    "tender_source",
    "tenderSource",
    "publicationDateStart",
    "publicationDateEnd",
    "contractDateStart",
    "contractDateEnd",
    "sortBy",
    "apex_rid",
    "apexRid",
    "statezones",
    "zone",
    "subCategory",
    "sub_category",
  ]);
  Object.keys(payload || {}).forEach((key) => {
    if (mergeSkip.has(key)) return;
    const value = payload[key];
    if (value === undefined) return;
    body[key] = value;
  });

  Object.keys(body).forEach((key) => {
    if (body[key] === null || body[key] === undefined || body[key] === "" ||
      (Array.isArray(body[key]) && body[key].length === 0)) {
      delete body[key];
    }
  });
  if (body.global_search) {
    delete body.keyword;
    delete body.search_term;
  }

  try {
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    console.log("Calling /tender-results/advanced-filter with payload:", body);
    const response = await http.post("/tender-results/advanced-filter", body, { params });
    const rawData = response?.data;

    if (Array.isArray(rawData)) {
      return rawData;
    }
    if (Array.isArray(rawData?.results)) {
      return rawData.results;
    }
    if (Array.isArray(rawData?.data)) {
      return rawData.data;
    }

    return [];
  } catch (error) {
    console.error("Error fetching advanced filtered tender results:", error);
    if (error.response) {
      console.error("Error response:", error.response.data);
    }
    return [];
  }
}

/**
 * Normalize a single bidder from API (supports new response: result_id, bidder_name, bid_num, bid_type,
 * aoc_rank, financial_rank, bid_amount, rate_difference, technical_bid_status, financial_bid_status,
 * aoc_bid_status, technical_remark, financial_remark, aoc_remark, remarks, created_at, updated_at).
 * Ensures rank is set from financial_rank or aoc_rank when present.
 * @param {Object} b - Raw bidder object
 * @returns {Object} Bidder with rank derived from financial_rank/aoc_rank if needed
 */
function normalizeBidderItem(b) {
  if (!b || typeof b !== "object") return b;
  const rank = b.financial_rank ?? b.aoc_rank ?? b.rank;
  return { ...b, rank };
}

/**
 * Fetch bidders for a tender result (GET /tender-results/bidders/{apexRid}?user_id=...)
 * Response shape: array of { result_id, bidder_name, bid_num, bid_type, aoc_rank, financial_rank,
 * bid_amount, rate_difference, technical_bid_status, financial_bid_status, aoc_bid_status, ... }
 * @param {string} apexRid - APEX RID (result ID)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<Array>} Array of bidder objects (with rank from financial_rank/aoc_rank when present)
 */
export async function fetchTenderBidders(apexRid, userId) {
  if (!apexRid) {
    return [];
  }

  const encodedId = encodeURIComponent(apexRid);
  const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
  const response = await http.get(`/tender-results/bidders/${encodedId}`, { params });
  const raw = response?.data;

  let list = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (Array.isArray(raw?.results)) {
    list = raw.results;
  } else if (Array.isArray(raw?.data)) {
    list = raw.data;
  }

  return list.map(normalizeBidderItem);
}

/**
 * Fetch result details (GET /tender-results/result-details/{apexRid}?user_id=...)
 * @param {string} apexRid - APEX RID (result ID only, e.g. 500000040 or 2026_TCMPF_641531_1 as used by backend)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<object|null>} Result details or null
 */
export async function fetchTenderResultDetails(apexRid, userId) {
  if (!apexRid) {
    return null;
  }

  const encodedId = encodeURIComponent(apexRid);
  const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
  const response = await http.get(`/tender-results/result-details/${encodedId}`, { params });

  if (response?.data) {
    const data = response.data;
    const contentAvailability = data.content_availability ?? data.contentAvailability;
    const resultPayload = data.data ?? data.result ?? data.tender ?? data;
    if (contentAvailability && resultPayload && resultPayload !== data) {
      return { ...resultPayload, content_availability: contentAvailability };
    }
    if (
      contentAvailability &&
      resultPayload &&
      !resultPayload.content_availability &&
      !resultPayload.contentAvailability
    ) {
      return { ...resultPayload, content_availability: contentAvailability };
    }
    return data;
  }

  return null;
}

/**
 * Bidder analysis for a tender/result (GET /statistics/bidder-analysis/{id}?user_id=...)
 * @param {string|number} tenderId - Tender or result id (e.g. organisation id / apex id as used by API)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<Array>}
 */
export async function fetchBidderAnalysis(tenderId, userId) {
  if (!tenderId) {
    return [];
  }

  const encodedId = encodeURIComponent(tenderId);
  const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
  const response = await http.get(`/statistics/bidder-analysis/${encodedId}`, { params });
  const raw = response?.data;

  if (Array.isArray(raw)) {
    return raw;
  }
  if (Array.isArray(raw?.results)) {
    return raw.results;
  }
  if (Array.isArray(raw?.data)) {
    return raw.data;
  }

  return [];
}

/**
 * Fetch result documents from API (Result Document API)
 * GET /tender-results/result-documents/{apexRid}?user_id=...
 * @param {string} resultId - APEX RID (result ID only, e.g. 500000040)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<{ documents: Array, document_access: object|null }>}
 */
export async function fetchResultDocuments(resultId, userId) {
  const empty = { documents: [], document_access: null };
  if (!resultId) return empty;
  try {
    const encodedResultId = encodeURIComponent(resultId);
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const response = await http.get(`/tender-results/result-documents/${encodedResultId}`, { params });

    let documents = [];
    let document_access = null;
    const data = response.data;

    if (Array.isArray(data)) {
      documents = data;
    } else if (data && typeof data === "object") {
      document_access = data.document_access ?? data.documentAccess ?? null;
      if (Array.isArray(data.documents)) {
        documents = data.documents;
      } else if (Array.isArray(data.data)) {
        documents = data.data;
      }
    }

    return { documents, document_access };
  } catch (error) {
    console.error("Error fetching result documents:", error);
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
    }
    return empty;
  }
}

/**
 * Fetch a single tender by ID (apexid).
 * @param {string} tenderId - Tender apexid or ID
 * @param {string|number|null|undefined} userId - Optional; when provided (e.g. after login), sent as user_id query param
 * @returns {Promise<object|null>} Tender object or null
 */
export async function fetchTenderById(tenderId, userId) {
  if (!tenderId) return null;

  try {
    // Decode the tenderId/apexid in case it's URL encoded
    const decodedId = decodeURIComponent(String(tenderId));
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;

    // Try to fetch from a specific endpoint first (if API supports it)
    // The endpoint now uses apexid: /tenders/{apexid}
    let found = null;

    try {
      // Fetch by apexid endpoint: /tenders/{apexid}?user_id= when logged in
      const response = await http.get(`/tenders/${encodeURIComponent(decodedId)}`, { params });
      const data = response.data;
      if (data) {
        // Ensure content_availability is on the tender (API may send it at top level or nested)
        const contentAvailability = data.content_availability ?? data.contentAvailability;
        const tenderPayload = data.data ?? data.tender ?? data.result ?? data;
        if (contentAvailability && tenderPayload && tenderPayload !== data) {
          return { ...tenderPayload, content_availability: contentAvailability };
        }
        if (contentAvailability && !tenderPayload.content_availability && !tenderPayload.contentAvailability) {
          return { ...data, content_availability: contentAvailability };
        }
        return data;
      }
    } catch (e) {
      // If specific endpoint doesn't exist, fall back to fetching all
      console.log("Specific tender endpoint not available, fetching all tenders");
    }

    // Fallback: Fetch all tenders and search
    const response = await http.get("/tenders/", { params });
    const data = Array.isArray(response.data) ? response.data : [];

    // Try multiple matching strategies - now also checking apexid
    found = data.find((t) => {
      const tApexId = String(t.apexid || t.apexId || t.apex_id || "");
      const tNumber = String(t.tendernumber || t.tenderid || t.id || "");
      const tId = String(t.tenderid || t.tendernumber || t.id || "");

      // Exact match - prioritize apexid
      if (tApexId === decodedId || tNumber === decodedId || tId === decodedId) {
        return true;
      }

      // Case-insensitive match
      if (tApexId.toLowerCase() === decodedId.toLowerCase() ||
        tNumber.toLowerCase() === decodedId.toLowerCase() ||
        tId.toLowerCase() === decodedId.toLowerCase()) {
        return true;
      }

      // Partial match (in case of formatting differences)
      if (tApexId.includes(decodedId) || decodedId.includes(tApexId) ||
        tNumber.includes(decodedId) || decodedId.includes(tNumber)) {
        return true;
      }

      return false;
    });

    if (!found) {
      console.warn(`Tender not found with ID: ${decodedId}`);
      console.log(`Searched in ${data.length} tenders`);
      // Log first few tender IDs for debugging
      if (data.length > 0) {
        console.log("Sample tender IDs:", data.slice(0, 5).map(t => ({
          apexid: t.apexid,
          tendernumber: t.tendernumber,
          tenderid: t.tenderid,
          id: t.id
        })));
      }
    }

    return found || null;
  } catch (error) {
    console.error("Error fetching tender by ID:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return null;
  }
}

/**
 * Get AI Summary for a tender
 * GET /tenders/ai-summary/{tenderId}?user_id={userId}
 * When summary is not yet generated, API may return { detail: "AI Summary Not Available" }.
 * @param {string} tenderId - Tender apexid or ID
 * @param {string|number|null|undefined} userId - Optional; sent as user_id query param
 * @returns {Promise<string|object|null>} Summary text, or { detail: "AI Summary Not Available" }, or null on error
 */
function getAiSummaryPayloadMessage(payload) {
  if (payload == null) return "";
  if (typeof payload === "string") return payload.trim();
  if (typeof payload !== "object") return "";

  const detailMessage = getAiSummaryPayloadMessage(payload.detail);
  if (detailMessage) return detailMessage;

  const message = payload.message || payload.error || payload.Error;
  return typeof message === "string" ? message.trim() : "";
}

function isAiSummaryDeniedPayload(payload) {
  const message = getAiSummaryPayloadMessage(payload);
  if (!message || message === "AI Summary Not Available") return false;
  const lowerMessage = message.toLowerCase();
  return (
    lowerMessage.includes("access denied") ||
    lowerMessage.includes("subscription plan") ||
    lowerMessage.includes("ai_summary") ||
    lowerMessage.includes("current plan") ||
    lowerMessage.includes("upgrade to enterprise") ||
    lowerMessage.includes("360 insights") ||
    lowerMessage.includes("daily ai summary") ||
    lowerMessage.includes("daily limit") ||
    lowerMessage.includes("ai summary access limit") ||
    (lowerMessage.includes("ai summary") && lowerMessage.includes("only available")) ||
    (lowerMessage.includes("ai summary") && lowerMessage.includes("not available"))
  );
}

function normalizeAiSummaryDeniedPayload(payload) {
  if (!isAiSummaryDeniedPayload(payload)) return null;
  return {
    ...(payload && typeof payload === "object" ? payload : {}),
    detail: getAiSummaryPayloadMessage(payload) || "AI Summary access denied",
  };
}

function parseMaybeJsonPayload(payload) {
  let value = payload;
  for (let i = 0; i < 3; i += 1) {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (!["{", "[", "\""].includes(trimmed[0])) return value;
    try {
      value = JSON.parse(trimmed);
    } catch (_) {
      return value;
    }
  }
  return value;
}

function isStructuredAiSummaryPayload(payload) {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) return false;
  return [
    "tender_meta",
    "scope_of_work",
    "key_dates",
    "eligibility_snapshot",
    "financial_requirements",
    "documents_required",
    "executive_summary",
    "legal_and_risk_clauses",
    "vendor_decision_hint",
    "additional_important_information",
  ].some((key) => Object.prototype.hasOwnProperty.call(payload, key));
}

function normalizeAiSummaryPayload(payload) {
  const parsedPayload = parseMaybeJsonPayload(payload);
  if (parsedPayload == null || parsedPayload === "") return null;

  if (typeof parsedPayload === "object") {
    if (parsedPayload.detail === "AI Summary Not Available") return parsedPayload;

    const deniedPayload = normalizeAiSummaryDeniedPayload(parsedPayload);
    if (deniedPayload) return deniedPayload;

    if (!isStructuredAiSummaryPayload(parsedPayload)) {
      const nestedPayload =
        parsedPayload.summary ?? parsedPayload.data ?? parsedPayload.text;
      if (nestedPayload != null) {
        return normalizeAiSummaryPayload(nestedPayload);
      }
    }
  }

  return parsedPayload;
}

/**
 * Check whether a user can open/generate AI Summary today.
 * GET /tenders/ai-summary/daily-limit?user_id={userId}&apexid={apexId}
 * Backend wraps: SELECT * FROM public.check_bid_eligibility_daily_limit(%s, %s);
 */
export async function fetchAiSummaryDailyLimit(userId, apexId) {
  if (userId == null || apexId == null) {
    return {
      is_allowed: false,
      already_used: false,
      message: "Unable to verify AI Summary daily limit. Please try again.",
    };
  }

  try {
    const response = await http.get("/tenders/ai-summary/daily-limit", {
      params: { user_id: String(userId), apexid: String(apexId) },
    });
    const data = response?.data ?? {};
    const isAllowed = data.is_allowed ?? data.isAllowed ?? data.allowed;
    const alreadyUsed = data.already_used ?? data.alreadyUsed;
    return {
      ...data,
      is_allowed:
        isAllowed === true ||
        isAllowed === 1 ||
        String(isAllowed).toLowerCase() === "true" ||
        String(isAllowed) === "1",
      already_used:
        alreadyUsed === true ||
        alreadyUsed === 1 ||
        String(alreadyUsed).toLowerCase() === "true" ||
        String(alreadyUsed) === "1",
      message: data.message || data.detail || "",
    };
  } catch (error) {
    console.error("Error checking AI summary daily limit:", error);
    const errData = error?.response?.data;
    const message =
      getAiSummaryPayloadMessage(errData) ||
      "Unable to verify AI Summary daily limit. Please try again.";
    return {
      is_allowed: false,
      already_used: false,
      message,
    };
  }
}



/**
 * Load direct-checkout totals for pay-as-you-go AI Summary credits.
 * POST /tenders/ai-summary/pay-as-you-go/order
 */
export async function createAiSummaryPayAsYouGoOrder(userId, apexId, quantity = 1) {
  if (userId == null || apexId == null) {
    throw new Error("Unable to start payment. Please try again.");
  }
  try {
    const response = await http.post("/tenders/ai-summary/pay-as-you-go/order", {
      user_id: Number(userId),
      apexid: String(apexId),
      quantity: Number(quantity),
    });
    return response?.data ?? response;
  } catch (error) {
    const data = error?.response?.data;
    const message =
      getAiSummaryPayloadMessage(data) ||
      data?.detail?.message ||
      data?.message ||
      error?.message ||
      "Unable to create payment order. Please try again.";
    throw new Error(message);
  }
}

/**
 * Confirm Razorpay payment and add persistent purchased AI Summary credits.
 * POST /tenders/ai-summary/pay-as-you-go/confirm
 */
export async function confirmAiSummaryPayAsYouGoPayment(payload) {
  try {
    const response = await http.post("/tenders/ai-summary/pay-as-you-go/confirm", {
      user_id: Number(payload.user_id ?? payload.userId),
      apexid: String(payload.apexid ?? payload.apexId),
      razorpay_order_id: String(payload.razorpay_order_id || ""),
      razorpay_payment_id: String(payload.razorpay_payment_id || ""),
      razorpay_signature: String(payload.razorpay_signature || ""),
      quantity: Number(payload.quantity || 1),
    });
    return response?.data ?? response;
  } catch (error) {
    const data = error?.response?.data;
    const message =
      getAiSummaryPayloadMessage(data) ||
      data?.detail?.message ||
      data?.message ||
      error?.message ||
      "Unable to confirm payment. Please contact support if payment was deducted.";
    throw new Error(message);
  }
}


export async function fetchTenderAiSummary(tenderId, userId) {
  if (!tenderId) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const response = await http.get(`/tenders/ai-summary/${encodedId}`, { params });
    const data = response?.data;
    if (data == null) return null;
    return normalizeAiSummaryPayload(data);
  } catch (error) {
    const status = error?.response?.status;
    const errData = error?.response?.data;
    if (errData && typeof errData === "object") {
      if (errData.detail === "AI Summary Not Available") return errData;
      const deniedPayload = normalizeAiSummaryDeniedPayload(errData);
      if (deniedPayload) return deniedPayload;
    }
    if (status === 404) {
      return { detail: "AI Summary Not Available" };
    }
    if (status === 403 && errData && typeof errData === "object" && errData.detail) {
      return {
        ...errData,
        detail: getAiSummaryPayloadMessage(errData) || "AI Summary access denied",
      };
    }
    if (status === 403) {
      return { detail: "AI Summary access denied" };
    }
    console.error("Error fetching AI summary:", error);
    return null;
  }
}

/**
 * Increment AI summary usage (track that user viewed an AI summary)
 * POST /utils/ai-summary-usage/increment
 * Body: { userid: number, apexid: number }
 * @param {string|number} userId - User ID
 * @param {string|number} apexId - Tender apexid
 * @returns {Promise<object|null>} Response data or null on error
 */
export async function incrementAiSummaryUsage(userId, apexId) {
  if (userId == null || apexId == null) return null;
  try {
    const body = { userid: Number(userId), apexid: Number(apexId) };
    const response = await http.post("/utils/ai-summary-usage/increment", body);
    return response?.data ?? null;
  } catch (error) {
    console.error("Error incrementing AI summary usage:", error);
    return null;
  }
}

/**
 * Track a result detail view (POST /result-views/track).
 * Does not call the API unless userid and apexrid are finite numbers greater than 0.
 * @param {string|number|null|undefined} userId
 * @param {string|number|null|undefined} apexRid
 * @returns {Promise<object|null>}
 */
export async function trackResultView(userId, apexRid) {
  const uid = userId != null && userId !== "" ? Number(userId) : NaN;
  if (!Number.isFinite(uid) || uid <= 0) return null;
  const rid =
    apexRid != null && apexRid !== "" ? Number(String(apexRid).trim()) : NaN;
  if (!Number.isFinite(rid) || rid <= 0) return null;
  try {
    const response = await http.post("/result-views/track", {
      userid: uid,
      apexrid: rid,
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error tracking result view:", error);
    return null;
  }
}

/**
 * Track a tender detail view (POST /tender-views/track).
 * Does not call the API unless userid and apexid are finite numbers greater than 0.
 * @param {string|number|null|undefined} userId
 * @param {string|number|null|undefined} apexId
 * @returns {Promise<object|null>}
 */
export async function trackTenderView(userId, apexId) {
  const uid = userId != null && userId !== "" ? Number(userId) : NaN;
  if (!Number.isFinite(uid) || uid <= 0) return null;
  const aid =
    apexId != null && apexId !== "" ? Number(String(apexId).trim()) : NaN;
  if (!Number.isFinite(aid) || aid <= 0) return null;
  try {
    const response = await http.post("/tender-views/track", {
      userid: uid,
      apexid: aid,
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error tracking tender view:", error);
    return null;
  }
}

/**
 * Total tender detail views for a user.
 * GET /tender-views/total?userid={id}
 * @param {string|number|null|undefined} userId
 * @returns {Promise<{ totalcount: number, userid: number }|null>}
 */
export async function fetchTotalTenderViews(userId) {
  const uid = userId != null && userId !== "" ? Number(userId) : NaN;
  if (!Number.isFinite(uid) || uid <= 0) return null;
  try {
    const response = await http.get("/tender-views/total", {
      params: { userid: uid },
    });
    const data = response?.data;
    if (data == null || typeof data !== "object") return null;
    const raw =
      data.totalcount ??
      data.total_count ??
      data.totalCount ??
      0;
    const totalcount = Number(raw);
    const userid =
      data.userid != null
        ? Number(data.userid)
        : data.user_id != null
          ? Number(data.user_id)
          : uid;
    return {
      totalcount: Number.isFinite(totalcount) ? totalcount : 0,
      userid: Number.isFinite(userid) ? userid : uid,
    };
  } catch (error) {
    console.error("Error fetching total tender views:", error);
    return null;
  }
}

/**
 * Paginated tender view history (detail rows per tender).
 * GET /tender-views/history/details?userid=&page=&limit=
 * @param {Object} payload
 * @param {number|string} payload.userid
 * @param {number} [payload.page=1]
 * @param {number} [payload.limit=50]
 * @returns {Promise<{ data: Array, page: number, limit: number, total: number }>}
 */
export async function fetchTenderViewsHistoryDetails(payload = {}) {
  const userid = payload.userid;
  const page = Number(payload.page) || 1;
  const limit = Number(payload.limit) || 50;

  if (userid == null || userid === "") {
    return { data: [], page, limit, total: 0 };
  }

  try {
    const response = await http.get("/tender-views/history/details", {
      params: { userid: String(userid), page, limit },
    });
    const apiData = response?.data ?? {};
    const rows = Array.isArray(apiData?.data) ? apiData.data : [];
    const totalRaw =
      apiData?.count ??
      apiData?.total ??
      apiData?.total_count ??
      apiData?.totalCount ??
      0;
    const total = Number(totalRaw) || 0;
    return {
      data: rows,
      page: Number(apiData?.page) || page,
      limit: Number(apiData?.limit) || limit,
      total,
    };
  } catch (error) {
    console.error("Error fetching tender views history details:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load tender view history",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Total result detail views for a user.
 * GET /result-views/total?userid={id}
 * @param {string|number|null|undefined} userId
 * @returns {Promise<{ totalcount: number, userid: number }|null>}
 */
export async function fetchTotalResultViews(userId) {
  const uid = userId != null && userId !== "" ? Number(userId) : NaN;
  if (!Number.isFinite(uid) || uid <= 0) return null;
  try {
    const response = await http.get("/result-views/total", {
      params: { userid: uid },
    });
    const data = response?.data;
    if (data == null || typeof data !== "object") return null;
    const raw =
      data.totalcount ??
      data.total_count ??
      data.totalCount ??
      0;
    const totalcount = Number(raw);
    const userid =
      data.userid != null
        ? Number(data.userid)
        : data.user_id != null
          ? Number(data.user_id)
          : uid;
    return {
      totalcount: Number.isFinite(totalcount) ? totalcount : 0,
      userid: Number.isFinite(userid) ? userid : uid,
    };
  } catch (error) {
    console.error("Error fetching total result views:", error);
    return null;
  }
}

/**
 * Paginated result view history (detail rows per result).
 * GET /result-views/history/details?userid=&page=&limit=
 * @param {Object} payload
 * @param {number|string} payload.userid
 * @param {number} [payload.page=1]
 * @param {number} [payload.limit=100]
 * @returns {Promise<{ data: Array, page: number, limit: number, total: number }>}
 */
export async function fetchResultViewsHistoryDetails(payload = {}) {
  const userid = payload.userid;
  const page = Number(payload.page) || 1;
  const limit = Number(payload.limit) || 100;

  if (userid == null || userid === "") {
    return { data: [], page, limit, total: 0 };
  }

  try {
    const response = await http.get("/result-views/history/details", {
      params: { userid: String(userid), page, limit },
    });
    const apiData = response?.data ?? {};
    const rows = Array.isArray(apiData?.data) ? apiData.data : [];
    const totalRaw =
      apiData?.count ??
      apiData?.total ??
      apiData?.total_count ??
      apiData?.totalCount ??
      0;
    const total = Number(totalRaw) || 0;
    return {
      data: rows,
      page: Number(apiData?.page) || page,
      limit: Number(apiData?.limit) || limit,
      total,
    };
  } catch (error) {
    console.error("Error fetching result views history details:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load result view history",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Track a BOQ detail view (POST /boq-views/track).
 * Does not call the API unless userid and boqheadid are finite numbers greater than 0.
 * @param {string|number|null|undefined} userId
 * @param {string|number|null|undefined} boqHeadId
 * @returns {Promise<object|null>}
 */
export async function trackBoqView(userId, boqHeadId) {
  const uid = userId != null && userId !== "" ? Number(userId) : NaN;
  if (!Number.isFinite(uid) || uid <= 0) return null;
  const bid =
    boqHeadId != null && boqHeadId !== ""
      ? Number(String(boqHeadId).trim())
      : NaN;
  if (!Number.isFinite(bid) || bid <= 0) return null;
  try {
    const response = await http.post("/boq-views/track", {
      userid: uid,
      boquniqueid: bid,
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error tracking BOQ view:", error);
    return null;
  }
}

/**
 * Total BOQ detail views for a user.
 * GET /boq-views/total?userid={id}
 * @param {string|number|null|undefined} userId
 * @returns {Promise<{ totalcount: number, userid: number }|null>}
 */
export async function fetchTotalBoqViews(userId) {
  const uid = userId != null && userId !== "" ? Number(userId) : NaN;
  if (!Number.isFinite(uid) || uid <= 0) return null;
  try {
    const response = await http.get("/boq-views/total", {
      params: { userid: uid },
    });
    const data = response?.data;
    if (data == null || typeof data !== "object") return null;
    const raw =
      data.totalcount ??
      data.total_count ??
      data.totalCount ??
      0;
    const totalcount = Number(raw);
    const userid =
      data.userid != null
        ? Number(data.userid)
        : data.user_id != null
          ? Number(data.user_id)
          : uid;
    return {
      totalcount: Number.isFinite(totalcount) ? totalcount : 0,
      userid: Number.isFinite(userid) ? userid : uid,
    };
  } catch (error) {
    console.error("Error fetching total BOQ views:", error);
    return null;
  }
}

/**
 * Paginated BOQ view history (detail rows).
 * GET /boq-views/history/details?userid=&page=&limit=
 * @param {Object} payload
 * @param {number|string} payload.userid
 * @param {number} [payload.page=1]
 * @param {number} [payload.limit=100]
 * @returns {Promise<{ data: Array, page: number, limit: number, total: number }>}
 */
export async function fetchBoqViewsHistoryDetails(payload = {}) {
  const userid = payload.userid;
  const page = Number(payload.page) || 1;
  const limit = Number(payload.limit) || 100;

  if (userid == null || userid === "") {
    return { data: [], page, limit, total: 0 };
  }

  try {
    const response = await http.get("/boq-views/history/details", {
      params: { userid: String(userid), page, limit },
    });
    const apiData = response?.data ?? {};
    const rows = Array.isArray(apiData?.data) ? apiData.data : [];
    const totalRaw =
      apiData?.boq_count ??
      apiData?.count ??
      apiData?.total ??
      apiData?.total_count ??
      apiData?.totalCount ??
      0;
    const total = Number(totalRaw) || 0;
    return {
      data: rows,
      page: Number(apiData?.page) || page,
      limit: Number(apiData?.limit) || limit,
      total,
    };
  } catch (error) {
    console.error("Error fetching BOQ views history details:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load BOQ view history",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Generate AI summary for a tender using a document's S3 URL
 * POST /tenders/generate-ai-summary?user_id={userId}
 * Body: { apexid, s3_url }. Use document with forsummary === 1 from tenders-all-docs.
 * @param {string} tenderId - Tender apexid (e.g. "100127007")
 * @param {string|number|null|undefined} userId - Sent as user_id query param
 * @param {string} s3Url - S3 URL of the document to use for summary (from doc with forsummary === 1)
 * @returns {Promise<object|null>} Response data or null on error
 */
export async function fetchGenerateAiSummary(tenderId, userId, s3Url) {
  if (!tenderId || !s3Url) return null;
  try {
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const body = { apexid: String(tenderId), s3_url: s3Url };
    const response = await http.post("/tenders/generate-ai-summary", body, { params });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error generating AI summary:", error);
    const errData = error?.response?.data;
    const deniedPayload = normalizeAiSummaryDeniedPayload(errData);
    if (deniedPayload) return deniedPayload;
    if (error?.response?.status === 403) {
      return { detail: getAiSummaryPayloadMessage(errData) || "AI Summary access denied" };
    }
    return null;
  }
}

/**
 * Get or auto-generate Bid Eligibility Checker data for a tender.
 * GET /tenders/bid-eligibility/{apexid}?user_id={userId}&auto_generate=false
 */
export async function fetchBidEligibility(tenderId, userId, autoGenerate = false) {
  if (!tenderId || userId == null) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const response = await http.get(`/tenders/bid-eligibility/${encodedId}`, {
      params: {
        user_id: String(userId),
        auto_generate: autoGenerate,
      },
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error fetching bid eligibility:", error);
    const errData = error?.response?.data;
    if (errData) {
      return {
        detail:
          typeof errData === "string"
            ? errData
            : errData.detail || errData.message || "Bid Eligibility Check Not Available",
        status: error?.response?.status,
      };
    }
    return null;
  }
}

/**
 * Lightweight Bid Eligibility status check.
 * GET /tenders/bid-eligibility/status/{apexid}?user_id={userId}
 */
export async function fetchBidEligibilityStatus(tenderId, userId) {
  if (!tenderId || userId == null) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const response = await http.get(`/tenders/bid-eligibility/status/${encodedId}`, {
      params: { user_id: String(userId) },
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error fetching bid eligibility status:", error);
    const errData = error?.response?.data;
    if (errData) {
      return {
        detail:
          typeof errData === "string"
            ? errData
            : errData.detail || errData.message || "Failed to load Bid Eligibility status",
        status: error?.response?.status,
      };
    }
    return null;
  }
}

/**
 * Re-run Bid Eligibility Checker using the already generated AI summary.
 * POST /tenders/bid-eligibility/run?user_id={userId}
 */
export async function runBidEligibility(tenderId, userId, force = true) {
  if (!tenderId || userId == null) return null;
  try {
    const response = await http.post(
      "/tenders/bid-eligibility/run",
      { apexid: String(tenderId), force },
      { params: { user_id: String(userId) } }
    );
    return response?.data ?? null;
  } catch (error) {
    console.error("Error running bid eligibility:", error);
    const errData = error?.response?.data;
    if (errData) {
      return {
        detail:
          typeof errData === "string"
            ? errData
            : errData.detail || errData.message || "Failed to run Bid Eligibility Check",
        status: error?.response?.status,
      };
    }
    return null;
  }
}

/**
 * Fetch backend-built JV Connect modal context.
 * GET /tenders/jv-connect/context/{apexid}?user_id={userId}
 */
export async function fetchJvConnectContext(tenderId, userId) {
  if (!tenderId || userId == null) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const response = await http.get(`/tenders/jv-connect/context/${encodedId}`, {
      params: { user_id: String(userId) },
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error fetching JV Connect context:", error);
    const errData = error?.response?.data;
    throw {
      message:
        typeof errData === "string"
          ? errData
          : errData?.detail || errData?.message || "Failed to load JV Connect information",
      status: error?.response?.status || 0,
      data: errData,
    };
  }
}

/**
 * Fetch backend-built Contact TenderApex modal context.
 * GET /tenders/contact-tenderapex/context/{apexid}?user_id={userId}
 */
export async function fetchContactTenderApexContext(tenderId, userId) {
  if (!tenderId || userId == null) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const response = await http.get(`/tenders/contact-tenderapex/context/${encodedId}`, {
      params: { user_id: String(userId) },
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error fetching Contact TenderApex context:", error);
    const errData = error?.response?.data;
    throw {
      message:
        typeof errData === "string"
          ? errData
          : errData?.detail || errData?.message || "Failed to load Contact TenderApex information",
      status: error?.response?.status || 0,
      data: errData,
    };
  }
}

/**
 * Submit JV Connect request. Frontend sends only message; backend derives all other data.
 * POST /tenders/jv-connect/request/{apexid}?user_id={userId}
 */
export async function submitJvConnectRequest(tenderId, userId, message = "") {
  if (!tenderId || userId == null) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const response = await http.post(
      `/tenders/jv-connect/request/${encodedId}`,
      { message: String(message || "") },
      { params: { user_id: String(userId) } }
    );
    return response?.data ?? null;
  } catch (error) {
    console.error("Error submitting JV Connect request:", error);
    const errData = error?.response?.data;
    throw {
      message:
        typeof errData === "string"
          ? errData
          : errData?.detail || errData?.message || "Failed to submit JV Connect request",
      status: error?.response?.status || 0,
      data: errData,
    };
  }
}

/**
 * Submit Contact TenderApex inquiry.
 * POST /tenders/contact-tenderapex/request/{apexid}?user_id={userId}
 */
export async function submitContactTenderApexRequest(tenderId, userId, formData = {}) {
  if (!tenderId || userId == null) return null;
  try {
    const encodedId = encodeURIComponent(String(tenderId));
    const response = await http.post(
      `/tenders/contact-tenderapex/request/${encodedId}`,
      {
        companyName: String(formData.companyName || ""),
        contactName: String(formData.contactName || ""),
        mobileNumber: String(formData.mobileNumber || ""),
        email: String(formData.email || ""),
        query: String(formData.query || ""),
      },
      { params: { user_id: String(userId) } }
    );
    return response?.data ?? null;
  } catch (error) {
    console.error("Error submitting Contact TenderApex inquiry:", error);
    const errData = error?.response?.data;
    throw {
      message:
        typeof errData === "string"
          ? errData
          : errData?.detail || errData?.message || "Failed to submit Contact TenderApex inquiry",
      status: error?.response?.status || 0,
      data: errData,
    };
  }
}

/**
 * Fetch tender documents from API
 * @param {string} tenderId - The tender ID (will be URL encoded)
 * @returns {Promise<Array>} Array of document objects
 */
export async function fetchTenderDocuments(tenderId) {
  if (!tenderId) return [];
  try {
    // URL encode the tender ID (e.g., "GEM/2025/B/6178092" becomes "GEM%2F2025%2FB%2F6178092")
    const encodedTenderId = encodeURIComponent(tenderId);
    const response = await http.get(`/tender-documents/tender/${encodedTenderId}`);

    // Debug: Log the response to understand the structure
    console.log("Tender Documents API Response:", response.data);

    // Handle different response formats
    let documents = [];
    if (Array.isArray(response.data)) {
      documents = response.data;
    } else if (response.data && Array.isArray(response.data.documents)) {
      documents = response.data.documents;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      documents = response.data.data;
    } else if (response.data && typeof response.data === 'object') {
      // If response.data is an object, try to extract documents from it
      documents = Object.values(response.data).filter(item =>
        item && typeof item === 'object' && (item.s3url || item.url || item.document_url)
      );
    }

    // Debug: Log the extracted documents
    console.log("Extracted Documents:", documents);

    return documents;
  } catch (error) {
    console.error("Error fetching tender documents:", error);
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
    }
    return [];
  }
}

/**
 * Fetch all documents for a tender from the new API endpoint
 * @param {string} tenderId - Tender ID (e.g. Apex TID "100126975" or "2025_MES_739593_2")
 * @param {string|number|null|undefined} userId - Optional; when provided (e.g. after login), sent as user_id query param
 * @returns {Promise<{ documents: Array, document_access: object }>} documents array (includes locked) and document_access (has_access, message, user_type)
 */
export async function fetchTenderAllDocuments(tenderId, userId) {
  const empty = {
    documents: [],
    document_access: { has_access: false, message: "", user_type: "" },
    boq_details: [],
    boq_item_amount_figure: null,
    boq_item_amount_word: "",
  };
  if (!tenderId) return empty;
  try {
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const response = await http.get(`/tenders-all-docs/${tenderId}`, { params });

    console.log("Tender All Documents API Response:", response.data);

    const document_access = response.data?.document_access ?? { has_access: true, message: "", user_type: "" };
    let documents = [];

    if (response.data && response.data.documents) {
      const { main_documents = [], attached_documents = [] } = response.data.documents;
      const allDocs = [...main_documents, ...attached_documents];

      documents = allDocs.map((doc) => {
        const url = doc.s3url || doc.documenturl || doc.url || null;
        return {
          ...doc,
          s3url: url,
          url,
          doctype: doc.doctype || doc.documenttype || "Other",
          docname: doc.docname || doc.tenderfilename || doc.documenttype || "Document",
          document_type: doc.doctype || doc.documenttype || "Other",
          document_name: doc.docname || doc.tenderfilename || doc.documenttype || "Document",
          name: doc.docname || doc.tenderfilename || doc.documenttype || "Document",
          displayName: doc.docname || doc.tenderfilename || doc.documenttype || "Document",
          is_locked: doc.is_locked === true,
          message: doc.message || ""
        };
      });
    }

    const boq_details = Array.isArray(response.data?.boq_details) ? response.data.boq_details : [];
    const boq_item_amount_figure = response.data?.boq_item_amount_figure ?? null;
    const boq_item_amount_word = response.data?.boq_item_amount_word ?? "";

    console.log("Transformed Documents:", documents.length, "document_access:", document_access);
    return { documents, document_access, boq_details, boq_item_amount_figure, boq_item_amount_word };
  } catch (error) {
    console.error("Error fetching tender all documents:", error);
    if (error.response) {
      console.error("Error response data:", error.response.data);
      console.error("Error response status:", error.response.status);
    }
    return empty;
  }
}

/**
 * Search BOQ Leads
 * @param {string} keyword - Search keyword (sent as search_term)
 * @param {object} options - Additional options (page, limit, boqType, userId, filters, etc.)
 * @param {string} options.boqType - Type of BOQ: "tender" or "result"
 * @param {string|number|null|undefined} options.userId - Optional; when provided (e.g. after login), sent as user_id query param
 * @returns {Promise<Array>} Array of BOQ lead objects
 */
export async function searchBoqLeads(keyword, options = {}) {
  let params = {
    page: options.page || 1,
    limit: options.limit || 100,
  };

  // Only add search_term if keyword is provided and not empty
  if (keyword && keyword.trim()) {
    params.search_term = keyword.trim();
  }

  if (typeof options.userId !== "undefined" && options.userId != null) {
    params.user_id = String(options.userId);
  }

  // Add filter parameters if provided
  const filters = options.filters || {};
  if (filters.apexTid) params.apex_tid = filters.apexTid;
  if (filters.apexRid) params.apex_rid = filters.apexRid;
  if (filters.state) params.state = filters.state;
  if (filters.amountMin) params.amount_min = filters.amountMin;
  if (filters.amountMax) params.amount_max = filters.amountMax;
  if (filters.quantityMin) params.quantity_min = filters.quantityMin;
  if (filters.quantityMax) params.quantity_max = filters.quantityMax;
  if (filters.boqId) params.boq_id = filters.boqId;
  if (filters.category) params.category = filters.category;
  if (filters.city) params.city = filters.city;
  const boqType = options.boqType || "tender";
  if (boqType === "result") {
    if (filters.submissionFrom) params.contract_date_start = filters.submissionFrom;
    if (filters.submissionTo) params.contract_date_end = filters.submissionTo;
  } else {
    if (filters.submissionFrom) params.submission_from = filters.submissionFrom;
    if (filters.submissionTo) params.submission_to = filters.submissionTo;
  }
  if (filters.gem) params.gem = filters.gem;

  // Determine API endpoint based on BOQ type
  // Use /boq/list/ for Tender BOQ, /boq/result-list/ for Result BOQ
  const apiEndpoint = boqType === "tender" ? "/boq/list/" : "/boq/result-list/";

  try {
    console.log("Fetching BOQ leads from API:", apiEndpoint, params, "Type:", boqType);
    const response = await http.get(apiEndpoint, { params });
    const rawData = response?.data;

    console.log("BOQ API Response:", rawData);

    let items = [];
    if (Array.isArray(rawData)) {
      items = rawData;
    } else if (Array.isArray(rawData?.results)) {
      items = rawData.results;
    } else if (Array.isArray(rawData?.data)) {
      items = rawData.data;
    }

    console.log("Processed BOQ items:", items.length);

    // Map API response fields to component expected format
    const mappedItems = items.map((item) => ({
      id: item.boq_unique_id,
      boq_id: item.boq_unique_id,
      submission_date: item.submission_date,
      tender_value: item.tender_value,
      apex_id: item.apex_id,
      description: item.item_description,
      item_description: item.item_description,
      quantity: item.quantity,
      boq_quantity: item.boq_quantity,
      boq_units: item.boq_units,
      quantity_unit: item.quantity_unit,
      boq_item_amount: item.boq_item_amount,
      location: item.location,
      boq_document: item.boq_document,
      boq_document_url: item.boq_document_link || (item.boq_document ? `https://docdemoapex.s3.ap-southeast-2.amazonaws.com/${item.boq_document}` : null),
      boq_document_link: item.boq_document_link,
      tender_id: item.tender_id,
      boq_detail_id: item.boq_detail_id,
      // Keep original data for reference
      ...item
    }));

    return mappedItems;
  } catch (error) {
    console.error("Error fetching BOQ leads:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    return [];
  }
}

/**
 * Advanced Search for BOQ Leads
 * @param {Object} searchParams - Search parameters matching API payload structure; may include userId (sent as user_id query param, not in body)
 * @param {string|number|null|undefined} searchParams.userId - Optional; when provided (e.g. after login), sent as user_id query param
 * @returns {Promise<Array>} Array of BOQ lead items
 */
export async function advancedSearchBoqLeads(searchParams) {
  try {
    const { userId, ...restParams } = searchParams || {};
    console.log("Advanced search BOQ leads with params:", searchParams);

    // Build payload with default values (exclude userId - it goes in query string)
    // Handle state and city as arrays
    let stateArray = [];
    if (restParams.state) {
      if (Array.isArray(restParams.state)) {
        stateArray = restParams.state.filter(s => s && String(s).trim());
      } else if (String(restParams.state).trim()) {
        stateArray = [String(restParams.state).trim()];
      }
    }

    let cityArray = [];
    if (restParams.city) {
      if (Array.isArray(restParams.city)) {
        cityArray = restParams.city.filter(c => c && String(c).trim());
      } else if (String(restParams.city).trim()) {
        cityArray = [String(restParams.city).trim()];
      }
    }

    const rawKeywords = Array.isArray(restParams.keywords)
      ? restParams.keywords
      : restParams.keywords != null && String(restParams.keywords).trim()
        ? [String(restParams.keywords).trim()]
        : [];
    const keywords = [...new Set(rawKeywords.map((k) => String(k).trim()).filter(Boolean))];

    const payload = {
      apexid: restParams.apexid || "",
      apex_rid: restParams.apex_rid || "",
      boq_id: restParams.boq_id || "",
      boq_type: restParams.boq_type || restParams.boqType || "",
      keywords,
      isGem: restParams.isGem !== undefined ? restParams.isGem : null,
      state: stateArray,
      city: cityArray,
      statezone: restParams.statezone || "",
      item_amount_min: restParams.item_amount_min || null,
      item_amount_max: restParams.item_amount_max || null,
      quantity_min: restParams.quantity_min || null,
      quantity_max: restParams.quantity_max || null,
      closing_start: restParams.closing_start || "",
      closing_end: restParams.closing_end || "",
      contract_date_start: restParams.contract_date_start || restParams.contractDateStart || "",
      contract_date_end: restParams.contract_date_end || restParams.contractDateEnd || "",
      bidder_name: restParams.bidder_name || "",
      search_term: restParams.search_term || "",
      page: restParams.page || 1,
      limit: restParams.limit || 50,
    };

    // Remove empty string values and convert to null/undefined for optional fields
    Object.keys(payload).forEach(key => {
      if (key === "state" || key === "city" || key === "keywords") {
        // Remove empty arrays for state, city, and keywords
        if (Array.isArray(payload[key]) && payload[key].length === 0) {
          delete payload[key];
        }
      } else if (payload[key] === "" || payload[key] === 0) {
        // Keep numeric 0 for min/max, but remove empty strings for optional fields
        if (key.includes("_min") || key.includes("_max")) {
          // Keep 0 for numeric fields
        } else if (key === "page" || key === "limit") {
          // Keep page and limit
        } else if (key === "bidder_name" || key === "search_term") {
          // Remove empty bidder_name and search_term
          delete payload[key];
        } else {
          // Remove empty strings for optional fields
          delete payload[key];
        }
      }
    });

    // Convert isGem boolean to proper format
    if (payload.isGem === null || payload.isGem === undefined) {
      delete payload.isGem;
    }

    console.log("Advanced search payload:", payload);
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const response = await http.post("/boq/advanced-search", payload, { params });
    const rawData = response?.data;

    console.log("Advanced search API Response:", rawData);

    let items = [];
    if (Array.isArray(rawData)) {
      items = rawData;
    } else if (Array.isArray(rawData?.results)) {
      items = rawData.results;
    } else if (Array.isArray(rawData?.data)) {
      items = rawData.data;
    } else if (rawData?.data && Array.isArray(rawData.data)) {
      items = rawData.data;
    }

    console.log("Processed advanced search items:", items.length);

    // Map API response fields to component expected format
    const mappedItems = items.map((item) => ({
      id: item.boq_unique_id || item.id,
      boq_id: item.boq_unique_id || item.boq_id,
      submission_date: item.submission_date,
      tender_value: item.tender_value,
      apex_id: item.apex_id,
      description: item.item_description || item.description,
      item_description: item.item_description || item.description,
      quantity: item.quantity,
      boq_quantity: item.boq_quantity,
      boq_units: item.boq_units,
      quantity_unit: item.quantity_unit,
      boq_item_amount: item.boq_item_amount,
      location: item.location,
      boq_document: item.boq_document,
      boq_document_url: item.boq_document_link || (item.boq_document ? `https://docdemoapex.s3.ap-southeast-2.amazonaws.com/${item.boq_document}` : null),
      boq_document_link: item.boq_document_link,
      tender_id: item.tender_id,
      // Keep original data for reference
      ...item
    }));

    return mappedItems;
  } catch (error) {
    console.error("Error in advanced search BOQ leads:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    return [];
  }
}

/**
 * Fetch BOQ Lead Details (BOQ Detail Page API)
 * @param {string|number} boqDetailId - The BOQ detail ID from list API (e.g. 1431553 from /boq/list/)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<Object|null>} BOQ lead details object
 */
export async function fetchBoqLeadDetails(boqDetailId, userId) {
  if (boqDetailId == null || boqDetailId === "") return null;
  try {
    const id = String(boqDetailId);
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    console.log("Fetching BOQ details for boq_detail_id:", id);
    const response = await http.get(`/boq/detail-page/${id}`, { params });

    console.log("BOQ Details API Response:", response.data);

    if (response?.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching BOQ lead details:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return null;
  }
}

/**
 * Search BOQ Leads from Dashboard (Dashboard BOQ Lead API)
 * @param {Object} payload - Search criteria payload
 * @param {string} payload.criteria - "tenderboq" or "resultboq"
 * @param {string} payload.apexid - Apex TID
 * @param {string} payload.apex_rid - Apex RID
 * @param {string|string[]} [payload.brief] - Brief terms (comma-separated string or list)
 * @param {string} payload.boq_id - BOQ ID
 * @param {string[]} payload.keywords - Keyword strings (list)
 * @param {string|string[]} payload.tender_subcategory - Tender sub-category filter
 * @param {string} payload.product - Product (classification) filter
 * @param {boolean} payload.isGem - GEM filter
 * @param {Array<string>} payload.state - State array
 * @param {Array<string>} payload.city - City array
 * @param {string} payload.statezone - State zone
 * @param {number} payload.item_amount_min - Minimum item amount
 * @param {number} payload.item_amount_max - Maximum item amount
 * @param {number} payload.quantity_min - Minimum quantity
 * @param {number} payload.quantity_max - Maximum quantity
 * @param {string} payload.closing_start - Closing start date (YYYY-MM-DD)
 * @param {string} payload.closing_end - Closing end date (YYYY-MM-DD)
 * @param {number} payload.page - Page number
 * @param {number} payload.limit - Results per page
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<Array>} Array of BOQ detail objects
 */
export async function searchDashboardBoqLeads(payload, userId) {
  try {
    const { userId: payloadUserId, _useDashboardAdvanceSearch, ...restPayload } = payload || {};
    const finalUserId = userId || payloadUserId;

    console.log("Dashboard BOQ Lead search with payload:", restPayload);

    // Build clean payload
    const rawKeywords = Array.isArray(restPayload.keywords)
      ? restPayload.keywords
      : restPayload.keywords != null && String(restPayload.keywords).trim()
        ? [String(restPayload.keywords).trim()]
        : [];
    const keywords = [...new Set(rawKeywords.map((k) => String(k).trim()).filter(Boolean))];

    const rawTenderSub = Array.isArray(restPayload.tender_subcategory)
      ? restPayload.tender_subcategory
      : restPayload.tender_subcategory != null &&
          String(restPayload.tender_subcategory).trim()
        ? [String(restPayload.tender_subcategory).trim()]
        : [];
    const tender_subcategory = [
      ...new Set(rawTenderSub.map((k) => String(k).trim()).filter(Boolean)),
    ];

    const categoryValues = Array.isArray(restPayload.category)
      ? restPayload.category
          .map((c) => String(c ?? "").trim())
          .filter(Boolean)
      : (restPayload.category != null && String(restPayload.category).trim()
          ? [String(restPayload.category).trim()]
          : []);
    const statezoneValues = Array.isArray(restPayload.statezone)
      ? restPayload.statezone
          .map((z) => String(z ?? "").trim())
          .filter(Boolean)
      : (restPayload.statezone != null && String(restPayload.statezone).trim()
          ? [String(restPayload.statezone).trim()]
          : []);
    const tenderTypeValues = Array.isArray(restPayload.tender_type)
      ? restPayload.tender_type
          .map((t) => String(t ?? "").trim())
          .filter(Boolean)
      : (restPayload.tender_type != null && String(restPayload.tender_type).trim()
          ? [String(restPayload.tender_type).trim()]
          : []);

    const cleanPayload = {
      criteria: restPayload.criteria || "resultboq",
      apexid: restPayload.apexid || "",
      apex_rid: restPayload.apex_rid || "",
      brief: parseBriefInputToList(restPayload.brief),
      boq_id: restPayload.boq_id || "",
      category: categoryValues,
      tender_subcategory,
      product: Array.isArray(restPayload.product)
        ? restPayload.product
            .map((p) => String(p ?? "").trim())
            .filter(Boolean)
        : restPayload.product != null && String(restPayload.product).trim()
          ? [String(restPayload.product).trim()]
          : [],
      keywords,
      tender_type: tenderTypeValues,
      isGem: restPayload.isGem !== undefined ? restPayload.isGem : undefined,
      state: Array.isArray(restPayload.state) ? restPayload.state : (restPayload.state ? [restPayload.state] : []),
      city: Array.isArray(restPayload.city) ? restPayload.city : (restPayload.city ? [restPayload.city] : []),
      statezone: statezoneValues,
      item_amount_min:
        typeof restPayload.item_amount_min === "number" &&
        Number.isFinite(restPayload.item_amount_min) &&
        restPayload.item_amount_min > 0
          ? restPayload.item_amount_min
          : undefined,
      item_amount_max:
        typeof restPayload.item_amount_max === "number" &&
        Number.isFinite(restPayload.item_amount_max) &&
        restPayload.item_amount_max > 0
          ? restPayload.item_amount_max
          : undefined,
      quantity_min:
        typeof restPayload.quantity_min === "number" &&
        Number.isFinite(restPayload.quantity_min) &&
        restPayload.quantity_min > 0
          ? restPayload.quantity_min
          : undefined,
      quantity_max:
        typeof restPayload.quantity_max === "number" &&
        Number.isFinite(restPayload.quantity_max) &&
        restPayload.quantity_max > 0
          ? restPayload.quantity_max
          : undefined,
      closing_start: restPayload.closing_start || "",
      closing_end: restPayload.closing_end || "",
      page: restPayload.page || 1,
      limit: restPayload.limit || 50,
    };

    // Remove empty values
    Object.keys(cleanPayload).forEach(key => {
      if (
        key === "state" ||
        key === "city" ||
        key === "keywords" ||
        key === "brief" ||
        key === "category" ||
        key === "tender_subcategory" ||
        key === "product" ||
        key === "statezone" ||
        key === "tender_type"
      ) {
        if (Array.isArray(cleanPayload[key]) && cleanPayload[key].length === 0) {
          delete cleanPayload[key];
        }
      } else if (
        cleanPayload[key] === "" ||
        cleanPayload[key] === 0 ||
        cleanPayload[key] === undefined
      ) {
        if (key === "page" || key === "limit") return;
        delete cleanPayload[key];
      }
    });

    // Remove undefined isGem
    if (cleanPayload.isGem === undefined) {
      delete cleanPayload.isGem;
    }

    const params = typeof finalUserId !== "undefined" && finalUserId != null ? { user_id: String(finalUserId) } : undefined;
    // Legacy dashboard endpoint kept commented for quick rollback:
    // const endpoint = "/dashboard/boqlead";
    const endpoint = _useDashboardAdvanceSearch
      ? "/dashboard/advance-search/boqlead"
      : "/dashboard/boqlead";
    const response = await http.post(endpoint, cleanPayload, { params });
    const rawData = response?.data;

    console.log("Dashboard BOQ Lead API Response:", rawData);

    // Handle response - extract items array and total count
    let items = [];
    let total = 0;
    if (Array.isArray(rawData)) {
      items = rawData;
      total = rawData.length;
    } else if (Array.isArray(rawData?.results)) {
      items = rawData.results;
      total = rawData.total ?? rawData.count ?? rawData.totalCount ?? rawData.results.length;
    } else if (Array.isArray(rawData?.data)) {
      items = rawData.data;
      total = rawData.total ?? rawData.count ?? rawData.totalCount ?? rawData.data.length;
    }

    return { data: items, total };
  } catch (error) {
    console.error("Error in dashboard BOQ lead search:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return { data: [], total: 0 };
  }
}

/**
 * Fetch My BOQ Quote list for dashboard BOQ Lead.
 * POST /dashboard/boqlead/myboqquote with user_id, page, limit.
 * @param {string|number|null|undefined} userId - User ID (sent as user_id in payload)
 * @param {number} page - Page (default 1)
 * @param {number} limit - Page size (default 50)
 * @returns {Promise<{ total: number, data: Array<{ item, unitprice, proposed_price }> }>}
 */
export async function fetchMyBoqQuote(userId, page = 1, limit = 50) {
  const body = {
    user_id: userId != null ? Number(userId) : 0,
    page: Number(page) || 1,
    limit: Number(limit) || 50,
  };
  const response = await http.post("/dashboard/boqlead/myboqquote", body);
  const raw = response?.data ?? {};
  return {
    total: raw?.total ?? 0,
    data: Array.isArray(raw?.data) ? raw.data : [],
  };
}

/**
 * Fetch Bidders List for a Tender Result (Result Bidders API)
 * GET /tender-results/bidders/{id}?user_id=... â€” id is APEX RID (e.g. 500005189) or legacy result/tender id in path
 * Response shape: array of { result_id, bidder_name, bid_num, bid_type, aoc_rank, financial_rank,
 * bid_amount, rate_difference, technical_bid_status, financial_bid_status, aoc_bid_status, ... }
 * @param {string} id - Path segment (e.g. APEX RID 500005189)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as user_id query param
 * @returns {Promise<Array>} Array of bidder objects (with rank from financial_rank/aoc_rank when present)
 */
export async function fetchBidders(id, userId) {
  if (!id) return [];
  try {
    const encodedId = encodeURIComponent(id);
    const params = typeof userId !== "undefined" && userId != null ? { user_id: String(userId) } : undefined;
    const response = await http.get(`/tender-results/bidders/${encodedId}`, { params });

    let bidders = [];
    if (Array.isArray(response.data)) {
      bidders = response.data;
    } else if (response.data && Array.isArray(response.data.bidders)) {
      bidders = response.data.bidders;
    } else if (response.data && Array.isArray(response.data.data)) {
      bidders = response.data.data;
    } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
      bidders = response.data.results;
    }

    return bidders.map(normalizeBidderItem);
  } catch (error) {
    console.error("Error fetching bidders:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return [];
  }
}

/**
 * Fetch potential suppliers by category
 * GET /suppliers/search-all-potential-suppliers?category=...&limit=...&page=...
 * @param {string} category - Category text used for supplier search
 * @param {{limit?: number, page?: number}} [options]
 * @returns {Promise<Array<string>>}
 */
export async function fetchPotentialSuppliers(category, options = {}) {
  const categoryValue = String(category || "").trim();
  if (!categoryValue) return [];
  try {
    const limit = Number.isFinite(options.limit) ? options.limit : 100;
    const page = Number.isFinite(options.page) ? options.page : 1;
    const response = await http.get("/suppliers/search-all-potential-suppliers", {
      params: {
        category: categoryValue,
        limit,
        page
      }
    });

    const payload = response?.data;
    if (Array.isArray(payload?.suppliers)) {
      return payload.suppliers;
    }
    if (Array.isArray(payload?.data)) {
      return payload.data;
    }
    return [];
  } catch (error) {
    console.error("Error fetching potential suppliers:", error);
    return [];
  }
}

/**
 * Logged-in supplier discovery for BOQ lead details.
 * GET /suppliers/search-all-potential-suppliers?user_id=...&limit=...&page=...
 * @param {string|number} userId
 * @param {{ limit?: number, page?: number }} [options] - page is 0-based (default 0)
 * @returns {Promise<Array>} Raw supplier rows (strings or objects)
 */
export async function fetchSearchAllPotentialSuppliersForUser(userId, options = {}) {
  if (userId == null || String(userId).trim() === "") {
    return [];
  }
  const limit = Number.isFinite(Number(options.limit)) ? Number(options.limit) : 100;
  const page = Number.isFinite(Number(options.page)) ? Number(options.page) : 0;
  try {
    const response = await http.get("/suppliers/search-all-potential-suppliers", {
      params: {
        user_id: String(userId),
        limit,
        page,
      },
    });
    const payload = response?.data;
    if (Array.isArray(payload?.suppliers)) return payload.suppliers;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    if (Array.isArray(payload)) return payload;
    return [];
  } catch (error) {
    console.error("Error fetching search-all-potential-suppliers for user:", error);
    throw error;
  }
}

function normalizeSupplierNameValue(item) {
  if (item == null) return "";
  if (typeof item === "string") return item.trim();
  return String(
    item.supplier_name ||
      item.supplierName ||
      item.name ||
      item.company_name ||
      item.companyname ||
      item.vendor_name ||
      ""
  ).trim();
}

function normalizeSupplierNameList(rawSuppliers) {
  const suppliers = [];
  const addSupplier = (value) => {
    const supplier = normalizeSupplierNameValue(value);
    if (supplier && !suppliers.includes(supplier)) suppliers.push(supplier);
  };

  if (Array.isArray(rawSuppliers)) {
    rawSuppliers.forEach(addSupplier);
  } else if (typeof rawSuppliers === "string") {
    const trimmed = rawSuppliers.trim();
    if (trimmed) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) parsed.forEach(addSupplier);
        else addSupplier(parsed);
      } catch (_) {
        trimmed
          .replace(/^\{/, "")
          .replace(/\}$/, "")
          .split(",")
          .forEach(addSupplier);
      }
    }
  } else {
    addSupplier(rawSuppliers);
  }

  return suppliers;
}

/**
 * Public bulk supplier discovery by BOQ IDs.
 * POST /suppliers/search-by-boq-ids
 * @param {Array<string|number>} boqIds
 * @param {{ timeout?: number }} [options]
 * @returns {Promise<{ items: Array<{ boq_id: string, supplier_count: number, supplier_names: Array<string>, supplier_name: string|null }> }>}
 */
export async function fetchSuppliersByBoqIds(boqIds, options = {}) {
  const normalizedBoqIds = [...new Set((Array.isArray(boqIds) ? boqIds : [])
    .map((boqId) => String(boqId || "").trim())
    .filter((boqId) => boqId && boqId !== "N/A"))];
  if (normalizedBoqIds.length === 0) return { items: [] };

  const timeout = Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 180000;

  try {
    const response = await http.post("/suppliers/search-by-boq-ids", {
      boq_ids: normalizedBoqIds,
    }, { timeout });
    const payload = response?.data || {};
    const rawItems = Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

    const items = rawItems
      .map((item) => {
        const boqId = String(item?.boq_id ?? item?.boqId ?? "").trim();
        if (!boqId) return null;

        const supplierNames = normalizeSupplierNameList(item?.supplier_names ?? item?.supplierNames ?? item?.suppliers);
        const singleSupplier = normalizeSupplierNameValue(item?.supplier_name ?? item?.supplierName);
        if (singleSupplier && !supplierNames.includes(singleSupplier)) supplierNames.push(singleSupplier);

        return {
          boq_id: boqId,
          supplier_count: Number(item?.supplier_count ?? item?.supplierCount ?? supplierNames.length) || supplierNames.length,
          supplier_names: supplierNames,
          supplier_name: singleSupplier || null,
        };
      })
      .filter(Boolean);

    return { items };
  } catch (error) {
    console.error("Error fetching suppliers by BOQ IDs:", error);
    throw error;
  }
}

/**
 * Public supplier discovery using the existing potential-suppliers API.
 * GET /suppliers/search-all-potential-suppliers?boq_id=...&limit=...&page=...
 * @param {string|number} lookupId - BOQ ID for supplier lookup
 * @param {{ boqId?: string|number, boq_id?: string|number, category?: string, limit?: number, page?: number, userId?: string|number, user_id?: string|number, timeout?: number }} [options]
 * @returns {Promise<{ suppliers: Array<string>, total: number, limit: number, page: number }>}
 */
export async function fetchSuppliersByBoqId(lookupId, options = {}) {
  const boqIdValue = String(options.boq_id ?? options.boqId ?? lookupId ?? "").trim();
  const categoryValue = String(options.category || options.searchTerm || options.keyword || "").trim();
  const limit = Number.isFinite(Number(options.limit)) ? Number(options.limit) : 100;
  const page = Number.isFinite(Number(options.page)) ? Number(options.page) : 0;
  const timeout = Number.isFinite(Number(options.timeout)) ? Number(options.timeout) : 180000;
  const userId = options.userId ?? options.user_id;
  const params = {
    limit,
    page,
  };
  if (boqIdValue && boqIdValue !== "N/A") {
    params.boq_id = boqIdValue;
  } else if (categoryValue) {
    params.category = categoryValue;
  }
  if (userId != null && String(userId).trim() !== "") params.user_id = String(userId).trim();

  try {
    const response = await http.get("/suppliers/search-all-potential-suppliers", {
      params,
      timeout,
    });
    const payload = response?.data || {};
    const rawSuppliers = Array.isArray(payload?.suppliers)
      ? payload.suppliers
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
            ? payload
            : [];
    const suppliers = normalizeSupplierNameList(rawSuppliers);

    const total = Number(payload?.total ?? suppliers.length) || suppliers.length;
    return {
      suppliers,
      total,
      limit: Number(payload?.limit ?? limit) || limit,
      page: Number(payload?.page ?? page) || page,
    };
  } catch (error) {
    console.error("Error fetching search-all-potential-suppliers:", error);
    throw error;
  }
}

const BOQ_BID_PLAN_UPGRADE_FALLBACK_MESSAGE =
  "BOQ bid data is not available in your current plan. Please upgrade to Enterprise or Insight 360 to access this feature.";

/** Normalize BOQ bid-data API bodies (flat JSON, FastAPI `detail`, string payloads). */
function unwrapBoqBidApiPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    try {
      return unwrapBoqBidApiPayload(JSON.parse(raw));
    } catch {
      return { message: raw, error: raw };
    }
  }
  if (typeof raw !== "object") return null;
  const out = { ...raw };
  const det = raw.detail;
  if (typeof det === "string") {
    if (!out.error) out.error = det;
    if (!out.message && det) out.message = det;
  } else if (det && typeof det === "object" && !Array.isArray(det)) {
    Object.assign(out, det);
  } else if (Array.isArray(det) && det.length > 0 && det[0] && typeof det[0].msg === "string") {
    if (!out.message) out.message = det.map((x) => x.msg).filter(Boolean).join("; ");
  }
  return out;
}

function isBoqBidPlanUpgradePayload(d) {
  if (!d || typeof d !== "object") return false;
  const ru = d.requires_upgrade ?? d.requiresUpgrade;
  if (ru === true || ru === 1 || String(ru).toLowerCase() === "true" || String(ru) === "1") return true;
  const feat = String(d.upgrade_feature || d.upgradeFeature || "").toLowerCase();
  if (feat === "boq_view") return true;
  const err = String(d.error || d.Error || "").toLowerCase();
  if (err.includes("access denied")) return true;
  const msg = String(d.message || "").toLowerCase();
  if (msg.includes("not available in your current plan")) return true;
  if (msg.includes("upgrade to enterprise") || msg.includes("insight 360")) return true;
  if (
    Array.isArray(d.items) &&
    d.items.length === 0 &&
    (msg.includes("current plan") || msg.includes("upgrade"))
  ) {
    return true;
  }
  return false;
}

function boqBidPlanUpgradeResultFromPayload(raw) {
  const d = unwrapBoqBidApiPayload(raw);
  if (!d || !isBoqBidPlanUpgradePayload(d)) return null;
  return {
    requires_upgrade: true,
    message: d.message || BOQ_BID_PLAN_UPGRADE_FALLBACK_MESSAGE,
    upgrade_feature: d.upgrade_feature || d.upgradeFeature || "",
    error: d.error || d.Error || "Access denied",
  };
}

/**
 * Fetch BOQ Bid Data by Tender ID
 * @param {string} tenderId - The Tender ID
 * @param {string|number} [userId] - Optional user ID for query param user_id
 * @returns {Promise<Object|null>} BOQ bid data object with items array
 */
export async function fetchBoqBidData(tenderId, userId) {
  if (!tenderId) return null;
  try {
    const encodedTenderId = encodeURIComponent(tenderId);
    const params = {};
    if (userId != null && userId !== "") params.user_id = userId;
    console.log("Fetching BOQ bid data for tender ID:", tenderId, params.user_id != null ? { user_id: params.user_id } : "");
    const response = await http.get(`/boq/bid-data/${encodedTenderId}`, { params });

    console.log("BOQ Bid Data API Response:", response.data);

    if (response?.data != null) {
      const upgrade = boqBidPlanUpgradeResultFromPayload(response.data);
      if (upgrade) return upgrade;
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching BOQ bid data:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    const upgrade = boqBidPlanUpgradeResultFromPayload(error.response?.data);
    if (upgrade) return upgrade;
    return null;
  }
}

/**
 * Submit BOQ Bid
 * @param {Object} bidData - The bid data object
 * @param {string} bidData.userid - User ID
 * @param {number} bidData.boquniqueid - BOQ Unique ID
 * @param {string} bidData.item - Item description
 * @param {number} bidData.unitprice - Original unit price
 * @param {number} bidData.proposedprice - Proposed quantity (editable quantity)
 * @param {string} bidData.proposedunit - Unit of measure
 * @param {number} bidData.taxes - Tax percentage
 * @param {number} bidData.totalprice - Total price
 * @returns {Promise<Object|null>} Response data
 */
export async function submitBoqBid(bidData) {
  try {
    console.log("Submitting BOQ bid:", bidData);
    const response = await http.post("/boq-bids/", bidData);

    console.log("BOQ Bid Submission Response:", response.data);

    if (response?.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error submitting BOQ bid:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
}

/**
 * Submit BOQ Bids in Bulk
 * @param {Array} boqBids - Array of bid data objects
 * @param {Object} boqBids[].userid - User ID
 * @param {string} boqBids[].tenderid - Tender ID (mapped from apexid)
 * @param {number} boqBids[].boquniqueid - BOQ Unique ID
 * @param {number} boqBids[].boqdetailsid - BOQ Details ID
 * @param {string} boqBids[].item - Item description
 * @param {number} boqBids[].unitprice - Original unit price
 * @param {number} boqBids[].proposedprice - Proposed price
 * @param {number} boqBids[].proposedunit - Proposed unit (as number)
 * @param {number} boqBids[].taxes - Tax percentage
 * @param {number} boqBids[].totalprice - Total price
 * @returns {Promise<Object|null>} Response data
 */
export async function submitBoqBidsBulk(boqBids) {
  try {
    const payload = {
      boq_bids: boqBids
    };

    const response = await http.post("/boq-bids/bulk", payload);

    if (response?.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error submitting BOQ bids in bulk:", error);
    throw error;
  }
}

/**
 * Submit User Buying Requirements
 * @param {Object} requirementsData - The buying requirements data
 * @param {string} requirementsData.companyname - Company name
 * @param {string} requirementsData.contactname - Contact name
 * @param {string} requirementsData.userid - User ID
 * @param {string} requirementsData.mobile - Mobile number
 * @param {string} requirementsData.email - Email address
 * @param {string} requirementsData.producttobuy - Product to buy
 * @param {string} requirementsData.quantity - Quantity
 * @param {string} requirementsData.state - State
 * @param {string} requirementsData.city - City
 * @returns {Promise<Object|null>} Response data
 */
export async function submitBuyingRequirements(requirementsData) {
  try {
    const body = { ...requirementsData };
    if (body.userid != null && body.userid !== "") {
      body.userid = String(body.userid);
    }

    console.log("Submitting buying requirements to API:", body);
    console.log("API Endpoint: /user-buying-requirements/");

    const response = await http.post("/user-buying-requirements/", body);

    console.log("Buying Requirements Submission Response:", response);
    console.log("Response Data:", response.data);

    if (response?.data) {
      return response.data;
    }
    return response;
  } catch (error) {
    console.error("Error submitting buying requirements:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
      console.error("Error response headers:", error.response.headers);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    throw error;
  }
}

/**
 * Submit GeM free consultation request
 * POST /gem-consultation-req/
 * @param {Object} body
 * @param {string} body.companyname
 * @param {string} body.contactname
 * @param {string} body.mobilenumber
 * @param {string} body.email
 * @param {string} body.requiredservice
 * @returns {Promise<Object|null>}
 */
export async function submitGemConsultationRequest(body) {
  try {
    const response = await http.post("/gem-consultation-req/", body);
    if (response?.data) return response.data;
    return response;
  } catch (error) {
    console.error("Error submitting GeM consultation request:", error);
    throw error;
  }
}

/**
 * Submit business compliance support request
 * POST /business-compliance-req/
 * @param {Object} body
 * @param {string} body.companyname
 * @param {string} body.contactname
 * @param {string} body.mobilenumber
 * @param {string} body.email
 * @param {string} body.requiredservice
 * @returns {Promise<Object|null>}
 */
export async function submitBusinessComplianceRequest(body) {
  try {
    const response = await http.post("/business-compliance-req/", body);
    if (response?.data) return response.data;
    return response;
  } catch (error) {
    console.error("Error submitting business compliance request:", error);
    throw error;
  }
}

/**
 * Submit calendar event to calendar-module
 * POST /calendar-module/
 * @param {Object} payload
 * @param {number} [payload.uid=0] - User ID
 * @param {string} [payload.date] - Date (YYYY-MM-DD)
 * @param {string} [payload.event] - Event description
 * @param {number} [payload.entrykind=0] - 0 = Note, 1 = Reminder (IMP Reminder)
 * @param {number} [payload.apexid] - Apex tender ID (omitted from body when empty/0)
 * @param {number} [payload.apexrid] - Apex result ID (omitted from body when empty/0)
 * @param {number} [payload.boqid] - BOQ ID (omitted from body when empty/0)
 * @returns {Promise<Object>} Response data
 */
export async function submitCalendarEvent(payload) {
  const uidRaw = payload?.uid != null && payload?.uid !== "" ? Number(payload.uid) : 0;
  const uid = Number.isFinite(uidRaw) && uidRaw >= 0 ? uidRaw : 0;
  const entryRaw =
    payload?.entrykind != null && payload?.entrykind !== ""
      ? Number(payload.entrykind)
      : NaN;
  const entrykind = Number.isFinite(entryRaw) && entryRaw === 1 ? 1 : 0;
  const positiveId = (v) => {
    const n = v != null && v !== "" ? Number(v) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const body = {
    uid,
    date: payload?.date ?? new Date().toISOString().slice(0, 10),
    event: payload?.event != null && String(payload.event).trim() !== "" ? String(payload.event) : "string",
    entrykind,
  };
  const apexid = positiveId(payload?.apexid);
  if (apexid != null) body.apexid = apexid;
  const apexrid = positiveId(payload?.apexrid);
  if (apexrid != null) body.apexrid = apexrid;
  const boqid = positiveId(payload?.boqid);
  if (boqid != null) body.boqid = boqid;
  const response = await http.post("/calendar-module/", body);
  return response?.data ?? response;
}

/**
 * Fetch date counts for calendar view
 * GET /calendar-module/user/date-counts/{userId}/?month={month}&year={year}
 * @param {number} userId - User ID
 * @param {number} month - Month (1-12)
 * @param {number} year - Year (e.g., 2026)
 * @returns {Promise<{ data: Array<{ date: string, count: number }> }>}
 */
export async function fetchCalendarDateCounts(userId, month, year) {
  const response = await http.get(`/calendar-module/user/date-counts/${userId}`, {
    params: { month, year },
  });
  return response?.data ?? response;
}

/**
 * Fetch date-wise events for calendar
 * GET /calendar-module/user/date-wise/{userId}?date={date}&page={page}&limit={limit}
 * @param {number} userId - User ID
 * @param {string} date - Date in DD-MM-YYYY format
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 10)
 * @returns {Promise<{ data: Array<{ date: string, events: Array<string|{ event: string, apexid?: number, apexrid?: number, entrykind?: number }> }>, pagination: object }>}
 * Event objects may include entrykind: 0 = note, 1 = reminder.
 */
export async function fetchCalendarDateWiseEvents(userId, date, page = 1, limit = 10) {
  const response = await http.get(`/calendar-module/user/date-wise/${userId}`, {
    params: { date, page, limit },
  });
  return response?.data ?? response;
}

/**
 * Result-dashboard calendar note/reminder counts per user
 * GET /calendar-module/user/kind-counts/results/{userId}
 * @param {string|number} userId
 * @returns {Promise<{ uid?: number, notes_count: number, reminders_count: number }>}
 */
export async function fetchCalendarKindCountsResults(userId) {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId) : "";
  if (!uid) {
    return { notes_count: 0, reminders_count: 0 };
  }
  try {
    const response = await http.get(`/calendar-module/user/kind-counts/results/${encodeURIComponent(uid)}`);
    const data = response?.data ?? response;
    return {
      uid: data?.uid != null ? Number(data.uid) : undefined,
      notes_count: Number(data?.notes_count) || 0,
      reminders_count: Number(data?.reminders_count) || 0,
    };
  } catch (error) {
    console.error("Error fetching calendar kind counts (results):", error);
    return { notes_count: 0, reminders_count: 0 };
  }
}

/**
 * Tender-dashboard calendar note/reminder counts per user
 * GET /calendar-module/user/kind-counts/tenders/{userId}
 * @param {string|number} userId
 * @returns {Promise<{ uid?: number, notes_count: number, reminders_count: number }>}
 */
export async function fetchCalendarKindCountsTenders(userId) {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId) : "";
  if (!uid) {
    return { notes_count: 0, reminders_count: 0 };
  }
  try {
    const response = await http.get(`/calendar-module/user/kind-counts/tenders/${encodeURIComponent(uid)}`);
    const data = response?.data ?? response;
    return {
      uid: data?.uid != null ? Number(data.uid) : undefined,
      notes_count: Number(data?.notes_count) || 0,
      reminders_count: Number(data?.reminders_count) || 0,
    };
  } catch (error) {
    console.error("Error fetching calendar kind counts (tenders):", error);
    return { notes_count: 0, reminders_count: 0 };
  }
}

/**
 * Paginated calendar entries for a user (tender dashboard / IMP Reminder list)
 * GET /calendar-module/user/{userId}?page={page}&limit={limit}
 * @param {string|number} userId
 * @param {number} [page=1]
 * @param {number} [limit=100]
 * @returns {Promise<{ events?: Array<object> } | unknown>} Often `{ events: [...] }` with apexid, date, event, entrykind
 */
export async function fetchCalendarModuleUserEntries(userId, page = 1, limit = 100) {
  const uid = userId != null && String(userId).trim() !== "" ? String(userId) : "";
  if (!uid) {
    return { data: [] };
  }
  const response = await http.get(`/calendar-module/user/${encodeURIComponent(uid)}`, {
    params: { page, limit },
  });
  return response?.data ?? response;
}

/**
 * Homepage fresh tenders for one exact footer subcategory.
 * Errors are propagated so the feed can show its retry state.
 */
export async function fetchHomepageFreshTenders(subcategory, userId, { signal } = {}) {
  const params = { subcategory };
  if (userId != null) params.user_id = String(userId);
  const response = await http.get("/tenders/homepage-fresh", { params, signal });
  const result = response.data;
  if (!result || result.subcategory !== subcategory || !Array.isArray(result.tenders)) {
    throw new Error("Invalid homepage fresh-tenders response");
  }
  return result;
}

/**
 * Fetch subcategory master list for pricing / Sub-Category dropdowns
 * GET /utils/subcategory-master/?page=1&limit=1000
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Items per page (default 1000)
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array<{ subcategory: string }> }>}
 */
export async function fetchSubcategoryMaster(page = 1, limit = 700) {
  try {
    const response = await cachedTender360LookupGet(
      "/utils/subcategory-master/",
      { page, limit },
    );
    const out = response?.data ?? response;
    return {
      total: out?.total ?? 0,
      page: out?.page ?? 1,
      limit: out?.limit ?? limit,
      data: Array.isArray(out?.data) ? out.data : [],
    };
  } catch (error) {
    console.error("Error fetching subcategory master:", error);
    return { total: 0, page: 1, limit: 0, data: [] };
  }
}

/**
 * Fetch active subcategories grouped under an industry from industry_subcategory_mapping.
 * GET /utils/industry-subcategory-mapping/?industry=...
 * @param {string} industry - Industry label, e.g. Infrastructure & Roads
 * @returns {Promise<{ total: number, industry: string|null, data: Array<{ id?: number, industry: string, subcategory: string }> }>}
 */
export async function fetchIndustrySubcategoryMapping(industry = "") {
  const cleanIndustry = String(industry || "").trim();
  if (!cleanIndustry) {
    return { total: 0, industry: null, data: [] };
  }

  try {
    const response = await http.get("/utils/industry-subcategory-mapping/", {
      params: { industry: cleanIndustry },
    });
    const out = response?.data ?? response;
    return {
      total: out?.total ?? 0,
      industry: out?.industry ?? cleanIndustry,
      data: Array.isArray(out?.data) ? out.data : [],
    };
  } catch (error) {
    console.error("Error fetching industry subcategory mapping:", error);
    return { total: 0, industry: cleanIndustry, data: [] };
  }
}

/**
 * Fetch keyword-based subcategory autocomplete results.
 * GET /sub-category/autocomplete?q=...
 * @param {string} query - Partial keyword text (minimum 2 characters)
 * @param {number} limit - Maximum subcategories to return (default 250)
 * @returns {Promise<{ query: string, matched_keywords: string[], subcategories: string[], message?: string }>}
 */
export async function fetchKeywordAutocompleteSubcategories(query, limit = 250) {
  const q = String(query || "").trim();
  if (q.length < 2) {
    return { query: q, matched_keywords: [], subcategories: [] };
  }

  try {
    const response = await http.get("/sub-category/autocomplete", {
      params: { q, limit },
    });
    const data = response?.data || {};
    return {
      query: data.query || q,
      matched_keywords: Array.isArray(data.matched_keywords) ? data.matched_keywords : [],
      subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
      message: data.message,
    };
  } catch (error) {
    console.error("Error fetching keyword autocomplete subcategories:", error);
    return { query: q, matched_keywords: [], subcategories: [] };
  }
}

/**
 * Fetch unique filters data (states and cities)
 * @returns {Promise<Object>} Object with states and cities arrays
 */
export async function fetchUniqueFiltersData() {
  try {
    const response = await cachedTender360LookupGet(
      "/utils/unique-filters-data",
    );
    const data = response?.data || {};

    // Convert states array to dropdown options format
    const statesOptions = Array.isArray(data.states)
      ? data.states.map(state => ({ value: state, label: state }))
      : [];

    // Convert cities array to dropdown options format
    const citiesOptions = Array.isArray(data.cities)
      ? data.cities.map(city => ({ value: city, label: city }))
      : [];

    // Convert departments array to dropdown options format if available
    const departmentsOptions = Array.isArray(data.departments)
      ? data.departments.map(dept => ({ value: dept, label: dept }))
      : [];

    return {
      states: statesOptions,
      cities: citiesOptions,
      departments: departmentsOptions,
    };
  } catch (error) {
    console.error("Error fetching unique filters data:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return {
      states: [],
      cities: [],
      departments: [],
    };
  }
}

/**
 * Fetch auto-suggestions for category
 * @param {string} prefix - The search prefix (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 50)
 * @returns {Promise<Array>} Array of category suggestions
 */
export async function fetchCategorySuggestions(prefix, limit = 50) {
  if (!prefix || prefix.length < 3) {
    return [];
  }

  try {
    const response = await http.get("/utils/auto-suggest/category/", {
      params: { prefix: prefix.trim(), limit },
    });

    const data = response?.data || {};
    // Handle both formats: direct array or object with suggestions property
    if (Array.isArray(data)) {
      return data;
    } else if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions;
    }
    return [];
  } catch (error) {
    console.error("Error fetching category suggestions:", error);
    return [];
  }
}

/**
 * Fetch auto-suggestions for subcategory
 * @param {string} prefix - The search prefix (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 50)
 * @returns {Promise<Array>} Array of subcategory suggestions
 */
export async function fetchSubcategorySuggestions(prefix, limit = 50) {
  if (!prefix || prefix.length < 3) {
    return [];
  }

  try {
    const response = await http.get("/utils/auto-suggest/subcategory/", {
      params: { prefix: prefix.trim(), limit },
    });

    const data = response?.data || {};
    // Handle both formats: direct array or object with suggestions property
    if (Array.isArray(data)) {
      return data;
    } else if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions;
    }
    return [];
  } catch (error) {
    console.error("Error fetching subcategory suggestions:", error);
    return [];
  }
}

/**
 * Fetch sub-category autocomplete suggestions.
 * GET /sub-category/autocomplete?q=ci&limit=250
 * @param {string} query - Search text (minimum 2 characters)
 * @param {number} limit - Maximum number of results
 * @param {AbortSignal} [signal] - Optional abort signal for stale requests
 * @param {object} [options] - Optional API flags
 * @param {boolean} [options.includeProduct] - Whether backend should match product text
 * @returns {Promise<Array>} Array of sub-category suggestions
 */
export async function fetchSubcategoryAutocomplete(
  query,
  limit = 250,
  signal,
  options = {},
) {
  const q = String(query || "").trim();
  if (q.length <= 1) {
    return [];
  }


  const sortByAutocompleteRelevance = (items) => {
    const qLower = q.toLowerCase();
    const getItemText = (item) => {
      if (typeof item === "string") return item;
      if (!item || typeof item !== "object") return "";
      return String(
        item.keyword ??
          item.keywords ??
          item.matchedKeyword ??
          item.label ??
          item.value ??
          item.subcategory ??
          item.subindustry ??
          "",
      );
    };
    const scoreItem = (item) => {
      const text = getItemText(item).trim().toLowerCase();
      if (!text) return [9, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, ""];
      const index = text.indexOf(qLower);
      if (text === qLower) return [0, 0, text.length, text];
      if (text.startsWith(qLower)) return [1, 0, text.length, text];
      if (index >= 0) return [2, index, text.length, text];
      return [8, Number.MAX_SAFE_INTEGER, text.length, text];
    };

    return [...items].sort((a, b) => {
      const scoreA = scoreItem(a);
      const scoreB = scoreItem(b);
      for (let i = 0; i < scoreA.length; i += 1) {
        if (scoreA[i] < scoreB[i]) return -1;
        if (scoreA[i] > scoreB[i]) return 1;
      }
      return 0;
    });
  };

  try {
    const params = { q, limit };
    if (typeof options.includeProduct === "boolean") {
      params.include_product = options.includeProduct;
    }

    const response = await http.get("/sub-category/autocomplete", {
      params,
      signal,
    });

    const data = response?.data ?? response;
    if (Array.isArray(data)) {
      return data;
    }
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    if (Array.isArray(data?.suggestions)) {
      return data.suggestions;
    }
    if (Array.isArray(data?.results)) {
      return data.results;
    }
    if (Array.isArray(data?.items)) {
      return sortByAutocompleteRelevance(data.items);
    }
    if (Array.isArray(data?.subcategories)) {
      const matchedKeywords = Array.isArray(data?.matched_keywords)
        ? data.matched_keywords
            .map((keyword) => String(keyword || "").trim())
            .filter(Boolean)
        : [];
      const exactMatchedKeyword =
        matchedKeywords.find(
          (keyword) => keyword.toLowerCase() === q.toLowerCase(),
        ) || matchedKeywords[0];

      return data.subcategories.map((subcategory) => ({
        value: subcategory,
        label: exactMatchedKeyword
          ? `${exactMatchedKeyword} â€“ ${subcategory}`
          : subcategory,
        matchedKeyword: exactMatchedKeyword,
        suggestionType: "subcategory",
      }));
    }
    return [];
  } catch (error) {
    if (
      error?.name === "CanceledError" ||
      error?.code === "ERR_CANCELED" ||
      signal?.aborted
    ) {
      return [];
    }
    console.error("Error fetching subcategory autocomplete:", error);
    return [];
  }
}

/**
 * Fetch products by one or more subcategories (classification master).
 * POST /utils/classification-master/products/?page=&limit=
 * Body: { subcategory: string[] }
 * @param {string|string[]} subcategoryOrSubcategories - One subcategory name, or a list of names
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 500)
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array<{ product: string }> }>}
 */
export async function fetchProductsBySubcategory(
  subcategoryOrSubcategories,
  page = 1,
  limit = 500,
) {
  const raw = Array.isArray(subcategoryOrSubcategories)
    ? subcategoryOrSubcategories
    : subcategoryOrSubcategories != null && String(subcategoryOrSubcategories).trim() !== ""
      ? [subcategoryOrSubcategories]
      : [];
  const subList = [...new Set(raw.map((s) => String(s ?? "").trim()).filter(Boolean))];
  if (subList.length === 0) {
    return { total: 0, page: 1, limit: 0, data: [] };
  }
  try {
    const response = await cachedTender360LookupPost(
      "/utils/classification-master/products/",
      { subcategory: subList },
      { page, limit },
    );
    const out = response?.data ?? response;
    return {
      total: out?.total ?? 0,
      page: out?.page ?? 1,
      limit: out?.limit ?? limit,
      data: Array.isArray(out?.data) ? out.data : [],
    };
  } catch (error) {
    console.error("Error fetching products by subcategory:", error);
    return { total: 0, page: 1, limit: 0, data: [] };
  }
}

/**
 * Product name suggestions (min 3 characters).
 * When `subcategory` is set: loads GET /utils/classification-master/products/ for that subcategory
 * and returns names whose text includes the typed prefix (case-insensitive).
 * Otherwise: GET /utils/auto-suggest/product/ (same response shape as category/subcategory auto-suggest).
 * @param {string} prefix
 * @param {number} [limit=50]
 * @param {string|null|undefined} [subcategory]
 * @returns {Promise<string[]>}
 */
export async function fetchProductSuggestions(prefix, limit = 50, subcategory = null) {
  const p = prefix != null ? String(prefix).trim() : "";
  if (!p || p.length < 3) {
    return [];
  }

  const sub = subcategory != null ? String(subcategory).trim() : "";

  if (sub) {
    try {
      const res = await fetchProductsBySubcategory(sub, 1, 500);
      const rows = Array.isArray(res?.data) ? res.data : [];
      const q = p.toLowerCase();
      const names = [
        ...new Set(
          rows
            .map((d) => {
              const name = d?.product ?? d?.name ?? d?.value;
              return name ? String(name).trim() : "";
            })
            .filter(Boolean),
        ),
      ];
      const filtered = names.filter((n) => n.toLowerCase().includes(q));
      filtered.sort((a, b) => a.localeCompare(b));
      return filtered.slice(0, limit);
    } catch (error) {
      console.error("Error fetching product suggestions (classification products):", error);
      return [];
    }
  }

  try {
    const response = await http.get("/utils/auto-suggest/product/", {
      params: { prefix: p, limit },
    });

    const data = response?.data || {};
    if (Array.isArray(data)) {
      return data;
    }
    if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions;
    }
    return [];
  } catch (error) {
    console.error("Error fetching product suggestions:", error);
    return [];
  }
}


/**
 * Fetch keywords by subcategory
 * GET /utils/classification-master/keywords/?subcategory={subcategory}&product={product?}&page=1&limit=500
 * @param {string} subcategory - Subcategory name
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 500)
 * @param {string|null|undefined} [product] - Optional selected product; sent when set so keywords are scoped to that product
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array<{ keyword: string }> }>}
 */
export async function fetchKeywordsBySubcategory(subcategory, page = 1, limit = 500, product = null) {
  if (!subcategory || !subcategory.trim()) {
    return { total: 0, page: 1, limit: 0, data: [] };
  }
  try {
    const params = { subcategory: subcategory.trim(), page, limit };
    const productTrimmed = product != null ? String(product).trim() : "";
    if (productTrimmed) params.product = productTrimmed;
    const response = await cachedTender360LookupGet(
      "/utils/classification-master/keywords/",
      params,
    );
    const out = response?.data ?? response;
    return {
      total: out?.total ?? 0,
      page: out?.page ?? 1,
      limit: out?.limit ?? limit,
      data: Array.isArray(out?.data) ? out.data : [],
    };
  } catch (error) {
    console.error("Error fetching keywords by subcategory:", error);
    return { total: 0, page: 1, limit: 0, data: [] };
  }
}

/**
 * Fetch keywords by product
 * GET /utils/classification-master/keywords-by-product/?product={product}&subcategory={subcategory?}&page=1&limit=100
 * @param {string} product - Product name
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 100)
 * @param {string|null|undefined} [subcategory] - Optional tender sub-category for scoped keyword lists
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array<{ keyword: string }> }>}
 */
export async function fetchKeywordsByProduct(product, page = 1, limit = 500, subcategory = null) {
  if (!product || !product.trim()) {
    return { total: 0, page: 1, limit: 0, data: [] };
  }
  try {
    const params = { product: product.trim(), page, limit };
    const subTrimmed = subcategory != null ? String(subcategory).trim() : "";
    if (subTrimmed) params.subcategory = subTrimmed;
    const response = await cachedTender360LookupGet(
      "/utils/classification-master/keywords-by-product/",
      params,
    );
    const out = response?.data ?? response;
    return {
      total: out?.total ?? 0,
      page: out?.page ?? 1,
      limit: out?.limit ?? limit,
      data: Array.isArray(out?.data) ? out.data : [],
    };
  } catch (error) {
    console.error("Error fetching keywords by product:", error);
    return { total: 0, page: 1, limit: 0, data: [] };
  }
}

/**
 * Extract list rows from NOT IN keywords API (supports multiple response envelopes).
 * @param {*} out - Parsed JSON body
 * @returns {Array<string|Record<string, unknown>>}
 */
function extractNotInKeywordsResponseRows(out) {
  if (out == null) return [];
  if (Array.isArray(out)) return out;
  if (typeof out !== "object") return [];
  const keys = [
    "data",
    "results",
    "items",
    "not_in_keywords",
    "notinkeywords",
    "keywords",
    "suggestions",
    "rows",
  ];
  for (const key of keys) {
    const v = out[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/**
 * Map one API row to a display string for NOT IN keyword chips.
 * @param {*} row
 * @returns {string}
 */
function notInKeywordRowToString(row) {
  if (row == null) return "";
  if (typeof row === "string") return String(row).trim();
  if (typeof row !== "object") return String(row).trim();
  const raw =
    row.keyword ??
    row.not_in_keyword ??
    row.notinkeyword ??
    row.not_in_keyword_name ??
    row.exclude_keyword ??
    row.excludeKeyword ??
    row.name ??
    row.label ??
    row.value ??
    row.text ??
    row.suggestion ??
    row.title ??
    "";
  return String(raw).trim();
}

/**
 * Fetch NOT IN (exclusion) keyword suggestions for selected include-keywords
 * POST /utils/classification-master/not-in-keywords/
 * Body: { keywords: string[], subcategory: string, products: string[] }
 * @param {string|string[]} keywordsInput - One or more selected keywords (classification)
 * @param {number} _page - Reserved (pagination not in current API body)
 * @param {number} _limit - Reserved (pagination not in current API body)
 * @param {{ subcategory?: string, products?: string[] }} [context] - Subcategory label and selected products from query module
 * @returns {Promise<{ total: number, page: number, limit: number, data: string[] }>}
 */
export async function fetchNotInKeywords(keywordsInput, _page = 1, _limit = 100, context = {}) {
  const raw = Array.isArray(keywordsInput) ? keywordsInput : [keywordsInput];
  const keywords = [...new Set(raw.map((k) => String(k ?? "").trim()).filter(Boolean))];
  if (keywords.length === 0) {
    return { total: 0, page: 1, limit: 0, data: [] };
  }
  const subcategory = String(context.subcategory ?? "").trim();
  const products = Array.isArray(context.products)
    ? [...new Set(context.products.map((p) => String(p ?? "").trim()).filter(Boolean))]
    : [];
  try {
    const response = await http.post("/utils/classification-master/not-in-keywords/", {
      keywords,
      subcategory,
      products,
    });
    const out = response?.data ?? response;
    const rows = extractNotInKeywordsResponseRows(out);
    const strings = [...new Set(rows.map(notInKeywordRowToString).filter(Boolean))];
    const total =
      typeof out?.total === "number"
        ? out.total
        : typeof out?.count === "number"
          ? out.count
          : strings.length;
    return {
      total,
      page: out?.page ?? _page,
      limit: out?.limit ?? _limit,
      data: strings,
    };
  } catch (error) {
    console.error("Error fetching not-in keywords:", error);
    return { total: 0, page: 1, limit: 0, data: [] };
  }
}

/**
 * Fetch auto-suggestions for keywords
 * @param {string} prefix - The search prefix (minimum 3 characters)
 * @param {number} limit - Maximum number of results (default: 50)
 * @returns {Promise<Array>} Array of keyword suggestions
 */
export async function fetchKeywordSuggestions(prefix, limit = 50) {
  if (!prefix || prefix.length < 3) {
    return [];
  }

  try {
    const response = await http.get("/utils/auto-suggest/keywords/", {
      params: { prefix: prefix.trim(), limit },
    });

    const data = response?.data || {};
    // Handle both formats: direct array or object with suggestions property
    if (Array.isArray(data)) {
      return data;
    } else if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions;
    }
    return [];
  } catch (error) {
    console.error("Error fetching product suggestions:", error);
    return [];
  }
}

/**
 * Fetch auto-suggestions for agency/tendering authority
 * @param {string} prefix - The search prefix (minimum 3 characters, unless `allowEmptyPrefix`)
 * @param {number} [limit] - When set, sent as `limit` query param; omitted otherwise (backend default)
 * @param {{ allowEmptyPrefix?: boolean }} [options] - Set `allowEmptyPrefix` to fetch with empty prefix (e.g. footer listings)
 * @returns {Promise<Array>} Array of agency suggestions
 */
export async function fetchAgencySuggestions(prefix, limit, options = {}) {
  const trimmed = typeof prefix === "string" ? prefix.trim() : "";
  const allowEmpty = options.allowEmptyPrefix === true;
  if (!allowEmpty && (!trimmed || trimmed.length < 3)) {
    return [];
  }

  try {
    const params = { prefix: trimmed };
    if (limit != null && limit !== "") {
      params.limit = limit;
    }
    const response = await cachedTender360LookupGet(
      "/utils/auto-suggest/agency/",
      params,
    );

    const data = response?.data || {};
    // Handle both formats: direct array or object with suggestions property
    if (Array.isArray(data)) {
      return data;
    } else if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions;
    }
    return [];
  } catch (error) {
    console.error("Error fetching agency suggestions:", error);
    return [];
  }
}

function normalizeGlobalSuggestRow(row) {
  if (row == null) return null;
  if (typeof row === "string") {
    const s = row.trim();
    return s ? { id: s, label: s, type: "keyword" } : null;
  }
  const label =
    row.label ??
    row.name ??
    row.title ??
    row.text ??
    row.value ??
    "";
  const labelStr = String(label).trim();
  if (!labelStr) return null;
  const rawType = row.type ?? row.entity ?? row.category ?? row.kind ?? "keyword";
  const type = String(rawType)
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  const id = row.id ?? row.pk ?? row.slug ?? labelStr;
  return { id, label: labelStr, type };
}

function parseGlobalSuggestResponseBody(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && Array.isArray(data.suggestions)) return data.suggestions;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

/**
 * Hero / global unified search suggestions.
 * GET /globalsearch/suggest?q=...&limit=40
 * @param {string} query
 * @param {number} [limit=40]
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<{ id: string, label: string, type: string }>>}
 */
export async function fetchGlobalSearchSuggest(query, limit = 40, signal) {
  const q = String(query || "").trim();
  if (q.length < 2) {
    return [];
  }

  try {
    const config = {
      params: { q, limit },
    };
    if (signal) {
      config.signal = signal;
    }
    const response = await http.get("/globalsearch/suggest", config);
    const raw = parseGlobalSuggestResponseBody(response?.data);
    return raw.map(normalizeGlobalSuggestRow).filter(Boolean);
  } catch (error) {
    if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
      return [];
    }
    console.error("Error fetching global search suggestions:", error);
    return [];
  }
}

/**
 * Fetch auto-suggestions for bidder/supplier name
 * @param {string} prefix - The search prefix
 * @param {number} limit - Maximum number of suggestions (default: 50)
 * @returns {Promise<Array<string>>} Array of suggestion strings
 */
export async function fetchBidderNameSuggestions(prefix, limit = 50) {
  if (!prefix || prefix.trim().length === 0) {
    console.log("fetchBidderNameSuggestions: Empty prefix, returning empty array");
    return [];
  }

  try {
    const url = "/utils/auto-suggest/biddername/";
    const params = { prefix: prefix.trim(), limit };
    console.log("Calling API:", url, "with params:", params);

    const response = await cachedTender360LookupGet(url, params);
    console.log("API Response:", response?.data);

    const data = response?.data || {};
    const asStrings = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((s) => (s == null ? "" : String(s).trim()))
        .filter((s) => s.length > 0);
    // Handle both formats: direct array or { suggestions: string[] }
    if (Array.isArray(data)) {
      console.log("Returning array format:", data);
      return asStrings(data);
    }
    if (data.suggestions && Array.isArray(data.suggestions)) {
      console.log("Returning suggestions format:", data.suggestions);
      return asStrings(data.suggestions);
    }
    console.log("No valid data format found, returning empty array");
    return [];
  } catch (error) {
    console.error("Error fetching bidder name suggestions:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return [];
  }
}

/**
 * Search suppliers by name (prefix)
 * GET /suppliers/search?prefix=...&limit=...&page=...
 * @param {string} prefix - Search prefix (supplier name)
 * @param {number} limit - Max results per page (default: 10)
 * @param {number} page - Page index (default: 0)
 * @returns {Promise<{ suppliers: Array<{ supplier_name: string, total_tenders_participated: number, total_tenders_won: number, total_contract_value: string, last_contract_date: string, first_participation_date: string }>, total: number }>}
 */
export async function searchSuppliersByName(prefix, limit = 10, page = 0) {
  if (!prefix || String(prefix).trim().length === 0) {
    return { suppliers: [], total: 0 };
  }

  try {
    const response = await http.get("/suppliers/search", {
      params: {
        prefix: String(prefix).trim(),
        limit: Number(limit) || 10,
        page: Number(page) >= 0 ? Number(page) : 0,
      },
    });
    const data = response?.data ?? {};
    return {
      suppliers: Array.isArray(data.suppliers) ? data.suppliers : [],
      total: typeof data.total === "number" ? data.total : 0,
    };
  } catch (error) {
    console.error("Error searching suppliers by name:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return { suppliers: [], total: 0 };
  }
}

const normalize360RateAnalysisList = (value) => {
  const values = Array.isArray(value) ? value : value != null ? [value] : [];
  return [...new Set(values.map((item) => String(item ?? "").trim()).filter(Boolean))];
};

/** Build the backend Tender360StoredProcedureRequest shape used by all rate-analysis APIs. */
const build360RateAnalysisPayload = (payload = {}, includePagination = false) => {
  const body = {};
  const bidderName = String(payload.bidder_name ?? "").trim();
  const subcategory = filterDefaultCategory(
    normalize360RateAnalysisList(payload.subcategory),
  ) ?? [];
  const product = normalize360RateAnalysisList(payload.product);
  const keyword = normalize360RateAnalysisList(payload.keyword);
  const department = normalize360RateAnalysisList(payload.department);
  const zone = normalize360RateAnalysisList(payload.zone);
  const state = normalize360RateAnalysisList(payload.state);
  const city = normalize360RateAnalysisList(payload.city);

  if (bidderName) body.bidder_name = bidderName;
  if (subcategory.length) body.subcategory = subcategory;
  if (product.length) body.product = product;
  if (keyword.length) body.keyword = keyword;
  if (department.length) body.department = department;
  if (zone.length) body.zone = zone;
  if (state.length) body.state = state;
  if (city.length) body.city = city;
  if (payload.search_bidder != null && String(payload.search_bidder).trim()) {
    body.search_bidder = String(payload.search_bidder).trim();
  }
  if (payload.tender_type != null && String(payload.tender_type).trim()) {
    body.tender_type = String(payload.tender_type).trim().toLowerCase();
  }
  if (payload.tender_value_min != null) body.tender_value_min = payload.tender_value_min;
  if (payload.tender_value_max != null) body.tender_value_max = payload.tender_value_max;
  if (payload.date_from) body.date_from = payload.date_from;
  if (payload.date_to) body.date_to = payload.date_to;
  if (payload.sort_by != null && String(payload.sort_by).trim()) {
    body.sort_by = String(payload.sort_by).trim().toLowerCase();
  }
  if (payload.sort_order != null && String(payload.sort_order).trim()) {
    body.sort_order = String(payload.sort_order).trim().toLowerCase();
  }
  if (includePagination) {
    body.page = Math.max(Number(payload.page) || 1, 1);
    body.limit = Math.min(Math.max(Number(payload.limit) || 10, 1), 100);
  }
  if (payload?.[TENDER360_FORCE_REFRESH_KEY] === true) {
    body[TENDER360_FORCE_REFRESH_KEY] = true;
  }
  return body;
};

/** POST /get-360-my-bid-rate-analysis */
export async function get360MyBidRateAnalysis(payload) {
  const response = await cachedTender360Post(
    "/get-360-my-bid-rate-analysis",
    build360RateAnalysisPayload(payload, true),
  );
  return response.data;
}

/** POST /get-360-gap-margin-analysis */
export async function get360GapMarginAnalysis(payload) {
  const response = await cachedTender360Post(
    "/get-360-gap-margin-analysis",
    build360RateAnalysisPayload(payload, true),
  );
  return response.data;
}

/** POST /get-360-competitor-bid-rate-analysis */
export async function get360CompetitorBidRateAnalysis(payload) {
  const response = await cachedTender360Post(
    "/get-360-competitor-bid-rate-analysis",
    build360RateAnalysisPayload(payload, true),
  );
  return response.data;
}

/**
 * Tender 360 competitor-matrix bidder summary list.
 * POST /get-360-bidder-summary-list
 */
export async function get360BidderSummaryList(payload) {
  const response = await cachedTender360Post("/get-360-bidder-summary-list", payload);
  return response?.data ?? response;
}

/**
 * Tender 360 comparison summary for two bidders.
 * POST /get-360-compare-bidders
 */
export async function get360CompareBidders(payload) {
  const response = await cachedTender360Post("/get-360-compare-bidders", payload);
  return response?.data ?? response;
}

/**
 * Tender 360 state-wise comparison for two bidders.
 * POST /get-360-compare-bidders-state
 */
export async function get360CompareBiddersState(payload) {
  const response = await cachedTender360Post("/get-360-compare-bidders-state", payload);
  return response?.data ?? response;
}

/**
 * Tender 360 ownership-wise comparison for two bidders.
 * POST /get-360-compare-bidders-ownership
 */
export async function get360CompareBiddersOwnership(payload) {
  const response = await cachedTender360Post(
    "/get-360-compare-bidders-ownership",
    payload,
  );
  return response?.data ?? response;
}

/**
 * Tender 360 bid-clash comparison for two bidders.
 * POST /get-360-bidder-clash-comparison
 */
export async function get360BidderClashComparison(payload) {
  const response = await cachedTender360Post(
    "/get-360-bidder-clash-comparison",
    payload,
  );
  return response?.data ?? response;
}

/**
 * Tender 360 strongest/weakest analysis for two bidders.
 * POST /get-360-compare-bidders-strength-analysis
 */
export async function get360CompareBiddersStrengthAnalysis(payload) {
  const response = await cachedTender360Post(
    "/get-360-compare-bidders-strength-analysis",
    payload,
  );
  return response?.data ?? response;
}

/** POST /get-360-competitor-rate-analysis-detail */
export async function get360CompetitorRateAnalysisDetail(payload) {
  const response = await cachedTender360Post(
    "/get-360-competitor-rate-analysis-detail",
    build360RateAnalysisPayload(payload, true),
  );
  return response.data;
}

/**
 * Search suppliers by category
 * @param {string} category - Category name
 * @param {number} limit - Number of results to return (default: 100)
 * @param {number} offset - Offset for pagination (default: 0)
 * @returns {Promise<Array>} Array of supplier names
 */
export async function searchSuppliersByCategory(category, limit = 100, offset = 0) {
  if (!category || category.trim().length === 0) {
    return [];
  }

  try {
    const url = "/suppliers/search-by-category";
    const params = {
      category: category.trim(),
      limit,
      offset
    };

    const response = await http.get(url, { params });
    const data = response?.data || {};

    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    } else if (Array.isArray(data.results)) {
      return data.results;
    } else if (Array.isArray(data.suppliers)) {
      return data.suppliers;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    }

    return [];
  } catch (error) {
    console.error("Error searching suppliers by category:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return [];
  }
}

/**
 * Advanced search for suppliers
 * @param {Object} searchParams - Search parameters
 * @param {Array<string>} searchParams.states - Array of state names
 * @param {Array<string>} searchParams.cities - Array of city names
 * @param {Array<string>} searchParams.departments - Array of department names
 * @param {string} searchParams.bidder_name - Bidder/supplier name
 * @param {number} searchParams.tender_value_min - Minimum tender value
 * @param {number} searchParams.tender_value_max - Maximum tender value
 * @param {number} searchParams.contract_value_min - Minimum contract value
 * @param {number} searchParams.contract_value_max - Maximum contract value
 * @param {number} searchParams.limit - Number of results (default: 100)
 * @param {number} searchParams.page - Page number (default: 0)
 * @returns {Promise<Array>} Array of supplier names
 */
export async function advancedSearchSuppliers(searchParams) {
  try {
    const payload = {
      keyword: searchParams.keyword || "",
      criteria: searchParams.criteria || "",
      states: searchParams.states || [],
      cities: searchParams.cities || [],
      departments: searchParams.departments || [],
      tender_value_min: searchParams.tender_value_min || 0,
      tender_value_max: searchParams.tender_value_max || 0,
      contract_value_min: searchParams.contract_value_min || 0,
      contract_value_max: searchParams.contract_value_max || 0,
      limit: searchParams.limit || 100,
      page: searchParams.page || 1
    };

    // Remove empty arrays and zero values for optional fields
    // Keep keyword even if empty (API might need it)
    // if (!payload.keyword || payload.keyword.trim() === "") delete payload.keyword;
    if (!payload.criteria || payload.criteria.trim() === "") delete payload.criteria;
    if (payload.states.length === 0) delete payload.states;
    if (payload.cities.length === 0) delete payload.cities;
    if (payload.departments.length === 0) delete payload.departments;
    if (payload.tender_value_min === 0) delete payload.tender_value_min;
    if (payload.tender_value_max === 0) delete payload.tender_value_max;
    if (payload.contract_value_min === 0) delete payload.contract_value_min;
    if (payload.contract_value_max === 0) delete payload.contract_value_max;

    const response = await http.post("/suppliers/advanced-search", payload);
    const data = response?.data || {};

    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    } else if (Array.isArray(data.results)) {
      return data.results;
    } else if (Array.isArray(data.suppliers)) {
      return data.suppliers;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    }

    return [];
  } catch (error) {
    console.error("Error in advanced supplier search:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return [];
  }
}

/**
 * Fetch all zones (e.g. North, South, East, West, Central)
 * Uses auto-suggest zones endpoint.
 * @returns {Promise<Array<{value:string,label:string}>>}
 */
export async function fetchZones() {
  try {
    const response = await http.get("/utils/auto-suggest/all-zones/");
    const data = response?.data || {};

    let suggestions = [];
    if (Array.isArray(data)) {
      suggestions = data;
    } else if (Array.isArray(data.suggestions)) {
      suggestions = data.suggestions;
    }

    return suggestions.map((z) => ({
      value: z,
      label: z,
    }));
  } catch (error) {
    console.error("Error fetching zones:", error);
    return [];
  }
}

/**
 * Fetch all authority ownership labels (GET /utils/auto-suggest/all-ownership/).
 * @returns {Promise<Array<{value:string,label:string}>>}
 */
export async function fetchAllOwnershipSuggestions() {
  try {
    const response = await http.get("/utils/auto-suggest/all-ownership/");
    const data = response?.data || {};
    let suggestions = [];
    if (Array.isArray(data)) {
      suggestions = data;
    } else if (Array.isArray(data.suggestions)) {
      suggestions = data.suggestions;
    }
    return suggestions
      .map((x) =>
        typeof x === "string" ? x.trim() : String(x ?? "").trim(),
      )
      .filter(Boolean)
      .map((label) => ({ value: label, label }));
  } catch (error) {
    console.error("Error fetching ownership suggestions:", error);
    return [];
  }
}

/**
 * Remove near-duplicate location rows from dropdown data (e.g. "A & B" vs "A and B").
 * Keeps the first occurrence so downstream APIs still receive a stable string.
 * @param {Array<{ value: *, label: * }>} options
 * @returns {Array<{ value: string, label: string }>}
 */
function dedupeLocationSelectOptions(options) {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  const out = [];
  for (const opt of options) {
    const label = String(opt?.label ?? opt?.value ?? "").trim();
    const value = String(opt?.value ?? opt?.label ?? "").trim();
    if (!label) continue;
    const key = label
      .toLowerCase()
      .replace(/\s*&\s*/g, " and ")
      .replace(/\s+/g, " ")
      .trim();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      value: value || label,
      label: label || value,
    });
  }
  return out;
}

/**
 * Fetch states by zone
 * @param {string|null} zone - The zone name (e.g., "North", "South", "East", "West", "Central") or null for all states
 * @returns {Promise<Array>} Array of state objects with value and label
 */
export async function fetchStatesByZone(zone, options = {}) {
  try {
    // New auto-suggest endpoint; supports zone = null (all states). Send zone: null explicitly when no zone.
    const zoneValue = typeof zone === "string" && zone.trim() ? zone.trim() : null;
    const body = options.omitZone ? {} : { zone: zoneValue };
    const response = await cachedTender360LookupPost(
      "/utils/auto-suggest/all-states/",
      body,
    );

    const data = response?.data || {};
    let states = [];

    // Handle different response formats
    if (Array.isArray(data)) {
      states = data;
    } else if (Array.isArray(data.suggestions)) {
      states = data.suggestions;
    } else if (data.states && Array.isArray(data.states)) {
      states = data.states;
    } else if (data.results && Array.isArray(data.results)) {
      states = data.results;
    }

    // Convert to dropdown options format
    const mapped = states.map((state) => {
      if (typeof state === "string") {
        return { value: state, label: state };
      }
      if (state && typeof state === "object") {
        return {
          value: state.value || state.name || state.state || state,
          label: state.label || state.name || state.state || state.value || state,
        };
      }
      return { value: state, label: String(state) };
    });
    return dedupeLocationSelectOptions(mapped);
  } catch (error) {
    console.error("Error fetching states by zone:", error);
    return [];
  }
}

/** Fetch all state options without sending a zone request parameter. */
export async function fetchAllStates() {
  return fetchStatesByZone(null, { omitZone: true });
}

/**
 * Fetch cities by states
 * @param {Array<string>} states - Array of state names
 * @returns {Promise<Array>} Array of city objects with value and label
 */
/**
 * Given a city name, resolve possible state + zone rows (signup / location step).
 * POST /utils/auto-suggest/all-states-and-zones/
 * @param {string} cityName
 * @returns {Promise<{ city: string, matches: Array<{ state: string, zone: string }> }>}
 */
export async function fetchStateZoneMatchesForCity(cityName) {
  const city = String(cityName || "").trim();
  if (!city) {
    return { city: "", matches: [] };
  }
  try {
    const response = await http.post("/utils/auto-suggest/all-states-and-zones/", {
      city,
    });
    const data = response?.data || {};
    const raw = Array.isArray(data.matches) ? data.matches : [];
    const matches = raw
      .map((m) => ({
        state: String(m?.state ?? m?.State ?? "").trim(),
        zone: String(m?.zone ?? m?.Zone ?? "").trim(),
      }))
      .filter((m) => m.state && m.zone);
    const resolvedCity =
      data.city != null && String(data.city).trim() !== "" ? String(data.city).trim() : city;
    return { city: resolvedCity, matches };
  } catch (error) {
    console.error("Error fetching state/zone matches for city:", error);
    throw error;
  }
}

export async function fetchCitiesByStates(states) {
  const statesPayload = Array.isArray(states) ? states : [];
  try {
    // New auto-suggest endpoint; send states: [] for all cities
    const response = await cachedTender360LookupPost(
      "/utils/auto-suggest/all-cities/",
      { states: statesPayload },
    );

    const data = response?.data || {};
    let cities = [];

    // Handle different response formats
    if (Array.isArray(data)) {
      cities = data;
    } else if (Array.isArray(data.suggestions)) {
      cities = data.suggestions;
    } else if (data.cities && Array.isArray(data.cities)) {
      cities = data.cities;
    } else if (data.results && Array.isArray(data.results)) {
      cities = data.results;
    }

    // Convert to dropdown options format
    const mapped = cities.map((city) => {
      if (typeof city === "string") {
        return { value: city, label: city };
      }
      if (city && typeof city === "object") {
        return {
          value: city.value || city.name || city.city || city,
          label: city.label || city.name || city.city || city.value || city,
        };
      }
      return { value: city, label: String(city) };
    });
    return dedupeLocationSelectOptions(mapped);
  } catch (error) {
    console.error("Error fetching cities by states:", error);
    return [];
  }
}

/**
 * Fetch competitor matrix data
 * @param {string|number} apexId - Apex ID of the tender
 * @returns {Promise<Object|Array>} Competitor matrix data
 */
export async function fetchCompetitorMatrix(apexId) {
  if (!apexId) {
    console.warn("No apex ID provided for competitor matrix");
    return null;
  }

  try {
    const apexIdString = String(apexId).trim();
    if (!apexIdString) {
      console.warn("Empty apex ID provided for competitor matrix");
      return null;
    }

    console.log("Calling competitor matrix API:", {
      url: `/statistics/competitor-matrix/${apexIdString}`
    });

    // GET request: /statistics/competitor-matrix/{apexId}
    const response = await http.get(
      `/statistics/competitor-matrix/${encodeURIComponent(apexIdString)}`
    );

    console.log("Competitor matrix API response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching competitor matrix:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    } else if (error.request) {
      console.error("No response received. Request:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    return null;
  }
}

/**
 * Fetch Tender 360 competition intelligence data for a tender detail sidebar.
 * Returns display-ready KPI values when enough historical data exists.
 * @param {string|number} apexId - Apex ID of the tender
 * @param {{ companyName?: string }} options
 * @returns {Promise<Object|null>} Competition intelligence data
 */
export async function fetchCompetitionIntelligence(apexId, options = {}) {
  if (!apexId) {
    console.warn("No apex ID provided for competition intelligence");
    return null;
  }

  try {
    const apexIdString = String(apexId).trim();
    if (!apexIdString) {
      console.warn("Empty apex ID provided for competition intelligence");
      return null;
    }

    const companyName = String(options.companyName || "").trim();
    const params = companyName ? { company_name: companyName } : undefined;
    const response = await http.get(
      `/statistics/competition-intelligence/${encodeURIComponent(apexIdString)}`,
      params ? { params } : undefined
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching competition intelligence:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return null;
  }
}

/**
 * Fetch the paginated bidder-scoped Department Insights table.
 * Request supports bidder_name, subcategory, product, keyword, department,
 * ownership, state, city, tender-value range, date range, and pagination.
 * Response: { total, page, limit, data[] }
 */
export async function fetchTender360DepartmentInsights(payload = {}) {
  const page = Math.max(1, Number(payload.page) || 1);
  const limit = Math.min(100, Math.max(1, Number(payload.limit) || 10));
  const body = {
    ...buildTender360StoredProcedurePayload(payload),
    page,
    limit,
  };
  const response = await cachedTender360Post("/get-360-department-insights", body);
  const data = response?.data || {};
  const rows = Array.isArray(data?.data) ? data.data : [];

  return {
    bidderName: String(body.bidder_name || "").trim(),
    total: Math.max(rows.length, Number(data?.total) || 0),
    page: Math.max(1, Number(data?.page) || page),
    limit: Math.min(100, Math.max(1, Number(data?.limit) || limit)),
    rows: rows.map((item, index) => ({
      id: `${page}-${index}-${item?.department_name || "department"}`,
      departmentName: String(item?.department_name || "").trim(),
      publishedTender: Number(item?.published_tender) || 0,
      participatedTender:
        Number(item?.participated_tender ?? item?.published_tender) || 0,
      awardedTender: Number(item?.awarded_tender) || 0,
      awardedValue: Number(item?.awarded_value) || 0,
      whoWonMax: item?.who_won_max || "N/A",
    })),
  };
}

/**
 * Fetch Department Insights KPI values independently of table pagination.
 * Request supports the same filters as the Department Insights table.
 * Response: { summary: { total_department, awarded_value,
 *   most_competitive_department } }
 */
export async function fetchTender360DepartmentInsightsKpi(payload = {}) {
  const body = buildTender360StoredProcedurePayload(payload);
  const response = await cachedTender360Post(
    "/get-360-department-insights-kpi",
    body
  );
  const data = response?.data || {};
  const summary = data?.summary || {};

  return {
    totalDepartments: Number(summary?.total_department) || 0,
    awardedValue: Number(summary?.awarded_value) || 0,
    mostCompetitiveDepartment:
      summary?.most_competitive_department || "N/A",
  };
}

/**
 * Fetch bidder-scoped state insights from the Tender 360 stored-procedure API.
 * Request supports bidder_name, subcategory, product, keyword, department,
 * ownership, state, city, tender-value range, date range, and pagination.
 * Response: { total, page, limit, data[] }
 */
export async function fetchTender360StateInsights(payload = {}) {
  const page = Math.max(1, Number(payload.page) || 1);
  const limit = Math.min(100, Math.max(1, Number(payload.limit) || 10));
  const body = {
    ...buildTender360StoredProcedurePayload(payload),
    page,
    limit,
  };
  const response = await cachedTender360Post("/get-360-state-insights", body);
  const data = response?.data || {};
  const rows = Array.isArray(data?.data) ? data.data : [];

  return {
    bidderName: String(data?.bidder_name || body.bidder_name || "").trim(),
    total: Math.max(rows.length, Number(data?.total) || 0),
    page: Math.max(1, Number(data?.page) || page),
    limit: Math.min(100, Math.max(1, Number(data?.limit) || limit)),
    rows: rows.map((item, index) => ({
      id: `${page}-${index}-${item?.state_name || "state"}`,
      stateName: String(item?.state_name || "").trim(),
      publishedTender: Number(item?.published_tender) || 0,
      participatedTender:
        Number(item?.participated_tender ?? item?.published_tender) || 0,
      awardedTender: Number(item?.awarded_tender) || 0,
      awardedValue: Number(item?.awarded_value) || 0,
      whoWonMax: item?.who_won_max || "N/A",
    })),
  };
}

/**
 * Fetch bidder-scoped State Statistics KPI values.
 * Request supports the same filters as the State Statistics table.
 * Response: {
 *   summary: { total_state, awarded_value, most_competitive_state }
 * }
 */
export async function fetchTender360StateInsightsKpi(payload = {}) {
  const body = buildTender360StoredProcedurePayload(payload);
  const response = await cachedTender360Post("/get-360-state-insights-kpi", body);
  const data = response?.data || {};
  const summary =
    data?.summary && typeof data.summary === "object" ? data.summary : data;

  return {
    bidderName: String(data?.bidder_name || body.bidder_name || "").trim(),
    summary: {
      totalStates:
        Number(summary?.total_state ?? summary?.total_states) || 0,
      awardedValue: Number(summary?.awarded_value) || 0,
      mostCompetitiveState:
        summary?.most_competitive_state ||
        summary?.mostCompetitiveState ||
        "N/A",
    },
  };
}

/**
 * Fetch Tender 360 Insights for dashboard
 *
 * Endpoint: POST /tender-360-insights
 * Payload:
 * {
 *  tender_categories?: string[],
 *  year?: number,
 *  month_from?: "YYYY-MM-DD",
 *  month_to?: "YYYY-MM-DD",
 *  tender_value_min?: number,
 *  tender_value_max?: number,
 *  state?: string[],
 *  city?: string[],
 *  ownership?: string[],
 *  department?: string[],
 *  company_name?: string
 * }
 */

/**
 * Fetch "My All Competitors" for dashboard-main (Tender 360).
 * POST /my-all-competitors.
 * Preferred payload shape:
 * {
 *   bidder_name: string,
 *   tender_subcategory: string[],
 *   product: string[],
 *   keywords: string[],
 *   page: number,
 *   limit: number
 * }
 * Also supports legacy signature: (bidderName, categories, page, limit).
 */
export async function fetchMyAllCompetitors(arg1, categories = [], page = 1, limit = 50) {
  const normalizeString = (v) => (v == null ? "" : String(v)).trim();
  const normalizeToArray = (v) => {
    if (v == null) return [];
    if (Array.isArray(v)) return v.map((x) => normalizeString(x)).filter(Boolean);
    const s = normalizeString(v);
    return s ? [s] : [];
  };

  let body;
  if (arg1 && typeof arg1 === "object" && !Array.isArray(arg1)) {
    body = {
      bidder_name:
        normalizeString(arg1.bidder_name || arg1.company_name || arg1.companyName) || undefined,
      subcategory: normalizeToArray(arg1.subcategory || arg1.tender_subcategory || arg1.subCategory),
      product: normalizeToArray(arg1.product),
      keyword: normalizeToArray(arg1.keyword || arg1.keywords),
      page: Number(arg1.page) || 1,
      limit: Number(arg1.limit) || 50,
    };
    const zoneList = normalizeToArray(arg1.zone ?? arg1.zones);
    if (zoneList.length > 0) body.zone = zoneList;
    const stateList = normalizeToArray(arg1.state);
    if (stateList.length > 0) body.state = stateList;
    const cityList = normalizeToArray(arg1.city);
    if (cityList.length > 0) body.city = cityList;
    const ownershipList = normalizeToArray(arg1.ownership);
    if (ownershipList.length > 0) body.ownership = ownershipList;
    const departmentList = normalizeToArray(arg1.department);
    if (departmentList.length > 0) body.department = departmentList;
    const tenderValueMin = arg1.tender_value_min ?? arg1.tenderValueMin;
    if (tenderValueMin != null && tenderValueMin !== "" && Number.isFinite(Number(tenderValueMin))) {
      body.tender_value_min = Number(tenderValueMin);
    }
    const tenderValueMax = arg1.tender_value_max ?? arg1.tenderValueMax;
    if (tenderValueMax != null && tenderValueMax !== "" && Number.isFinite(Number(tenderValueMax))) {
      body.tender_value_max = Number(tenderValueMax);
    }
    const dateFrom = normalizeString(arg1.date_from || arg1.month_from || arg1.monthFrom);
    if (dateFrom) body.date_from = dateFrom;
    const dateTo = normalizeString(arg1.date_to || arg1.month_to || arg1.monthTo);
    if (dateTo) body.date_to = dateTo;
    if (arg1?.[TENDER360_FORCE_REFRESH_KEY] === true) {
      body[TENDER360_FORCE_REFRESH_KEY] = true;
    }
  } else {
    // Legacy compatibility
    body = {
      subcategory: normalizeToArray(categories),
      product: [],
      keyword: [],
      page: Number(page) || 1,
      limit: Number(limit) || 50,
    };
  }

  Object.keys(body).forEach((key) => {
    const value = body[key];
    if (value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete body[key];
    }
  });

  // Superseded by /get-360-competitors-list for Tender-360 dashboard binding.
  // const response = await http.post("/my-all-competitors", body);
  const response = await cachedTender360Post("/get-360-competitors-list", body);
  const data = response?.data;
  return {
    total: data?.total ?? 0,
    page: Number(data?.page ?? body.page) || 1,
    limit: Number(data?.limit ?? body.limit) || 50,
    competitors: Array.isArray(data?.competitors) ? data.competitors : [],
  };
}

function buildTender360StoredProcedurePayload(payload = {}) {
  const normalizeString = (v) => (v == null ? "" : String(v)).trim();
  const normalizeToStringArray = (v) => {
    if (v == null) return [];
    if (Array.isArray(v)) {
      return v
        .map((x) => normalizeString(x))
        .filter((x) => x.length > 0);
    }
    const single = normalizeString(v);
    return single ? [single] : [];
  };

  const numberOrUndefined = (value) => {
    if (value == null || value === "") return undefined;
    const numericValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numericValue) && numericValue >= 0
      ? numericValue
      : undefined;
  };

  const body = {
    bidder_name:
      normalizeString(payload.bidder_name || payload.company_name || payload.companyName) || undefined,
    comparison_bidder_name:
      normalizeString(
        payload.comparison_bidder_name ||
          payload.comparisonBidderName ||
          payload.bidder2_name,
      ) || undefined,
    excluded_bidder_name:
      normalizeString(
        payload.excluded_bidder_name ||
          payload.excludedBidderName ||
          payload.excluded_company_name,
      ) || undefined,
    subcategory: normalizeToStringArray(payload.subcategory || payload.tender_subcategory || payload.subCategory),
    product: normalizeToStringArray(payload.product),
    keyword: normalizeToStringArray(payload.keyword || payload.keywords),
    department: normalizeToStringArray(payload.department),
    ownership: normalizeToStringArray(payload.ownership),
    zone: normalizeToStringArray(payload.zone || payload.zones),
    state: normalizeToStringArray(payload.state),
    city: normalizeToStringArray(payload.city),
    tender_value_min: numberOrUndefined(payload.tender_value_min ?? payload.tenderValueMin),
    tender_value_max: numberOrUndefined(payload.tender_value_max ?? payload.tenderValueMax),
    date_from: normalizeString(payload.date_from || payload.month_from || payload.monthFrom) || undefined,
    date_to: normalizeString(payload.date_to || payload.month_to || payload.monthTo) || undefined,
  };

  Object.keys(body).forEach((key) => {
    const value = body[key];
    if (value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete body[key];
    }
  });

  if (payload?.[TENDER360_FORCE_REFRESH_KEY] === true) {
    body[TENDER360_FORCE_REFRESH_KEY] = true;
  }

  return body;
}

function extractTender360Array(source, keys = []) {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key];
  }

  const nestedKeys = [
    "data",
    "result",
    "results",
    "rows",
    "items",
    "list",
    "payload",
    "response",
  ];

  for (const key of nestedKeys) {
    if (source[key] && source[key] !== source) {
      const nested = extractTender360Array(source[key], keys);
      if (nested.length > 0) return nested;
    }
  }

  for (const value of Object.values(source)) {
    if (Array.isArray(value)) return value;
  }

  return [];
}

function normalizeTender360ProbableCompetitor(item, idx) {
  return {
    ...item,
    bidder_name:
      item?.bidder_name ??
      item?.bidderName ??
      item?.name ??
      item?.company_name ??
      item?.companyName ??
      "",
    tenderwinner_count:
      item?.tenderwinner_count ??
      item?.tenderwinnerCount ??
      item?.awarded_tenders ??
      item?.awardedTenders ??
      item?.tenders ??
      item?.tender_count ??
      item?.count ??
      0,
    rank: Number(item?.rank ?? item?.position ?? idx + 1) || idx + 1,
  };
}

function tender360NumberFrom(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    if (typeof value === "object" && !Array.isArray(value)) {
      const objectCountKeys = [
        "count",
        "total",
        "total_count",
        "totalCount",
        "tenders",
        "tender_count",
        "tenderCount",
      ];
      for (const key of objectCountKeys) {
        if (!Object.prototype.hasOwnProperty.call(value, key)) continue;
        const n = Number(String(value[key]).replace(/,/g, ""));
        if (Number.isFinite(n)) return n;
      }
      continue;
    }
    const n = Number(String(value).replace(/,/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function tender360StringFrom(item, keys, fallback = "N/A") {
  for (const key of keys) {
    const value = item?.[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return fallback;
}

function tender360RawStringFrom(item, keys) {
  for (const key of keys) {
    const value = item?.[key];
    if (value == null) continue;
    const text = String(value);
    if (text.trim()) return text;
  }
  return "";
}

function normalizeTender360DimensionRow(item, nameKeys, outputNameKey) {
  const rawDimensionValue = tender360RawStringFrom(item, nameKeys);
  const participated = tender360NumberFrom(
    item?.participated,
    item?.participated_tenders,
    item?.participatedTenders,
    item?.participated_count,
    item?.participatedCount,
    item?.participated_tender_count,
    item?.participatedTenderCount,
    item?.total_participated,
    item?.totalParticipated,
    item?.participation_count,
    item?.participationCount,
  );
  const won = tender360NumberFrom(
    item?.won,
    item?.won_count,
    item?.wonCount,
    item?.awarded,
    item?.awarded_tenders,
    item?.awardedTenders,
    item?.awarded_count,
    item?.awardedCount,
    item?.awarded_tender_count,
    item?.awardedTenderCount,
    item?.tenderwinner_count,
    item?.tenderwinnerCount,
    item?.winner_count,
    item?.winnerCount,
  );
  const technical = tender360NumberFrom(
    item?.technical,
    item?.technical_count,
    item?.technicalCount,
    item?.technical_tenders,
    item?.technicalTenders,
  );
  const financial = tender360NumberFrom(
    item?.financial,
    item?.financial_count,
    item?.financialCount,
    item?.financial_tenders,
    item?.financialTenders,
  );
  return {
    ...item,
    [outputNameKey]:
      rawDimensionValue.trim() || tender360StringFrom(item, nameKeys),
    dashboard_dimension_value: rawDimensionValue,
    published: tender360NumberFrom(
      item?.published,
      item?.published_tenders,
      item?.publishedTenders,
      item?.published_count,
      item?.publishedCount,
      item?.published_tender_count,
      item?.publishedTenderCount,
      item?.total_published,
      item?.totalPublished,
      item?.total_tenders,
      item?.totalTenders,
      item?.tender_count,
      item?.tenderCount,
      item?.count,
      item?.total,
    ),
    participated,
    technical,
    financial,
    awarded: won,
    aoc: won,
    won,
    winning_ratio: tender360NumberFrom(
      item?.winning_ratio,
      item?.winningRatio,
      item?.win_rate,
      item?.winRate,
    ),
  };
}

function normalizeTender360OwnershipRow(item) {
  return normalizeTender360DimensionRow(
    item,
    [
      "ownership",
      "ownership_name",
      "ownershipName",
      "ownership_type",
      "ownershipType",
      "owner_type",
      "ownerType",
      "tender_ownership",
      "tenderOwnership",
      "name",
      "label",
    ],
    "ownership",
  );
}

function normalizeTender360DepartmentRow(item) {
  return normalizeTender360DimensionRow(
    item,
    [
      "department",
      "department_name",
      "departmentName",
      "dept_name",
      "deptName",
      "authority",
      "authority_name",
      "authorityName",
      "organization_name",
      "organizationName",
      "organisation_name",
      "organisationName",
      "name",
      "label",
    ],
    "department",
  );
}

export async function fetchTender360DashboardDimension(section, payload = {}) {
  const dimensions = {
    probableCompetitors: {
      endpoint: "/get-360-probable-competitors",
      responseKey: "probable_competitors",
      normalize: (rows) => rows.map(normalizeTender360ProbableCompetitor),
    },
    stateWise: {
      endpoint: "/get-360-state-wise-tenders",
      responseKey: "state_wise_tenders",
      normalize: (rows) =>
        rows.map((item) => {
          const rawDimensionValue = String(
            item?.state ?? item?.state_name ?? "",
          );
          return {
            ...item,
            state: rawDimensionValue,
            dashboard_dimension_value: rawDimensionValue,
          };
        }),
    },
    ownership: {
      endpoint: "/get-360-tender-ownership",
      responseKey: "tender_ownership",
      normalize: (rows) => rows.map(normalizeTender360OwnershipRow),
    },
    department: {
      endpoint: "/get-360-department-wise",
      responseKey: "department_wise",
      normalize: (rows) => rows.map(normalizeTender360DepartmentRow),
    },
  };
  const dimension = dimensions[section];
  if (!dimension) {
    throw new Error(`Unsupported Tender 360 dashboard section: ${section}`);
  }

  const page = Math.max(1, Number(payload.page) || 1);
  const limit = Math.max(1, Number(payload.limit) || 10);
  const response = await cachedTender360Post(dimension.endpoint, {
    ...buildTender360StoredProcedurePayload(payload),
    page,
    limit,
  });
  const data = response?.data || {};
  const rawRows = extractTender360Array(data, [
    dimension.responseKey,
    "data",
    "rows",
    "result",
    "results",
  ]);
  const resolvedPage = Math.max(1, Number(data.page) || page);
  const resolvedLimit = Math.max(1, Number(data.limit) || limit);
  const rows = dimension.normalize(rawRows).map((item, index) =>
    section === "probableCompetitors"
      ? {
          ...item,
          rank: (resolvedPage - 1) * resolvedLimit + index + 1,
        }
      : item,
  );

  return {
    rows,
    total: Math.max(rawRows.length, Number(data.total) || 0),
    page: resolvedPage,
    limit: resolvedLimit,
  };
}

function buildTender360DashboardDrilldownBody(
  payload = {},
  { includePagination = true } = {}
) {
  const page = Math.max(1, Number(payload.page) || 1);
  const requestedLimit = Number(payload.limit);
  const metric = String(payload.metric || "").trim();
  const dimension = String(payload.dimension || "").trim();
  const rawDimensionValue = String(
    payload.dimension_value ?? payload.dimensionValue ?? ""
  );
  const dimensionValue = rawDimensionValue.trim();
  const sortBy = String(payload.sort_by ?? payload.sortBy ?? "").trim();
  const sortOrder = String(payload.sort_order ?? payload.sortOrder ?? "")
    .trim()
    .toLowerCase();
  const body = {
    ...buildTender360StoredProcedurePayload(payload),
    metric
  };

  if (includePagination) {
    body.page = page;
    if (Number.isInteger(requestedLimit) && requestedLimit > 0) {
      body.limit = requestedLimit;
    }
  }
  if (dimension) body.dimension = dimension;
  if (dimensionValue) body.dimension_value = rawDimensionValue;
  if (sortBy && sortOrder) {
    body.sort_by = sortBy;
    body.sort_order = sortOrder;
  }

  return body;
}

export async function fetchTender360DashboardDrilldown(payload = {}) {
  const page = Math.max(1, Number(payload.page) || 1);
  const requestedLimit = Number(payload.limit);
  const body = buildTender360DashboardDrilldownBody(payload);

  const response = await cachedTender360Post("/get-360-dashboard-drilldown", body);
  const data = response?.data || {};

  return {
    total: Number(data.total) || 0,
    page: Math.max(1, Number(data.page) || page),
    limit: Math.max(1, Number(data.limit) || requestedLimit || 1),
    tenders: Array.isArray(data.tenders) ? data.tenders : []
  };
}

export async function exportTender360DashboardDrilldown(payload = {}) {
  const body = buildTender360DashboardDrilldownBody(payload, {
    includePagination: false
  });

  try {
    const response = await http.post(
      "/get-360-dashboard-drilldown/export",
      body,
      {
        responseType: "blob",
        timeout: 300000
      }
    );
    return response?.data;
  } catch (error) {
    const responseData = error?.response?.data;
    if (
      typeof Blob !== "undefined" &&
      responseData instanceof Blob &&
      String(responseData.type || "").includes("json")
    ) {
      try {
        const parsed = JSON.parse(await responseData.text());
        error.response.data = parsed;
      } catch (_parseError) {
        // Preserve the original Axios error when the response is not JSON.
      }
    }
    throw error;
  }
}

async function fetchTender360DashboardBundle(payload = {}, options = {}) {
  const body = buildTender360StoredProcedurePayload(payload);
  const onPartial = typeof options.onPartial === "function" ? options.onPartial : null;
  // This endpoint is exclusive to My Participation. Keep it opt-in so an
  // unflagged dashboard bundle call can never add the expensive aggregation
  // to Tender 360 Insights.
  const includeProbableCompetitors = options.includeProbableCompetitors === true;
  const notifyPartial = (section, data) => {
    if (onPartial) onPartial({ section, data: data || {} });
  };
  const page = Math.max(1, Number(payload.page) || 1);
  const limit = Math.max(1, Number(payload.limit) || 10);
  const buildDimensionBody = (pageKey, limitKey) => ({
    ...body,
    page: Math.max(1, Number(payload[pageKey] ?? page) || 1),
    limit: Math.max(1, Number(payload[limitKey] ?? limit) || 10),
  });
  const stateWiseBody = buildDimensionBody("stateWisePage", "stateWiseLimit");
  const ownershipBody = buildDimensionBody("ownershipPage", "ownershipLimit");
  const departmentBody = buildDimensionBody("departmentPage", "departmentLimit");
  const probableCompetitorsBody = buildDimensionBody(
    "probableCompetitorsPage",
    "probableCompetitorsLimit",
  );

  const [
    kpiResponse,
    participatedResponse,
    probableCompetitorsResponse,
    stateWiseResponse,
    ownershipResponse,
    departmentWiseResponse,
  ] = await Promise.all([
    cachedTender360Post("/get-360-dashboard-kpi", body).then((response) => {
      notifyPartial("kpi", response?.data);
      return response;
    }),
    cachedTender360Post("/get-360-participated-count-participated-value", body).then((response) => {
      notifyPartial("participated", response?.data);
      return response;
    }),
    includeProbableCompetitors
      ? cachedTender360Post("/get-360-probable-competitors", probableCompetitorsBody).then((response) => {
          notifyPartial("probableCompetitors", response?.data);
          return response;
        })
      : Promise.resolve({
          data: {
            total: 0,
            page: probableCompetitorsBody.page,
            limit: probableCompetitorsBody.limit,
            probable_competitors: [],
          },
        }),
    cachedTender360Post("/get-360-state-wise-tenders", stateWiseBody).then((response) => {
      notifyPartial("stateWise", response?.data);
      return response;
    }),
    cachedTender360Post("/get-360-tender-ownership", ownershipBody).then((response) => {
      notifyPartial("ownership", response?.data);
      return response;
    }),
    cachedTender360Post("/get-360-department-wise", departmentBody).then((response) => {
      notifyPartial("department", response?.data);
      return response;
    }),
  ]);

  const kpi = kpiResponse?.data || {};
  const participated = participatedResponse?.data || {};
  const probableCompetitorsResponseData = probableCompetitorsResponse?.data || {};
  const stateWise = stateWiseResponse?.data || {};
  const ownership = ownershipResponse?.data || {};
  const departmentWise = departmentWiseResponse?.data || {};
  const probableCompetitors = extractTender360Array(probableCompetitorsResponseData, [
    "probable_competitors",
    "competitors",
    "data",
    "rows",
    "result",
    "results",
  ]);
  const ownershipRows = extractTender360Array(ownership, [
    "tender_ownership",
    "tenderOwnership",
    "ownership",
    "ownerships",
    "ownership_wise",
    "ownershipWise",
    "data",
    "rows",
    "result",
    "results",
  ]);
  const stateWiseRows = Array.isArray(stateWise.state_wise_tenders)
    ? stateWise.state_wise_tenders
    : [];
  const departmentRows = extractTender360Array(departmentWise, [
    "department_wise",
    "departmentWise",
    "departmentwise",
    "departments",
    "department",
    "department_rows",
    "departmentRows",
    "data",
    "rows",
    "result",
    "results",
  ]);

  return {
    participated_tenders: participated.participated_tenders || { count: 0, value: 0 },
    awarded_tenders: kpi.awarded_tenders || { count: 0, value: 0 },
    technical: kpi.technical || { count: 0, value: 0 },
    financial: kpi.financial || { count: 0, value: 0 },
    l1: kpi.l1 || { count: 0, value: 0 },
    lost: kpi.lost || { count: 0, value: 0 },
    pending_result: kpi.pending_result || { count: 0, value: 0 },
    winning_ratio: Number(kpi.winning_ratio) || 0,
    winning_ratio_status: kpi.winning_ratio_status || "",
    winning_ratio_suggestion: kpi.winning_ratio_suggestion || "",
    winning_ratio_show_cta: kpi.winning_ratio_show_cta === true,
    probable_competitors: probableCompetitors.map(normalizeTender360ProbableCompetitor),
    probable_competitors_total:
      Number(probableCompetitorsResponseData.total ?? probableCompetitors.length) || 0,
    probable_competitors_page:
      Number(probableCompetitorsResponseData.page ?? probableCompetitorsBody.page) || 1,
    probable_competitors_limit:
      Number(probableCompetitorsResponseData.limit ?? probableCompetitorsBody.limit) || 10,
    state_wise_tenders: stateWiseRows.map((item) => ({
      ...item,
      state: item?.state ?? item?.state_name ?? "",
    })),
    state_wise_tenders_total: Number(stateWise.total ?? stateWiseRows.length) || 0,
    state_wise_tenders_page: Number(stateWise.page ?? stateWiseBody.page) || 1,
    state_wise_tenders_limit: Number(stateWise.limit ?? stateWiseBody.limit) || 10,
    tender_ownership: ownershipRows.map(normalizeTender360OwnershipRow),
    tender_ownership_total: Number(ownership.total ?? ownershipRows.length) || 0,
    tender_ownership_page: Number(ownership.page ?? ownershipBody.page) || 1,
    tender_ownership_limit: Number(ownership.limit ?? ownershipBody.limit) || 10,
    department_wise: departmentRows.map(normalizeTender360DepartmentRow),
    department_wise_total: Number(departmentWise.total ?? departmentRows.length) || 0,
    department_wise_page: Number(departmentWise.page ?? departmentBody.page) || 1,
    department_wise_limit: Number(departmentWise.limit ?? departmentBody.limit) || 10,
  };
}

export async function fetchTender360Insights(payload = {}, options = {}) {
  try {
    // Superseded by split /get-360-* endpoints for Tender-360 dashboard binding.
    // const response = await http.post("/tender-360-insights", body);
    // Probable Competitors is intentionally not rendered on this tab. Avoid
    // running its full market aggregation during the dashboard's critical
    // load path; My Participation continues to request and display it.
    return await fetchTender360DashboardBundle(payload, {
      ...options,
      includeProbableCompetitors: false,
    });
  } catch (error) {
    console.error("Error fetching tender 360 insights:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
}

/**
 * Fetch My Participation data for dashboard
 * Uses the same endpoint as fetchTender360Insights but includes company_name
 * @param {Object} payload - Search parameters
 * @param {Array<string>} payload.tender_categories - Array of tender categories
 * @param {number} payload.year - Optional year
 * @param {string} payload.month_from - Optional month from (YYYY-MM-DD)
 * @param {string} payload.month_to - Optional month to (YYYY-MM-DD)
 * @param {number} payload.tender_value_min - Optional minimum tender value
 * @param {number} payload.tender_value_max - Optional maximum tender value
 * @param {Array<string>} payload.state - Optional array of state names
 * @param {Array<string>} payload.city - Optional array of city names
 * @param {Array<string>} payload.ownership - Optional array of ownership types
 * @param {Array<string>} payload.department - Optional array of department names
 * @param {string} payload.company_name - Company name (required for participation)
 * @returns {Promise<Object>} Participation data object
 */
export async function fetchMyParticipation(payload = {}, options = {}) {
  try {
    // Superseded by split /get-360-* endpoints for Tender-360 dashboard binding.
    // const response = await http.post("/tender-360-insights", body);
    return await fetchTender360DashboardBundle(payload, {
      ...options,
      includeProbableCompetitors: true,
    });
  } catch (error) {
    console.error("Error fetching my participation:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
}

/**
 * Fetch L1-L2 rate analysis data
 * @param {Object} payload - Search parameters
 * @param {string} payload.keyword - Optional keyword to search
 * @param {Array<string>} payload.state - Optional array of state names
 * @param {Array<string>} payload.department - Optional array of department names
 * @param {number} payload.year - Optional year
 * @returns {Promise<Array>} Array of L1-L2 rate analysis objects
 */
export async function fetchL1L2RateAnalysis(payload = {}) {
  try {
    const body = {
      keyword: payload.keyword && payload.keyword.trim() !== "" ? payload.keyword.trim() : undefined,
      state: Array.isArray(payload.state) && payload.state.length > 0 ? payload.state : undefined,
      department: Array.isArray(payload.department) && payload.department.length > 0 ? payload.department : undefined,
      year: typeof payload.year === "number" && payload.year > 0 ? payload.year : undefined,
    };

    // Remove undefined fields
    Object.keys(body).forEach(key => body[key] === undefined && delete body[key]);

    const response = await http.post("/l1-l2-rate-analysis", body);

    // Handle response structure: { data: [...], total: number } or direct array
    const rawData = response?.data;

    if (Array.isArray(rawData)) {
      return rawData;
    }
    if (Array.isArray(rawData?.data)) {
      return rawData.data;
    }
    if (Array.isArray(rawData?.results)) {
      return rawData.results;
    }

    return [];
  } catch (error) {
    console.error("Error fetching L1-L2 rate analysis:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return [];
  }
}

// Submit tender bidding support form
export async function submitTenderBiddingSupport(formData) {
  try {
    const payload = {
      apexid: formData.apexid || 0,
      companyname: formData.companyname || "",
      contactname: formData.contactname || "",
      mobileno: formData.mobileno || "",
      email: formData.email || "",
      servicefor: formData.servicefor || "",
      remarks: formData.remarks || "",
      otp: formData.otp || ""
    };

    console.log("Submitting tender bidding support:", payload);
    const response = await http.post("/tender-bids/", payload);

    console.log("Tender bidding support submission response:", response.data);

    if (response?.data) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error("Error submitting tender bidding support:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    throw error;
  }
}

/**
 * Fetch available subscription plans
 * @returns {Promise<Array>} Array of available plans
 */
export async function fetchAvailablePlans() {
  try {
    const response = await http.get("/user-plans/available-plans/");
    const rawData = response?.data;

    if (Array.isArray(rawData?.available_plans)) {
      return rawData.available_plans;
    }
    if (Array.isArray(rawData?.plans)) {
      return rawData.plans;
    }
    if (Array.isArray(rawData?.data)) {
      return rawData.data;
    }
    if (Array.isArray(rawData)) {
      return rawData;
    }
    // Backend may return object with numeric keys { 0: {...}, 1: {...} }
    if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
      const arr = Object.values(rawData);
      if (arr.length > 0 && arr.every((item) => item && typeof item === "object")) {
        return arr;
      }
    }

    return [];
  } catch (error) {
    console.error("Error fetching available plans:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
    }
    return [];
  }
}

/**
 * Whether the user already has (or purchased) the Freemium plan.
 * GET /user-plans/has-freemium/?user_id={userId}
 * @param {number|string} userId
 * @returns {Promise<boolean>}
 */
export async function fetchUserHasFreemium(userId) {
  try {
    const uid = userId != null ? Number(userId) : null;
    if (uid == null || uid <= 0 || Number.isNaN(uid)) {
      return false;
    }
    const response = await http.get("/user-plans/has-freemium/", {
      params: { user_id: uid },
    });
    const raw = response?.data ?? response;
    const v = raw?.has_freemium;
    return v === 1 || v === true || v === "1";
  } catch (error) {
    console.error("Error checking has-freemium:", error);
    return false;
  }
}

/**
 * Normalize GET /utils/user-plan-id-for-activation-form/ body (array, { user_plans }, etc.)
 * @param {*} data
 * @returns {Array}
 */
export function normalizeUserPlansFromActivationFormResponse(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== "object") return [];
  if (Array.isArray(data.user_plans)) return data.user_plans;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

/**
 * Fetch user plan IDs for activation form
 * GET /utils/user-plan-id-for-activation-form/?user_id={userId}
 * @param {number|string} userId - User ID
 * @returns {Promise<{user_plans: Array<{upid: number, pid: number, plan_name: string}>}>} Response with user_plans array
 */
export async function fetchDashboardUserStatus(userId) {
  const uid = userId != null ? Number(userId) : null;
  if (uid == null || Number.isNaN(uid) || uid <= 0) {
    return { status: "no_payment", message: "No payment found." };
  }
  try {
    const response = await http.get("/dashboard/user-status/", {
      params: { user_id: uid },
    });
    return response?.data ?? response ?? {};
  } catch (error) {
    console.error("Error fetching dashboard user status:", error);
    return { status: "unknown", message: "Unable to load dashboard status." };
  }
}
export async function fetchUserPlanIdForActivationForm(userId) {
  try {
    const uid = userId != null ? Number(userId) : null;
    if (uid == null || uid <= 0) {
      return { user_plans: [] };
    }
    const response = await http.get("/utils/user-plan-id-for-activation-form/", {
      params: { user_id: uid },
    });
    const data = response?.data ?? response;
    return {
      user_plans: normalizeUserPlansFromActivationFormResponse(data),
    };
  } catch (error) {
    console.error("Error fetching user plan IDs for activation form:", error);
    return { user_plans: [] };
  }
}

/**
 * Get user plan details by plan id (upid)
 * GET /user-plans/by-plan-id/{upid}
 * @param {number|string} upid - User plan ID
 * @returns {Promise<Object>} Plan details (upid, uid, pid, startdate, enddate, selectedcategories, etc.)
 */
/**
 * True when user has plan rows from activation-form API but no active plan details yet
 * (activation questionnaire not filed / not reflected in user-active-plan-details).
 * @param {string|number} userId
 * @returns {Promise<boolean>}
 */
export async function userMustCompleteActivationBeforeDashboard(userId) {
  const uid = userId != null ? Number(userId) : null;
  if (uid == null || Number.isNaN(uid) || uid <= 0) return false;
  try {
    const [activeRaw, activationRes] = await Promise.all([
      fetchUserActivePlanDetails(uid).catch(() => ({})),
      fetchUserPlanIdForActivationForm(uid),
    ]);
    const activePlans = Array.isArray(activeRaw) ? activeRaw : (activeRaw?.plans ?? []);
    const activationPlans = Array.isArray(activationRes?.user_plans) ? activationRes.user_plans : [];
    return activationPlans.length > 0 && activePlans.length === 0;
  } catch {
    return false;
  }
}

export async function fetchUserPlanByPlanId(upid) {
  try {
    const id = upid != null ? Number(upid) : null;
    if (id == null || id <= 0) {
      return null;
    }
    const response = await http.get(`/user-plans/by-plan-id/${id}`);
    return response?.data ?? response ?? null;
  } catch (error) {
    console.error("Error fetching user plan by plan id:", error);
    return null;
  }
}

const GSTIN_LENGTH = 15;

/**
 * GSTIN lookup for activation form (company name and type)
 * GET /utils/gst-info?gstin={gstin}
 * @param {string} gstNumber - 15-character GSTIN (spaces ignored)
 * @returns {Promise<{ company_name?: string, type?: string }|null>}
 */
export async function fetchGstInfo(gstNumber) {
  const gst = String(gstNumber || "")
    .replace(/\s/g, "")
    .toUpperCase()
    .slice(0, GSTIN_LENGTH);
  if (gst.length !== GSTIN_LENGTH) {
    return null;
  }
  try {
    const response = await http.get("/utils/gst-info", {
      params: { gstin: gst },
    });
    return response?.data ?? null;
  } catch (error) {
    console.error("Error fetching GST info:", error);
    return null;
  }
}

/**
 * Get user plans summary (for pre-filling upgrade plan inquiry, etc.)
 * GET /user-plans/summary/{userId}
 * @param {number|string} userId - User ID
 * @returns {Promise<Object|null>} { userid, companyname, contactperson, mobile, email, existingplan: string[] } or null
 */
export async function fetchUserPlansSummary(userId) {
  try {
    const id = userId != null ? Number(userId) : null;
    if (id == null || id <= 0) {
      return null;
    }
    const response = await http.get(`/user-plans/summary/${id}`);
    return response?.data ?? response ?? null;
  } catch (error) {
    console.error("Error fetching user plans summary:", error);
    return null;
  }
}

/**
 * Submit activation form
 * POST /activation-form/
 * @param {Object} payload - Activation form data
 * @param {number} payload.uId - User ID
 * @param {number} payload.upid - User plan ID (from user-plans/by-plan-id)
 * @param {string} payload.gstno - GST number
 * @param {string} payload.companyname - Company name
 * @param {string} payload.companytype - Company type
 * @param {string} payload.contactperson - Contact person
 * @param {string} payload.designation - Designation
 * @param {string} payload.address - Address
 * @param {string} payload.city - City
 * @param {string[]} payload.selectedstates - Selected states (array of strings)
 * @param {string} payload.state - State (single string, e.g. primary/first selected)
 * @param {string} payload.phone - Phone
 * @param {string} payload.mobile - Mobile
 * @param {string} payload.emailid - Email
 * @param {string} payload.altemailid - Alternate email
 * @param {string} payload.website_url - Website URL
 * @param {string[]} payload.productkeywords - Product keywords
 * @param {string} payload.subscriptionplan - Subscription plan
 * @param {string} payload.serviceduration - Service duration
 * @param {string} payload.amountdetails - Amount details
 * @param {string} payload.gemusername - GeM username (optional)
 * @param {string} payload.gempassword - GeM password (optional)
 * @param {string} payload.remarks - Remarks
 * @returns {Promise<Object>} API response
 */
/**
 * Submit user plans (POST /user-plans/)
 * Call after plan selection / successful payment to save user's subscription plans.
 * @param {Array<{uId: number, pId: number, startdate: string, enddate: string, statetype?: string|string[], selectedstates?: string|string[], selectedcategories?: string|string[], zone?: string|string[], servicecount?: number, activeform?: number, paymentid?: number}>} plans - Array of plan objects; zone is sent as string[] (isactive is not sent)
 * @returns {Promise<Object>} API response
 */
export async function submitUserPlans(plans) {
  try {
    const toStatetypeString = (v) => {
      if (v == null) return "";
      if (typeof v === "string") return v.trim();
      if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean).join(",");
      return String(v).trim();
    };
    const ensureArray = (v) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string" && v.trim()) return v.split(",").map((s) => s.trim()).filter(Boolean);
      return [];
    };
    const ensureZoneArray = (v) => {
      if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
      if (typeof v === "string" && v.trim()) return [v.trim()];
      return [];
    };
    const planList = Array.isArray(plans) ? plans : [plans];
    const payload = {
      plans: planList.map((p) => ({
        uId: p.uId ?? 0,
        pId: p.pId ?? 1,
        startdate: p.startdate || "",
        enddate: p.enddate || "",
        statetype: toStatetypeString(p.statetype ?? p.selectedstates),
        zone: ensureZoneArray(p.zone),
        selectedcategories: ensureArray(p.selectedcategories),
        servicecount: typeof p.servicecount === "number" ? p.servicecount : 0,
        activeform: p.activeform === 1 ? 1 : 0,
        paymentid: typeof p.paymentid === "number" ? p.paymentid : 0,
      })),
    };
    const response = await http.post("/user-plans/", payload);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error submitting user plans:", error);
    if (error.response) {
      throw {
        message: error.response?.data?.message || error.response?.data?.error || "Failed to save plans",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Record a user payment (POST /user-payments/)
 * Call after successful payment to store payment details.
 * @param {Object} payload - Payment record
 * @param {number} payload.uid - User ID
 * @param {number} payload.upid - User plan ID (or plan ID)
 * @param {number} payload.amount - Amount (e.g. in rupees)
 * @param {string} payload.payment_gateway - e.g. "razorpay"
 * @param {string} payload.currency - e.g. "INR"
 * @param {string} payload.payment_status - e.g. "success" or "pending"
 * @param {string} payload.payment_method - e.g. "razorpay"
 * @param {string} [payload.gateway_payment_id] - Gateway payment ID
 * @param {string} [payload.gateway_order_id] - Gateway order ID
 * @param {string} [payload.failure_reason] - Failure reason (empty on success)
 * @param {string} payload.paid_at - ISO date string
 * @returns {Promise<Object>} API response
 */
export async function createUserPayment(payload) {
  try {
    const uid = payload?.uid != null ? Number(payload.uid) : null;
    const upid = payload?.upid != null ? Number(payload.upid) : (payload?.upid === 0 ? 0 : null);
    if (uid == null || uid <= 0) {
      console.warn("createUserPayment: uid must be a positive number", { uid });
      return null;
    }
    if (upid == null || upid < 0) {
      console.warn("createUserPayment: upid must be a non-negative number", { upid });
      return null;
    }
    const body = {
      uid,
      upid: upid || 0,
      amount: payload?.amount != null ? Number(payload.amount) : 0,
      payment_gateway: payload?.payment_gateway ?? "razorpay",
      currency: payload?.currency ?? "INR",
      payment_status: payload?.payment_status ?? "success",
      payment_method: payload?.payment_method ?? "razorpay",
      gateway_payment_id: payload?.gateway_payment_id != null ? String(payload.gateway_payment_id) : "",
      gateway_order_id: payload?.gateway_order_id != null ? String(payload.gateway_order_id) : "",
      failure_reason: payload?.failure_reason != null ? String(payload.failure_reason) : "",
      paid_at: payload?.paid_at ?? new Date().toISOString(),
    };
    if (payload?.coupon_code) {
      body.coupon_code = String(payload.coupon_code).trim();
    }
    if (payload?.coupon_id != null) {
      body.coupon_id = Number(payload.coupon_id);
    }
    if (payload?.subtotal_amount != null) {
      body.subtotal_amount = Number(payload.subtotal_amount);
    }
    if (payload?.discount_amount != null) {
      body.discount_amount = Number(payload.discount_amount);
    }
    if (Array.isArray(payload?.plans)) {
      body.plans = payload.plans
        .map((plan) => ({
          pid: Number(plan?.pid ?? plan?.pId ?? 0),
          amount: Number(plan?.amount || 0),
        }))
        .filter((plan) => plan.pid > 0);
    }

    const response = await http.post("/user-payments/", body);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error recording user payment:", error);
    if (error.response) {
      throw {
        message: error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || "Failed to record payment",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * User-visible message from POST /activation-form/ failure (DRF detail, field errors, message, etc.)
 * @param {import("axios").AxiosError} error
 * @returns {string}
 */
function formatActivationFormApiError(error) {
  const res = error.response;
  const data = res?.data;
  const fallback = "Failed to submit activation form";

  const collectStrings = (v) => {
    if (v == null) return [];
    if (typeof v === "string" && v.trim()) return [v.trim()];
    if (typeof v === "number" || typeof v === "boolean") return [String(v)];
    if (Array.isArray(v)) return v.flatMap(collectStrings);
    if (typeof v === "object") {
      if (typeof v.message === "string" && v.message.trim()) return [v.message.trim()];
      return [JSON.stringify(v)];
    }
    return [];
  };

  const joinSentence = (parts) => parts.filter(Boolean).join(". ");

  if (data == null || data === "") {
    if (res?.status && res.statusText) return `${res.status} ${res.statusText}`.trim();
    return fallback;
  }
  if (typeof data === "string") {
    const t = data.trim();
    return t || fallback;
  }
  if (typeof data !== "object") return fallback;

  const blocks = [];
  for (const key of ["message", "error", "detail"]) {
    const s = joinSentence(collectStrings(data[key]));
    if (s) blocks.push(s);
  }
  if (Array.isArray(data.non_field_errors)) {
    const s = joinSentence(collectStrings(data.non_field_errors));
    if (s) blocks.push(s);
  }

  const skip = new Set(["message", "error", "detail", "non_field_errors", "status", "status_code"]);
  const fieldLines = [];
  for (const [k, v] of Object.entries(data)) {
    if (skip.has(k)) continue;
    const s = joinSentence(collectStrings(v));
    if (s) fieldLines.push(`${k}: ${s}`);
  }

  const out = [...blocks, ...fieldLines].filter(Boolean);
  if (out.length) return out.join(" ");
  return fallback;
}

export async function submitActivationForm(payload) {
  try {
    const response = await http.post("/activation-form/", payload);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error submitting activation form:", error);
    if (error.response) {
      throw {
        message: formatActivationFormApiError(error),
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    const rawMsg = typeof error?.message === "string" ? error.message.trim() : "";
    const netMsg =
      rawMsg && rawMsg !== "Network Error" ? rawMsg : "Network error. Please try again.";
    throw {
      message: netMsg,
      status: 0,
    };
  }
}

/**
 * Submit user tender action (POST /tender-status/action)
 * @param {Object} payload
 * @param {number} payload.userid - User ID
 * @param {number} payload.apexid - Tender Apex ID
 * @param {string} payload.action - e.g. "Preparation", "Submitted", "Won", "Lost"
 * @param {number} [payload.favourite] - 0 or 1; current favourite flag for this tender
 * @returns {Promise<Object>} API response
 */
export async function submitUserTenderAction(payload) {
  try {
    const body = {
      userid: payload.userid != null ? Number(payload.userid) : 0,
      apexid: payload.apexid != null ? Number(payload.apexid) : 0,
      action:
        payload.action != null && payload.action !== ""
          ? String(payload.action)
          : "Preparation",
      favourite:
        payload.favourite !== undefined && payload.favourite !== null
          ? Number(payload.favourite)
          : 0,
    };
    const response = await http.post("/tender-status/action", body);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error submitting user tender action:", error);
    if (error.response) {
      throw {
        message: error.response?.data?.message || error.response?.data?.error || "Failed to save action",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Fetch user tender status (GET /user-tender-get-status)
 * @param {number|string} userid - User ID
 * @returns {Promise<Object>} { total, preparation, won, lost, submitted, shortlisted }
 */
export async function fetchUserTenderStatus(userid) {
  const empty = { total: 0, preparation: 0, won: 0, lost: 0, submitted: 0, shortlisted: 0 };
  try {
    if (userid == null || userid === "") return { ...empty };
    const params = { userid: String(userid) };
    const response = await http.get("/user-tender-get-status", { params });
    const data = response?.data ?? response ?? {};
    return {
      total: Number(data.total) || 0,
      preparation: Number(data.preparation) || 0,
      won: Number(data.won) || 0,
      lost: Number(data.lost) || 0,
      submitted: Number(data.submitted) || 0,
      shortlisted: Number(data.shortlisted) || 0,
    };
  } catch (error) {
    console.error("Error fetching user tender status:", error);
    return { ...empty };
  }
}

/**
 * Fetch tenders by tender-status action (POST /tender-status/tenders)
 * @param {Object} payload
 * @param {number|string} payload.userid - User ID
 * @param {string} payload.action - e.g. "won", "lost", "submitted", "preparation", "shortlisted", "total"
 * @param {number} [payload.page=1]
 * @param {number} [payload.limit=50]
 * @returns {Promise<{data: Array, page: number, limit: number, total: number}>}
 */
export async function fetchTenderStatusTenders(payload = {}) {
  const userid = payload.userid;
  const page = Number(payload.page) || 1;
  const limit = Number(payload.limit) || 50;
  const action =
    payload.action != null && String(payload.action).trim() !== ""
      ? String(payload.action).trim().toLowerCase()
      : "";

  if (userid == null || userid === "") {
    return { data: [], page, limit, total: 0 };
  }

  try {
    const response = await http.post(
      "/tender-status/tenders",
      { action },
      { params: { userid: String(userid), page, limit } },
    );
    const apiData = response?.data ?? {};
    const rows = Array.isArray(apiData?.data) ? apiData.data : [];
    return {
      data: rows,
      page: Number(apiData?.page) || page,
      limit: Number(apiData?.limit) || limit,
      total: Number(apiData?.total) || 0,
    };
  } catch (error) {
    console.error("Error fetching tender-status tenders:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch tender status tenders",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Set tender favourite via POST /tender-status/favourite
 * @param {Object} payload
 * @param {number} payload.userid - User ID
 * @param {number} payload.apexid - Tender Apex ID
 * @param {number} payload.favourite - 0 = not favourite, 1 = favourite
 * @param {string} [payload.action] - optional context (e.g. status); default "" for heart-only toggle
 * @returns {Promise<Object>} API response
 */
export async function setTenderFavourite(payload) {
  try {
    const body = {
      userid: payload.userid != null ? Number(payload.userid) : 0,
      apexid: payload.apexid != null ? Number(payload.apexid) : 0,
      action:
        payload.action != null && String(payload.action).trim() !== ""
          ? String(payload.action)
          : "",
      favourite: payload.favourite !== undefined ? Number(payload.favourite) : 0,
    };
    const response = await http.post("/tender-status/favourite", body);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error setting tender favourite:", error);
    if (error.response) {
      throw {
        message: error.response?.data?.message || error.response?.data?.error || "Failed to update favourite",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Queue selected tender listing email.
 * Backend sends the email asynchronously using the fresh tender email table format.
 * @param {Object} payload
 * @param {string} payload.email - Recipient email address
 * @param {Array<string|number>} payload.apexIds - Selected tender Apex IDs
 * @param {number|string} [payload.userId]
 * @param {string} [payload.name]
 * @returns {Promise<Object>} API response
 */
export async function sendSelectedTenderEmail(payload = {}) {
  try {
    const body = {
      email: String(payload.email || "").trim(),
      name: String(payload.name || "Subscriber").trim() || "Subscriber",
      user_id:
        payload.userId !== undefined && payload.userId !== null
          ? Number(payload.userId)
          : null,
      apex_ids: Array.isArray(payload.apexIds)
        ? payload.apexIds.map((id) => String(id).trim()).filter(Boolean)
        : [],
      ...(payload.emailType ? { email_type: String(payload.emailType).trim() } : {}),
    };
    const response = await http.post("/tenders/selected-email", body);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error queueing selected tender email:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          error.response?.data?.error ||
          "Failed to queue selected tender email",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Fetch client bidding profiles (GET /client-bidding-profile/)
 * @param {number|string} userId
 * @param {number} [page=1]
 * @param {number} [limit=100]
 * @returns {Promise<{data:Array,total:number,page:number,limit:number}>}
 */
export async function fetchClientBiddingProfiles(userId, page = 1, limit = 100) {
  try {
    const uid = userId != null ? Number(userId) : NaN;
    const params = {
      page: Number(page) || 1,
      limit: Number(limit) || 100,
    };
    const response =
      Number.isFinite(uid) && uid > 0
        ? await http.get(`/client-bidding-profile/user/${uid}`)
        : await http.get("/client-bidding-profile/", { params });
    const raw = response?.data ?? response ?? {};
    const list = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object" && raw.id != null
        ? [raw]
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.results)
          ? raw.results
          : Array.isArray(raw?.items)
            ? raw.items
            : [];
    return {
      data: list.map(normalizeClientBiddingProfileFromApi),
      total: Number(raw?.total ?? raw?.count ?? list.length ?? 0) || 0,
      page: Number(raw?.page ?? params.page) || 1,
      limit: Number(raw?.limit ?? params.limit) || params.limit,
    };
  } catch (error) {
    console.error("Error fetching client bidding profiles:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to load bidding profiles",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Submit client bidding profile (POST /client-bidding-profile/?user_id={id})
 * @param {Object} payload - Form data (camelCase or snake_case)
 * @param {number|string} userId
 * @returns {Promise<Object>} API response
 */
export async function submitClientBiddingProfile(payload, userId) {
  try {
    const uid = userId != null ? Number(userId) : NaN;
    if (!Number.isFinite(uid) || uid <= 0) {
      throw { message: "Invalid user id for bidding profile submit", status: 400 };
    }
    const body = normalizeClientBiddingProfileUpdatePayload(payload);
    const response = await http.post(`/client-bidding-profile/?user_id=${uid}`, body);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error submitting client bidding profile:", error);
    if (error.response) {
      throw {
        message: error.response?.data?.message || error.response?.data?.detail || "Failed to save bidding profile",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Update client bidding profile (PUT /client-bidding-profile/{id})
 * @param {number|string} profileId
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export async function updateClientBiddingProfile(profileId, payload) {
  try {
    const id = Number(profileId);
    if (!Number.isFinite(id) || id <= 0) {
      throw { message: "Invalid bidding profile id", status: 400 };
    }
    const body = normalizeClientBiddingProfileUpdatePayload(payload);
    const response = await http.put(`/client-bidding-profile/${id}`, body);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error updating client bidding profile:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to update bidding profile",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: error?.message || "Network error. Please try again.",
      status: error?.status || 0,
    };
  }
}

function normalizeClientBiddingProfileUpdatePayload(payload) {
  const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
  const str = (v) => (v != null ? String(v).trim() : "");
  const registrationValidityRaw = payload.registrationvalidity ?? payload.registrationValidity;
  const registrationValidity =
    registrationValidityRaw != null && String(registrationValidityRaw).trim() !== ""
      ? String(registrationValidityRaw).trim()
      : "";
  const specificLicenseRaw =
    payload.specificlicense ?? payload.specificLicense ?? payload.isocertification;
  const specificLicense = Array.isArray(specificLicenseRaw)
    ? specificLicenseRaw
        .map((item) => (item != null ? String(item).trim() : ""))
        .filter(Boolean)
        .join(", ")
    : str(specificLicenseRaw);
  const firstTimeRaw = payload.firsttimeparticipant ??
    payload.firstTimeParticipant ??
    payload.first_time_participant ??
    0;
  const firstTimeParticipant =
    firstTimeRaw === 1 ||
    firstTimeRaw === true ||
    String(firstTimeRaw).toLowerCase() === "true" ||
    String(firstTimeRaw).toLowerCase() === "yes" ||
    String(firstTimeRaw).toLowerCase() === "y";

  return {
    companyname: str(payload.companyname ?? payload.companyName),
    companytype: str(payload.companytype ?? payload.companyType),
    annualturnover: num(payload.annualturnover ?? payload.annualTurnover),
    highestworkvalue: num(payload.highestworkvalue ?? payload.highestWorkValue ?? payload.similarworkvalue),
    projectscompleted: num(payload.projectscompleted ?? payload.projectsCompleted ?? payload.similarprojects),
    workexperience: str(payload.workexperience ?? payload.workExperience),
    specificlicense: specificLicense,
    registrationvalidity: registrationValidity,
    blacklistingdeclaration: str(payload.blacklistingdeclaration ?? payload.blacklistingDeclaration),
    msmeregistered:
      (payload.msmeregistered ?? payload.msmeRegistered) === "Yes" ||
      (payload.msmeregistered ?? payload.msmeRegistered) === 1
        ? 1
        : 0,
    emdexemption:
      (payload.emdexemption ?? payload.emdExemption) === "Yes" ||
      (payload.emdexemption ?? payload.emdExemption) === 1
        ? 1
        : 0,
    capitallockinimpact: str(payload.capitallockinimpact ?? payload.capitalLockInImpact),
    performancesecurity: str(payload.performancesecurity ?? payload.performanceSecurityBg ?? payload.performancesecuritybg),
    incorporationcerti: str(payload.incorporationcerti ?? payload.incorporationCertificate ?? payload.incorporationcertificate),
    statename: str(payload.statename ?? payload.state),
    cityname: str(payload.cityname ?? payload.city),
    gstin: str(payload.gstin ?? payload.gstRegistration ?? payload.gstregistration),
    firsttimeparticipant: firstTimeParticipant ? 1 : 0,
  };
}

function normalizeClientBiddingProfileFromApi(item) {
  if (!item || typeof item !== "object") return item;

  const state = item.state ?? item.statename ?? "";
  const city = item.city ?? item.cityname ?? "";
  const performanceSecurity =
    item.performancesecurity ?? item.performancesecuritybg ?? item.performanceSecurityBg ?? "";
  const highestWorkValue = item.highestworkvalue ?? item.similarworkvalue ?? item.highestWorkValue ?? "";
  const projectsCompleted = item.projectscompleted ?? item.similarprojects ?? item.projectsCompleted ?? "";
  const incorporationCertificate =
    item.incorporationcerti ?? item.incorporationcertificate ?? item.incorporationCertificate ?? "";
  const gstin = item.gstin ?? item.gstregistration ?? item.gstRegistration ?? "";
  const specificLicense = item.specificlicense ?? item.specificLicense ?? item.isocertification ?? "";
  const location =
    item.location ||
    [city, state].filter((part) => String(part || "").trim() !== "").join(", ");

  return {
    ...item,
    state,
    statename: item.statename ?? state,
    city,
    cityname: item.cityname ?? city,
    location,
    performancesecurity: performanceSecurity,
    performancesecuritybg: item.performancesecuritybg ?? performanceSecurity,
    highestworkvalue: highestWorkValue,
    similarworkvalue: item.similarworkvalue ?? highestWorkValue,
    projectscompleted: projectsCompleted,
    similarprojects: item.similarprojects ?? projectsCompleted,
    incorporationcerti: incorporationCertificate,
    incorporationcertificate: item.incorporationcertificate ?? incorporationCertificate,
    gstin,
    gstregistration: item.gstregistration ?? gstin,
    specificlicense: specificLicense,
    isocertification: item.isocertification ?? specificLicense,
    firsttimeparticipant:
      item.firsttimeparticipant ??
      item.firstTimeParticipant ??
      item.first_time_participant ??
      0,
  };
}

/**
 * Delete client bidding profile (DELETE /client-bidding-profile/{id})
 * @param {number|string} profileId
 * @returns {Promise<Object>}
 */
export async function deleteClientBiddingProfile(profileId) {
  try {
    const id = Number(profileId);
    if (!Number.isFinite(id) || id <= 0) {
      throw { message: "Invalid bidding profile id", status: 400 };
    }
    const response = await http.delete(`/client-bidding-profile/${id}`);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error deleting client bidding profile:", error);
    if (error.response) {
      throw {
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to delete bidding profile",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: error?.message || "Network error. Please try again.",
      status: error?.status || 0,
    };
  }
}

/**
 * Fetch pie chart statistics for a supplier
 * @param {string} supplierName - Name of the supplier
 * @returns {Promise<Object>} Object containing participatedTenders, l1Tenders, and totalAwardedValue
 */
export async function fetchPieChartStats(supplierName) {
  try {
    if (!supplierName || !supplierName.trim()) {
      console.warn("fetchPieChartStats: supplierName is required");
      return {
        participatedTenders: 0,
        l1Tenders: 0,
        totalAwardedValue: "0 Cr"
      };
    }

    // Endpoint: /suppliers/pie-chart-stats?bidder_name=...
    const params = {
      bidder_name: supplierName.trim()
    };

    console.log("Fetching pie chart stats for supplier:", supplierName.trim());
    console.log("API params:", params);

    const response = await http.get("/suppliers/pie-chart-stats", { params });

    console.log("Pie chart stats API response:", response);
    console.log("Response data:", response?.data);
    console.log("Response data type:", typeof response?.data);

    // Handle different response formats
    // The data might be directly in response.data or nested in response.data.data
    let data = response?.data;

    console.log("Initial data:", data);
    console.log("Initial data keys:", data ? Object.keys(data) : 'no keys');

    // Check if data is nested
    if (data && data.data && typeof data.data === 'object') {
      console.log("Data is nested, extracting from data.data");
      data = data.data;
    }

    // If data is an array, take first element
    if (Array.isArray(data) && data.length > 0) {
      console.log("Data is array, taking first element");
      data = data[0];
    }

    console.log("Processed data:", data);
    console.log("Processed data keys:", data ? Object.keys(data) : 'no keys');
    console.log("participated_tenders value:", data?.participated_tenders);
    console.log("l1_tenders value:", data?.l1_tenders);
    console.log("total_awarded_value value:", data?.total_awarded_value);

    // Expected response format:
    // { bidder_name: "DUTTA ENTERPRISE", participated_tenders: 126, l1_tenders: 2, total_awarded_value: 1482353.68 }

    // Ensure we have valid data
    if (!data || typeof data !== 'object') {
      console.warn("Invalid data structure received:", data);
      return {
        participatedTenders: 0,
        l1Tenders: 0,
        totalAwardedValue: "0 Cr"
      };
    }

    // Extract values - handle both snake_case and camelCase
    const participatedTenders = data.participated_tenders ?? data.participatedTenders ?? 0;
    const l1Tenders = data.l1_tenders ?? data.l1Tenders ?? 0;
    const totalAwardedValueNum = data.total_awarded_value ?? data.totalAwardedValue ?? 0;

    console.log("Extracted values:", {
      participatedTenders,
      l1Tenders,
      totalAwardedValueNum
    });

    // Convert total_awarded_value (number) to "X Cr" format
    const totalAwardedValueInCr = Number(totalAwardedValueNum) / 10000000; // Convert to crores
    const formattedValue = totalAwardedValueInCr > 0
      ? `${totalAwardedValueInCr.toFixed(2)} Cr`
      : "0 Cr";

    const result = {
      participatedTenders: Number(participatedTenders) || 0,
      l1Tenders: Number(l1Tenders) || 0,
      totalAwardedValue: formattedValue
    };

    console.log("Final result:", result);

    return result;
  } catch (error) {
    console.error("Error fetching pie chart stats:", error);
    if (error.response) {
      console.error("Error response status:", error.response.status);
      console.error("Error response data:", error.response.data);
      console.error("Full error response:", error.response);
    } else if (error.request) {
      console.error("No response received:", error.request);
    } else {
      console.error("Error setting up request:", error.message);
    }
    // Return default values instead of null
    return {
      participatedTenders: 0,
      l1Tenders: 0,
      totalAwardedValue: "0 Cr"
    };
  }
}

/**
 * Department-wise analytical report
 * POST /dashboard/analytical-report/department-wise
 * @param {Object} payload - bidder_name, category[], state[], department[], ownership[], date_from, date_to, contract_value_min, contract_value_max, page, limit
 * @returns {Promise<{ data: Array<{ department_name, participated_tenders, awarded_tenders }>, total, page, limit }>}
 */
function buildAnalyticalReportBody(payload) {
  const rawSubCategory = Array.isArray(payload?.sub_category)
    ? payload.sub_category
    : (payload?.sub_category
        ? [payload.sub_category]
        : (Array.isArray(payload?.category) ? payload.category : (payload?.category ? [payload.category] : [])));
  const rawProduct = Array.isArray(payload?.product) ? payload.product : (payload?.product ? [payload.product] : []);
  const rawKeywords = Array.isArray(payload?.keywords)
    ? payload.keywords
    : (payload?.keywords
        ? [payload.keywords]
        : (Array.isArray(payload?.keyword) ? payload.keyword : (payload?.keyword ? [payload.keyword] : [])));
  const rawState = Array.isArray(payload?.state) ? payload.state : (payload?.state ? [payload.state] : []);
  const rawZone = Array.isArray(payload?.zone) ? payload.zone : (payload?.zone ? [payload.zone] : []);
  const rawCity = Array.isArray(payload?.city) ? payload.city : (payload?.city ? [payload.city] : []);
  const rawDepartment = Array.isArray(payload?.department) ? payload.department : (payload?.department ? [payload.department] : []);
  const rawOwnership = Array.isArray(payload?.ownership) ? payload.ownership : (payload?.ownership ? [payload.ownership] : []);

  const normalizeList = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((x) => (typeof x === "string" ? x.trim() : String(x ?? "").trim()))
      .filter(Boolean);

  const subCategoryFiltered = filterDefaultCategory(normalizeList(rawSubCategory));
  const product = normalizeList(rawProduct);
  const keywords = normalizeList(rawKeywords);
  const state = normalizeList(rawState);
  const zone = normalizeList(rawZone);
  const city = normalizeList(rawCity);
  const department = normalizeList(rawDepartment);
  const ownership = normalizeList(rawOwnership);

  const bidderTrim =
    payload?.bidder_name != null && payload?.bidder_name !== ""
      ? String(payload.bidder_name).trim()
      : "";
  const dateFrom = payload?.date_from != null && String(payload.date_from).trim() !== "" ? String(payload.date_from).trim() : "";
  const dateTo = payload?.date_to != null && String(payload.date_to).trim() !== "" ? String(payload.date_to).trim() : "";

  const minRaw = payload?.contract_value_min;
  const maxRaw = payload?.contract_value_max;
  const minNum =
    minRaw != null && minRaw !== "" && !Number.isNaN(Number(minRaw)) ? Number(minRaw) : NaN;
  const maxNum =
    maxRaw != null && maxRaw !== "" && !Number.isNaN(Number(maxRaw)) ? Number(maxRaw) : NaN;

  const body = {
    page: payload?.page ?? 1,
    limit: payload?.limit ?? 50,
  };

  if (bidderTrim) body.bidder_name = bidderTrim;
  if (subCategoryFiltered != null && subCategoryFiltered.length > 0) body.sub_category = subCategoryFiltered;
  if (product.length > 0) body.product = product;
  if (keywords.length > 0) body.keywords = keywords;
  if (state.length > 0) body.state = state;
  if (zone.length > 0) body.zone = zone;
  if (city.length > 0) body.city = city;
  if (department.length > 0) body.department = department;
  if (ownership.length > 0) body.ownership = ownership;
  if (dateFrom) body.date_from = dateFrom;
  if (dateTo) body.date_to = dateTo;
  if (Number.isFinite(minNum) && minNum > 0) body.contract_value_min = minNum;
  if (Number.isFinite(maxNum) && maxNum > 0) body.contract_value_max = maxNum;
  if (payload?.[TENDER360_FORCE_REFRESH_KEY] === true) {
    body[TENDER360_FORCE_REFRESH_KEY] = true;
  }

  return body;
}

export async function getAnalyticalReportDepartmentWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/department-wise", body);
  return response.data;
}

/**
 * State-wise analytical report
 * POST /dashboard/analytical-report/state-wise
 * @param {Object} payload - same as department-wise (bidder_name, category[], state[], department[], ownership[], date_from, date_to, contract_value_min, contract_value_max, page, limit)
 * @returns {Promise<{ data: Array<{ state_name, participated_tenders: { count, value }, awarded_tenders: { count, value } }>, total, page, limit }>}
 */
export async function getAnalyticalReportStateWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/state-wise", body);
  return response.data;
}

/**
 * Bidder analysis analytical report
 * POST /dashboard/analytical-report/bidder-analysis
 * @param {Object} payload - same as department/state (bidder_name, category[], state[], department[], ownership[], date_from, date_to, contract_value_min, contract_value_max, page, limit)
 * @returns {Promise<{ data: Array<{ bidder_name, participated_tenders, awarded_tenders, lost_tenders, winning_ratio }>, total, page, limit }>}
 */
export async function getAnalyticalReportBidderAnalysis(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/bidder-analysis", body);
  return response.data;
}

/**
 * Month-wise analytical report
 * POST /dashboard/analytical-report/month-wise
 * @param {Object} payload - same as department/state/bidder
 * @returns {Promise<{ data: Array<{ month, participated_tenders, awarded_tenders, winning_ratio }>, total, page, limit }>}
 */
export async function getAnalyticalReportMonthWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/month-wise", body);
  return response.data;
}

/**
 * Year-wise analytical report
 * POST /dashboard/analytical-report/year-wise
 * @param {Object} payload - same as department/state/bidder/month
 * @returns {Promise<{ data: Array<{ year, participated_count, participated_value, awarded_count, awarded_value, winning_ratio }>, total, page, limit }>}
 */
export async function getAnalyticalReportYearWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/year-wise", body);
  return response.data;
}

/**
 * Contract value-wise analytical report
 * POST /dashboard/analytical-report/contract-value-wise
 * @param {Object} payload - same as department/state/bidder/month/year
 * @returns {Promise<{ data: Array<{ value_range, participated_tenders, awarded_tenders, winning_ratio }>, total, page, limit }>}
 */
export async function getAnalyticalReportContractValueWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/contract-value-wise", body);
  return response.data;
}

/**
 * Ownership-wise analytical report
 * POST /dashboard/analytical-report/ownership-wise
 * @param {Object} payload - same filters as other analytical reports (page, limit, etc.)
 */
export async function getAnalyticalReportOwnershipWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/ownership-wise", body);
  return response.data;
}

/**
 * Category-wise analytical report
 * POST /dashboard/analytical-report/category-wise
 * @param {Object} payload - same filters as other analytical reports (page, limit, etc.)
 * @returns {Promise<{ data: Array<{ industry?, participated_tenders, awarded_tenders, winning_ratio }>, total, page, limit }>}
 */
export async function getAnalyticalReportCategoryWise(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/category-wise", body);
  return response.data;
}

/**
 * Top 5 competitors analytical report
 * POST /dashboard/analytical-report/top-5-competitors
 * @param {Object} payload - same filter fields as other analytical reports (no page/limit)
 * @returns {Promise<{ data: Array<{ competitor_name, participated_tenders, participated_value, awarded_tenders, awarded_value, winning_ratio }> }>}
 */
export async function getAnalyticalReportTop5Competitors(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/top-5-competitors", body);
  return response.data;
}

/**
 * All competitors analytical report (paginated)
 * POST /dashboard/analytical-report/all-competitors
 * @param {Object} payload - same as other reports with page, limit
 * @returns {Promise<{ data: Array<{ competitor_name, participated_tenders, participated_value, awarded_tenders, awarded_value, winning_ratio }>, total, page, limit }>}
 */
export async function getAnalyticalReportAllCompetitors(payload) {
  const body = buildAnalyticalReportBody(payload);
  const response = await cachedTender360Post("/dashboard/analytical-report/all-competitors", body);
  return response.data;
}

/**
 * Reset user password.
 * POST /users/{user_id}/reset-password â€” validates existing password, then updates to new password.
 * @param {string|number} userId - User ID (path parameter)
 * @param {Object} payload
 * @param {string} payload.existing_password - Current / existing password
 * @param {string} payload.new_password - New password
 * @returns {Promise<Object>} API response
 */
export async function resetUserPassword(userId, payload) {
  if (userId == null || userId === "") {
    throw { message: "User ID is required to reset password.", status: 0 };
  }
  try {
    const body = {
      existing_password: payload.existing_password,
      new_password: payload.new_password,
    };
    const response = await http.post(`/users/${userId}/reset-password`, body);
    return response.data;
  } catch (error) {
    if (error.response) {
      const msg =
        error.response.data?.message ||
        error.response.data?.error ||
        "Current password is incorrect or reset failed.";
      throw { message: msg, status: error.response.status, data: error.response.data };
    }
    throw { message: "Network error. Please try again.", status: 0 };
  }
}

/**
 * Submit user feedback â€“ POST /feedback/{userid}
 * @param {string|number|null} userId - User ID (path param); use "0" for anonymous if API allows
 * @param {Object} payload
 * @param {number} payload.stars - Star rating (1â€“5)
 * @param {string} [payload.feedback] - Optional feedback text
 * @param {number} [payload.isDisplay=1] - Whether to display (default 1)
 * @returns {Promise<{ message: string, id: number }>} API response
 */
export async function submitFeedback(userId, payload) {
  const userid = userId != null && userId !== "" ? String(userId) : "0";
  try {
    const body = {
      stars: Number(payload.stars) || 0,
      feedback: String(payload.feedback || "").trim(),
      isDisplay: payload.isDisplay !== undefined ? Number(payload.isDisplay) : 1,
    };
    const response = await http.post(`/feedback/${userid}`, body);
    return response.data;
  } catch (error) {
    if (error.response) {
      const msg =
        error.response.data?.message ||
        error.response.data?.detail ||
        (typeof error.response.data === "string" ? error.response.data : null) ||
        error?.message ||
        "Failed to submit feedback.";
      throw { message: msg, status: error.response.status, data: error.response.data };
    }
    throw { message: "Network error. Please try again.", status: 0 };
  }
}

/**
 * Build payload for Contact Us / Book Demo APIs (form camelCase â†’ API snake_case)
 * @param {Object} formData - { name, companyName, mobile, email, subject, message }
 * @returns {{ name: string, companyname: string, mobileno: string, email: string, subject: string, message: string }}
 */
function buildContactPayload(formData) {
  return {
    name: String(formData.name || "").trim(),
    companyname: String(formData.companyName || "").trim(),
    mobileno: String(formData.mobile || "").trim(),
    email: String(formData.email || "").trim(),
    subject: String(formData.subject || "").trim(),
    message: String(formData.message || "").trim(),
  };
}

/**
 * Contact Us â€“ POST /contact-us/
 * @param {Object} formData - { name, companyName, mobile, email, subject, message }
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function submitContactUs(formData) {
  try {
    const payload = buildContactPayload(formData);
    const response = await http.post("/contact-us/", payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to submit. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Book Demo â€“ POST /book-demo/
 * @param {Object} formData - { name, companyName, mobile, email, subject, message }
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function submitBookDemo(formData) {
  try {
    const payload = buildContactPayload(formData);
    const response = await http.post("/book-demo/", payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to submit. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Log Complaint â€“ POST /complaints/
 * @param {Object} formData - { company_name, subject, complaint_to, description, user_id }
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function submitLogComplaint(formData) {
  try {
    const uidRaw = formData?.user_id != null ? String(formData.user_id).trim() : "";
    const uidNum = uidRaw ? parseInt(uidRaw, 10) : NaN;
    const uid = !Number.isNaN(uidNum) && uidNum > 0 ? uidNum : 0;

    const payload = {
      uid,
      subject: String(formData?.subject || "").trim(),
      complaintto: String(formData?.complaint_to || "").trim(),
      description: String(formData?.description || "").trim(),
    };

    const response = await http.post("/complaints/", payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to submit complaint. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Apex Expert Connect â€“ POST /apexexpertconnect/{userid}
 * @param {string|number|null} userId - User ID (path param); use "0" for anonymous if API allows
 * @param {Object} formData - { regarding, remarks } (regarding sent as "regards" in payload)
 * @returns {Promise<{ success: boolean, data?: { message: string, id: number }, error?: string }>}
 */
export async function submitApexExpertConnect(userId, formData) {
  const userid = userId != null && userId !== "" ? String(userId) : "0";
  try {
    const payload = {
      regards: String(formData.regarding ?? formData.regards ?? "").trim(),
      remarks: String(formData.remarks || "").trim(),
    };
    const response = await http.post(`/apexexpertconnect/${userid}`, payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to submit. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Upgrade Plan Inquiry â€“ POST /upgrade-plan-inquiry/
 * @param {Object} formData - { userId, companyName, contactPerson, mobile, emailId, existingPlan, upgradePlan }
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function submitUpgradePlanInquiry(formData) {
  try {
    const uidRaw = formData.userId != null ? String(formData.userId).trim() : "";
    const uidNum = uidRaw ? parseInt(uidRaw, 10) : NaN;
    const uid = !Number.isNaN(uidNum) && uidNum >= 1 ? uidNum : 0;
    if (!uid) {
      return { success: false, error: "User ID (uid) is required. Please provide a valid User ID." };
    }
    const payload = {
      uid,
      companyname: String(formData.companyName ?? "").trim(),
      contactperson: String(formData.contactPerson ?? "").trim(),
      mobileno: String(formData.mobile ?? "").trim(),
      email: String(formData.emailId ?? "").trim(),
      existingplan: String(formData.existingPlan ?? "").trim(),
      upgradetoplan: String(formData.upgradePlan ?? "").trim(),
    };
    const response = await http.post("/upgrade-plan-inquiry/", payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to submit. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Query-module API expects `zone` as a string; the form may send an array for multi-select (joined for the wire).
 * @param {string|string[]|undefined|null} value
 * @returns {string|undefined}
 */
function normalizeQueryModuleLocationField(value) {
  if (value == null || value === "") return undefined;
  if (Array.isArray(value)) {
    const parts = value.map((x) => String(x ?? "").trim()).filter(Boolean);
    return parts.length ? parts.join(",") : undefined;
  }
  const s = String(value).trim();
  return s || undefined;
}

/**
 * Query-module API expects `state` and `city` as string arrays.
 * @param {string|string[]|undefined|null} value
 * @returns {string[]|undefined}
 */
function normalizeQueryModuleStateCityList(value) {
  if (value == null || value === "") return undefined;
  if (Array.isArray(value)) {
    const parts = value.map((x) => String(x ?? "").trim()).filter(Boolean);
    return parts;
  }
  const s = String(value).trim();
  if (!s) return undefined;
  if (s.includes(",")) {
    const parts = s.split(",").map((x) => x.trim()).filter(Boolean);
    return parts;
  }
  return [s];
}

function queryModuleClientErrorMessage(error) {
  const data = error?.response?.data;
  if (data == null) {
    return error?.message ? String(error.message) : "";
  }
  if (typeof data === "string") return data;
  if (data.message) return String(data.message);
  if (data.error && typeof data.error === "string") return data.error;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    const first = data.detail[0];
    if (first && typeof first.msg === "string") return first.msg;
    if (first && typeof first.message === "string") return first.message;
  }
  return error?.message ? String(error.message) : "";
}

function normalizeWordSearchType(value) {
  const normalized = String(value || "").trim();
  return Object.values(WORD_SEARCH_TYPE).includes(normalized)
    ? normalized
    : WORD_SEARCH_TYPE.WORD_SEARCH;
}

function normalizeOptionalWordSearchType(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return undefined;
  return Object.values(WORD_SEARCH_TYPE).includes(normalized)
    ? normalized
    : undefined;
}

/**
 * Map a GET `/query-module/` list row into the shape expected by {@link updateQueryModule}.
 * @param {Object} query
 * @param {string|number} userId
 * @returns {Object}
 */
function mapQueryModuleListRowToUpdateFormData(query, userId) {
  const uidNum = Number(query?.uid ?? userId);
  const uid = Number.isFinite(uidNum) ? uidNum : Number(userId) || 0;
  const toStrArr = (val) => {
    if (Array.isArray(val)) {
      return val.map((x) => String(x ?? "").trim()).filter(Boolean);
    }
    if (val == null || val === "") return [];
    const s = String(val).trim();
    if (!s) return [];
    if (s.includes(",")) {
      return s.split(",").map((x) => x.trim()).filter(Boolean);
    }
    return [s];
  };
  const subcategory = toStrArr(query?.subcategory);
  const product = toStrArr(query?.product);
  const keywords = toStrArr(query?.keywords);
  const notinkeywords = toStrArr(query?.notinkeywords);
  const newkeywords = toStrArr(query?.newkeywords);
  const toBool = (v) => v === 1 || v === true || v === "1";

  return {
    uid,
    querymodule: String(query?.querymodule ?? "").trim() || "tender",
    querytitle: String(query?.querytitle ?? "").trim() || "Untitled query",
    subcategory,
    product,
    keywords,
    notinkeywords,
    zone: query?.zone,
    state: query?.state,
    city: query?.city,
    minvalue: query?.minvalue,
    maxvalue: query?.maxvalue,
    valueUnit: query?.valueunit ?? query?.valueUnit ?? query?.value_unit,
    emdexemption: toBool(query?.emdexemption ?? query?.emd_exemption),
    startupexemption: toBool(query?.startupexemption ?? query?.startup_exemption),
    tendersource: query?.tendersource ?? query?.tender_source,
    keyword_search_type:
      query?.keyword_search_type ??
      query?.keywordsearchtype ??
      query?.keywordSearchType ??
      "Relevant Word",
    word_search_type: normalizeOptionalWordSearchType(
      query?.word_search_type ?? query?.wordsearchtype ?? query?.wordSearchType
    ),
    newkeywords,
    documentavailable: query?.documentavailable ?? query?.document_available,
    isapexsuggestion: query?.isapexsuggestion ?? query?.is_apex_suggestion,
    tender_type: (() => {
      const raw =
        query?.tender_type ??
        query?.tenderType ??
        query?.type ??
        query?.select_type ??
        "";
      const s = String(raw).trim().toLowerCase();
      return s === "buy" || s === "sell" || s === "contract" ? s : "";
    })(),
  };
}

/**
 * Query Module â€“ POST /query-module/
 * @param {Object} formData - Query module form data
 * @param {number} formData.uid - User ID (default: 0)
 * @param {string} formData.querymodule - Query module type (tender/result/boq)
 * @param {string} formData.querytitle - Query title
 * @param {string[]} formData.subcategory - Array of subcategories
 * @param {string[]} formData.product - Array of products
 * @param {string[]} formData.keywords - Array of keywords
 * @param {string[]} formData.notinkeywords - Array of exclusion keywords
 * @param {string} formData.zone - Zone selection
 * @param {string|string[]} formData.state - State selection (sent as string[] to the API)
 * @param {string|string[]} formData.city - City selection (sent as string[] to the API)
 * @param {number} formData.minvalue - Minimum value
 * @param {number} formData.maxvalue - Maximum value
 * @param {boolean} formData.emdexemption - EMD exemption flag
 * @param {boolean} formData.startupexemption - Startup exemption flag
 * @param {string} formData.tendersource - Tender source (both/gem/nongem)
 * @param {string} [formData.tender_type] - buy | sell | contract (optional)
 * @param {number} [formData.documentavailable] - Sent only when not default (1)
 * @param {number} [formData.isapexsuggestion] - Sent only when not default (1)
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function submitQueryModule(formData) {
  try {
    // Helper function to convert value based on unit
    const convertValue = (value, unit) => {
      if (value == null || value === "" || isNaN(Number(value))) return undefined;
      const numValue = Number(value);
      if (numValue <= 0) return undefined;
      if (unit === "thousand") return numValue * 1000;
      if (unit === "lakh") return numValue * 100000;
      if (unit === "crore") return numValue * 10000000;
      return numValue;
    };

    const hasNewkeywordsField = Object.prototype.hasOwnProperty.call(formData, "newkeywords");
    const newkeywordsFiltered = Array.isArray(formData.newkeywords)
      ? formData.newkeywords.filter(Boolean)
      : [];
    const hasWordSearchTerms = newkeywordsFiltered.length > 0;
    const normalizedWordSearchType = normalizeOptionalWordSearchType(
      formData.word_search_type ?? formData.wordsearchtype
    );
    const optionalNonDefaultInt = (value, defaultNum) => {
      if (value == null || value === "") return undefined;
      const n = Number(value);
      if (Number.isNaN(n) || n === defaultNum) return undefined;
      return n;
    };

    const payload = {
      uid: formData.uid != null ? Number(formData.uid) : 0,
      querymodule: String(formData.querymodule ?? "").trim(),
      querytitle: String(formData.querytitle ?? "").trim(),
      subcategory: Array.isArray(formData.subcategory) ? formData.subcategory.filter(Boolean) : [],
      product: Array.isArray(formData.product) ? formData.product.filter(Boolean) : [],
      keywords: Array.isArray(formData.keywords) ? formData.keywords.filter(Boolean) : [],
      notinkeywords: Array.isArray(formData.notinkeywords) ? formData.notinkeywords.filter(Boolean) : [],
      zone: normalizeQueryModuleLocationField(formData.zone),
      state: normalizeQueryModuleStateCityList(formData.state),
      city: normalizeQueryModuleStateCityList(formData.city),
      minvalue: convertValue(formData.minvalue, formData.valueUnit),
      maxvalue: convertValue(formData.maxvalue, formData.valueUnit),
      valueunit:
        formData.valueUnit && String(formData.valueUnit).trim() !== ""
          ? String(formData.valueUnit).trim().toLowerCase()
          : undefined,
      emdexemption: formData.emdexemption === true ? 1 : undefined,
      startupexemption: formData.startupexemption === true ? 1 : undefined,
      documentavailable: optionalNonDefaultInt(formData.documentavailable, 1),
      tendersource: formData.tendersource && formData.tendersource !== "both" ? String(formData.tendersource).trim() : undefined,
      keywordsearchtype: formData.keyword_search_type && String(formData.keyword_search_type).trim() !== "" ? String(formData.keyword_search_type).trim() : undefined,
      wordsearchtype: hasNewkeywordsField
        ? hasWordSearchTerms
          ? normalizedWordSearchType
          : null
        : normalizedWordSearchType,
      newkeywords: hasNewkeywordsField
        ? hasWordSearchTerms
          ? newkeywordsFiltered
          : null
        : undefined,
      isapexsuggestion: optionalNonDefaultInt(formData.isapexsuggestion, 1),
      tender_type:
        formData.tender_type && String(formData.tender_type).trim() !== ""
          ? String(formData.tender_type).trim().toLowerCase()
          : undefined,
    };

    // Remove undefined values to keep payload clean
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    const response = await http.post("/query-module/", payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to submit query. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Update Query Module â€“ PUT /query-module/{queryId}
 * @param {number|string} queryId - Query ID
 * @param {Object} formData - Query module form data (same structure as submitQueryModule)
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function updateQueryModule(queryId, formData) {
  try {
    if (!queryId) {
      return { success: false, error: "Query ID is required." };
    }

    // Helper function to convert value based on unit
    const convertValue = (value, unit) => {
      if (!value || !unit) return undefined;
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return undefined;

      switch (unit.toLowerCase()) {
        case "thousand":
          return numValue * 1000;
        case "lakh":
          return numValue * 100000;
        case "crore":
          return numValue * 10000000;
        default:
          return numValue;
      }
    };

    const hasNewkeywordsField = Object.prototype.hasOwnProperty.call(formData, "newkeywords");
    const newkeywordsFiltered = Array.isArray(formData.newkeywords)
      ? formData.newkeywords.filter(Boolean)
      : [];
    const hasWordSearchTerms = newkeywordsFiltered.length > 0;
    const normalizedWordSearchType = normalizeOptionalWordSearchType(
      formData.word_search_type ?? formData.wordsearchtype
    );
    const optionalNonDefaultInt = (value, defaultNum) => {
      if (value == null || value === "") return undefined;
      const n = Number(value);
      if (Number.isNaN(n) || n === defaultNum) return undefined;
      return n;
    };

    const rawMin = formData.valueUnit ? convertValue(formData.minvalue, formData.valueUnit) : formData.minvalue;
    const rawMax = formData.valueUnit ? convertValue(formData.maxvalue, formData.valueUnit) : formData.maxvalue;
    const minvalue =
      rawMin != null && rawMin !== "" && Number.isFinite(Number(rawMin)) && Number(rawMin) > 0
        ? rawMin
        : undefined;
    const maxvalue =
      rawMax != null && rawMax !== "" && Number.isFinite(Number(rawMax)) && Number(rawMax) > 0
        ? rawMax
        : undefined;

    const payload = {
      uid: formData.uid,
      querymodule: formData.querymodule,
      querytitle: formData.querytitle,
      subcategory: Array.isArray(formData.subcategory) ? formData.subcategory : [],
      product: Array.isArray(formData.product) ? formData.product : [],
      keywords: Array.isArray(formData.keywords) ? formData.keywords : [],
      notinkeywords: Array.isArray(formData.notinkeywords) ? formData.notinkeywords : [],
      zone: normalizeQueryModuleLocationField(formData.zone),
      state: normalizeQueryModuleStateCityList(formData.state),
      city: normalizeQueryModuleStateCityList(formData.city),
      minvalue,
      maxvalue,
      valueunit:
        formData.valueUnit && String(formData.valueUnit).trim() !== ""
          ? String(formData.valueUnit).trim().toLowerCase()
          : undefined,
      emd_exemption: formData.emdexemption ? 1 : undefined,
      startup_exemption: formData.startupexemption ? 1 : undefined,
      tender_source:
        formData.tendersource && String(formData.tendersource).trim() !== "" && formData.tendersource !== "both"
          ? String(formData.tendersource).trim()
          : undefined,
      keywordsearchtype: formData.keyword_search_type && String(formData.keyword_search_type).trim() !== "" ? String(formData.keyword_search_type).trim() : undefined,
      wordsearchtype: hasNewkeywordsField
        ? hasWordSearchTerms
          ? normalizedWordSearchType
          : null
        : normalizedWordSearchType,
      newkeywords: hasNewkeywordsField
        ? hasWordSearchTerms
          ? newkeywordsFiltered
          : null
        : undefined,
      document_available: optionalNonDefaultInt(formData.documentavailable, 1),
      is_apex_suggestion: optionalNonDefaultInt(formData.isapexsuggestion, 1),
      tender_type:
        formData.tender_type && String(formData.tender_type).trim() !== ""
          ? String(formData.tender_type).trim().toLowerCase()
          : undefined,
    };

    if (formData.ismainquery === 1 || formData.ismainquery === true) {
      payload.ismainquery = 1;
    } else if (formData.ismainquery === 0 || formData.ismainquery === false) {
      payload.ismainquery = 0;
    }

    // Remove undefined values
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    const response = await http.put(`/query-module/${queryId}`, payload);
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      queryModuleClientErrorMessage(error) || "Failed to update query. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Fetch Query Module List â€“ GET /query-module/
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 100)
 * @param {string|number|null|undefined} userId - Optional; when provided, sent as uid query param to filter user-specific queries
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array }>}
 */
export async function fetchQueryModuleList(page = 1, limit = 100, userId = null) {
  try {
    const params = {
      page,
      limit,
    };

    // Add user_id parameter if userId is provided (matching pattern from other GET endpoints)
    if (typeof userId !== "undefined" && userId != null && userId !== "") {
      params.user_id = String(userId);
    }

    console.log("ðŸ”µ fetchQueryModuleList - userId received:", userId);
    console.log("ðŸ”µ fetchQueryModuleList - params being sent:", params);

    const response = await http.get("/query-module/", { params });
    return {
      total: response?.data?.total ?? 0,
      page: response?.data?.page ?? page,
      limit: response?.data?.limit ?? limit,
      data: Array.isArray(response?.data?.data) ? response.data.data : [],
    };
  } catch (error) {
    console.error("Error fetching query module list:", error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to fetch queries. Please try again.";
    throw new Error(message);
  }
}

function normalizeDashboardSavedQueryList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        String(
          item && typeof item === "object"
            ? item.value ?? item.label ?? ""
            : item ?? "",
        ).trim(),
      )
      .filter(Boolean);
  }
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function isDashboardSavedQueryMain(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  return value === true || value === 1 || normalized === "1" || normalized === "true";
}

function isDashboardSavedQueryEnabled(value) {
  return value === true || value === 1 || String(value ?? "").trim() === "1";
}

const DASHBOARD_TENDER_QUERY_MAP_BASE = {
  tender_type: "",
  apexid: "",
  apexrid: "",
  brief: [],
  keyword: "",
  keyword_search_type: "Exact Phrase",
  category: "",
  subcategory: "",
  product: [],
  tendering_authority: "",
  departments: [],
  states: [],
  cities: [],
  statezones: "",
  isGem: undefined,
  tender_value_min: "",
  tender_value_max: "",
  publication_start: "",
  publication_end: "",
  closing_start: "",
  closing_end: "",
  contract_start: "",
  contract_end: "",
  search_bidder: "",
  search_winner: "",
  quantity_min: "",
  quantity_max: "",
  msme_exemption: false,
  startup_exemption: false,
  tender_documents_available: false,
  valueFilter: null,
  valueMin: "",
  valueMax: "",
  selectType: null,
  referenceId: "",
  zone: null,
  authorityOwnership: null,
};

function findMainDashboardTenderQueryRow(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const isMain = (q) => isDashboardSavedQueryMain(q.ismainquery);
  const rowMatchesModule = (q) => {
    const m = String(q?.querymodule || "").toLowerCase();
    return !m.includes("result") && !m.includes("boq");
  };
  return list.find((q) => isMain(q) && rowMatchesModule(q)) || null;
}

function mapQueryModuleRowToDashboardTenderFormData(
  queryData,
  { classificationRowAboveBasic = false } = {},
) {
  const formData = { ...DASHBOARD_TENDER_QUERY_MAP_BASE };

  const savedSubcategories = normalizeDashboardSavedQueryList(queryData.subcategory);
  if (savedSubcategories.length > 0) {
    formData.subcategory = savedSubcategories[0];
  }

  const savedProducts = normalizeDashboardSavedQueryList(queryData.product);
  if (savedProducts.length > 0) {
    formData.product = savedProducts;
  }

  const rawKeywordValues = [
    ...normalizeDashboardSavedQueryList(queryData.keywords),
    ...normalizeDashboardSavedQueryList(queryData.keyword),
  ];
  if (rawKeywordValues.length > 0) {
    const keywordValues = [
      ...new Set(
        rawKeywordValues.map((k) => String(k ?? "").trim()).filter(Boolean),
      ),
    ];
    formData.keyword = classificationRowAboveBasic
      ? keywordValues
      : keywordValues[0] || "";
  }

  const newkeywordsRaw =
    queryData.newkeywords ?? queryData.newkeyword ?? queryData.new_keywords;
  if (newkeywordsRaw) {
    const newkeywordValues = Array.isArray(newkeywordsRaw)
      ? newkeywordsRaw
      : [newkeywordsRaw];
    const briefs = [
      ...new Set(
        newkeywordValues.map((x) => String(x ?? "").trim()).filter(Boolean),
      ),
    ];
    if (briefs.length > 0) {
      formData.brief = briefs;
      if (
        normalizeWordSearchType(
          queryData.wordsearchtype ??
            queryData.word_search_type ??
            queryData.wordSearchType,
        ) === WORD_SEARCH_TYPE.WITHIN_FILTER
      ) {
        formData.search_within = briefs.join(", ");
      }
    }
  }
  const savedWordSearchType =
    queryData.wordsearchtype ??
    queryData.word_search_type ??
    queryData.wordsearch_type ??
    queryData.wordSearchType;
  if (savedWordSearchType != null && String(savedWordSearchType).trim() !== "") {
    formData.wordsearch_type = normalizeWordSearchType(savedWordSearchType);
  }

  const savedZone = queryData.zone ?? queryData.statezones ?? queryData.statezone;
  if (savedZone) {
    formData.statezones = savedZone;
  }

  const savedStates = normalizeDashboardSavedQueryList(
    queryData.state ?? queryData.states ?? queryData.statelist ?? queryData.state_list,
  );
  if (savedStates.length > 0) {
    const stateArr = savedStates;
    formData.states = stateArr
      .map((s) => (typeof s === "string" ? s : s?.value || s))
      .filter(Boolean);
  }

  const savedCities = normalizeDashboardSavedQueryList(
    queryData.city ?? queryData.cities ?? queryData.citylist ?? queryData.city_list,
  );
  if (savedCities.length > 0) {
    const cityArr = savedCities;
    formData.cities = cityArr
      .map((c) => (typeof c === "string" ? c : c?.value || c))
      .filter(Boolean);

    if (!formData.states || formData.states.length === 0) {
      const firstCity = formData.cities[0] || "";
      const cityValue = String(firstCity).toLowerCase().trim();

      for (const [stateKey, cities] of Object.entries(indianStatesAndCities)) {
        const cityFound = cities.find(
          (c) =>
            c.value.toLowerCase() === cityValue || c.label.toLowerCase() === cityValue,
        );
        if (cityFound) {
          const stateOption = indianStates.find((s) => s.value === stateKey);
          if (stateOption) {
            formData.states = [stateOption.value];
          }
          break;
        }
      }
    }
  }

  if (queryData.minvalue || queryData.maxvalue) {
    const unit = String(
      queryData.valueunit ?? queryData.valueUnit ?? queryData.value_unit ?? "",
    ).toLowerCase().trim();
    const minRupees = parseFloat(queryData.minvalue || 0);
    const maxRupees = parseFloat(queryData.maxvalue || 0);

    if (minRupees > 0 || maxRupees > 0) {
      formData.tender_value_min = minRupees > 0 ? String(minRupees) : "";
      formData.tender_value_max = maxRupees > 0 ? String(maxRupees) : "";

      let minDisplay = minRupees;
      let maxDisplay = maxRupees;
      let filterValue = null;

      if (unit === "crore") {
        minDisplay = minRupees / 10000000;
        maxDisplay = maxRupees / 10000000;
        filterValue = "crore";
      } else if (unit === "lakh") {
        minDisplay = minRupees / 100000;
        maxDisplay = maxRupees / 100000;
        filterValue = "lakh";
      } else if (unit === "thousand") {
        minDisplay = minRupees / 1000;
        maxDisplay = maxRupees / 1000;
        filterValue = "thousand";
      }

      if (filterValue) {
        formData.valueFilter =
          VALUE_FILTER_OPTIONS.find((opt) => opt.value === filterValue) || null;
      }

      formData.valueMin = minDisplay > 0 ? String(minDisplay) : "";
      formData.valueMax = maxDisplay > 0 ? String(maxDisplay) : "";
    }
  }

  const tenderSource = String(
    queryData.tendersource ?? queryData.tender_source ?? "",
  ).trim().toLowerCase();
  if (tenderSource === "gem") {
    formData.isGem = true;
  } else if (tenderSource === "nongem") {
    formData.isGem = false;
  } else {
    formData.isGem = undefined;
  }

  formData.msme_exemption = isDashboardSavedQueryEnabled(
    queryData.emdexemption ?? queryData.emd_exemption,
  );
  formData.startup_exemption = isDashboardSavedQueryEnabled(
    queryData.startupexemption ?? queryData.startup_exemption,
  );

  formData.tender_documents_available = isDashboardSavedQueryEnabled(
    queryData.documentavailable ?? queryData.document_available,
  );

  const kwst =
    queryData.keywordsearchtype ||
    queryData.keyword_search_type ||
    queryData.keywordSearchType;
  formData.keyword_search_type =
    kwst && String(kwst).trim() ? String(kwst).trim() : "Exact Phrase";

  return formData;
}

/**
 * Plan-default tender form merged with the user's main Indian Tenders saved query (when present).
 * Used by dashboard initial load; advanced search panel may stay collapsed.
 */
export async function fetchMergedDashboardTenderFormFromMainQuery(
  userId,
  defaultFormDataForApi,
  { classificationRowAboveBasic = true } = {},
) {
  const base =
    defaultFormDataForApi && typeof defaultFormDataForApi === "object"
      ? { ...defaultFormDataForApi }
      : {};
  if (!userId) {
    return base;
  }
  try {
    const result = await fetchQueryModuleList(1, 100, userId);
    const list = result.data && Array.isArray(result.data) ? result.data : [];
    const main = findMainDashboardTenderQueryRow(list);
    if (!main) {
      return base;
    }
    const mapped = mapQueryModuleRowToDashboardTenderFormData(main, {
      classificationRowAboveBasic,
    });
    return { ...base, ...mapped };
  } catch (e) {
    console.error("Error loading main query for dashboard tenders:", e);
    return base;
  }
}

function findMainDashboardResultQueryRow(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const isMain = (q) => isDashboardSavedQueryMain(q.ismainquery);
  const rowMatchesModule = (q) => {
    const m = String(q?.querymodule || "").toLowerCase();
    return m.includes("result") && !m.includes("boq");
  };
  return list.find((q) => isMain(q) && rowMatchesModule(q)) || null;
}

/**
 * Plan-default result form merged with the user's main Indian Results saved query (when present).
 */
export async function fetchMergedDashboardResultFormFromMainQuery(
  userId,
  defaultFormDataForApi,
  { classificationRowAboveBasic = true } = {},
) {
  const base =
    defaultFormDataForApi && typeof defaultFormDataForApi === "object"
      ? { ...defaultFormDataForApi }
      : {};
  if (!userId) {
    return base;
  }
  try {
    const result = await fetchQueryModuleList(1, 100, userId);
    const list = result.data && Array.isArray(result.data) ? result.data : [];
    const main = findMainDashboardResultQueryRow(list);
    if (!main) {
      return base;
    }
    const mapped = mapQueryModuleRowToDashboardTenderFormData(main, {
      classificationRowAboveBasic,
    });
    return { ...base, ...mapped };
  } catch (e) {
    console.error("Error loading main query for dashboard results:", e);
    return base;
  }
}

function findMainDashboardBoqQueryRow(list, boqTab) {
  if (!Array.isArray(list) || list.length === 0) return null;
  const isMain = (q) => isDashboardSavedQueryMain(q.ismainquery);
  const tab = boqTab === "result" ? "result" : "tender";
  const matchesBoqTab = (q) => {
    const m = String(q?.querymodule || "").toLowerCase();
    if (!m.includes("boq")) return false;
    if (tab === "result") return m.includes("result");
    return !m.includes("result");
  };
  return list.find((q) => isMain(q) && matchesBoqTab(q)) || null;
}

function mapQueryModuleRowToDashboardBoqFormData(queryData) {
  const form = {
    boqType: null,
    apexid: "",
    apexrid: "",
    boqId: "",
    category: "",
    statezones: "",
    states: [],
    cities: [],
    valueMin: "",
    valueMax: "",
    valueFilter: "lakh",
    quantity_min: "",
    quantity_max: "",
    submission_start: "",
    submission_end: "",
    isGem: undefined,
    tenderSubcategory: "",
    product: [],
    keyword: [],
  };
  const sub = Array.isArray(queryData.subcategory)
    ? queryData.subcategory.filter(Boolean)
    : [];
  if (sub.length > 0) {
    form.category = sub[0];
    form.tenderSubcategory = sub[0];
  }
  const rawProductValues = Array.isArray(queryData.product)
    ? queryData.product
    : queryData.product != null
      ? [queryData.product]
      : [];
  if (rawProductValues.length > 0) {
    form.product = [
      ...new Set(
        rawProductValues.map((p) => String(p ?? "").trim()).filter(Boolean),
      ),
    ];
  }
  const rawKeywordValues = [
    ...(Array.isArray(queryData.keywords) ? queryData.keywords : []),
  ];
  if (rawKeywordValues.length > 0) {
    form.keyword = [
      ...new Set(
        rawKeywordValues.map((k) => String(k ?? "").trim()).filter(Boolean),
      ),
    ];
  } else if (typeof queryData.keywords === "string" && queryData.keywords.trim()) {
    form.keyword = [queryData.keywords.trim()];
  }
  const newkeywordsRaw = queryData.newkeywords ?? queryData.newkeyword;
  if (newkeywordsRaw) {
    const nkArr = Array.isArray(newkeywordsRaw) ? newkeywordsRaw : [newkeywordsRaw];
    const briefs = [
      ...new Set(nkArr.map((x) => String(x ?? "").trim()).filter(Boolean)),
    ];
    if (briefs.length > 0) {
      form.brief = briefs;
      if (normalizeWordSearchType(queryData.wordsearchtype ?? queryData.word_search_type) === WORD_SEARCH_TYPE.WITHIN_FILTER) {
        form.search_within = briefs.join(", ");
      }
    }
  }
  const zn = String(queryData.zone || "").toLowerCase().trim();
  if (zn) {
    const zoneOpt = ZONE_OPTIONS.find(
      (z) => z.value === zn || String(z.label).toLowerCase() === zn,
    );
    form.statezones = zoneOpt || { value: zn, label: zn };
  }
  if (queryData.state) {
    const stateArr = Array.isArray(queryData.state) ? queryData.state : [queryData.state];
    form.states = stateArr
      .map((s) => (typeof s === "string" ? s : s?.value || s))
      .filter(Boolean);
  }
  if (queryData.city) {
    const cityArr = Array.isArray(queryData.city) ? queryData.city : [queryData.city];
    form.cities = cityArr
      .map((c) => (typeof c === "string" ? c : c?.value || c))
      .filter(Boolean);
  }
  if (queryData.minvalue || queryData.maxvalue) {
    const unit = String(queryData.valueunit || "").toLowerCase().trim();
    const minRupees = parseFloat(queryData.minvalue || 0);
    const maxRupees = parseFloat(queryData.maxvalue || 0);
    if (minRupees > 0 || maxRupees > 0) {
      let minDisplay = minRupees;
      let maxDisplay = maxRupees;
      let filterValue = null;
      if (unit === "crore") {
        minDisplay = minRupees / 10000000;
        maxDisplay = maxRupees / 10000000;
        filterValue = "crore";
      } else if (unit === "lakh") {
        minDisplay = minRupees / 100000;
        maxDisplay = maxRupees / 100000;
        filterValue = "lakh";
      } else if (unit === "thousand") {
        minDisplay = minRupees / 1000;
        maxDisplay = maxRupees / 1000;
        filterValue = "thousand";
      }
      if (filterValue) {
        form.valueFilter = filterValue;
      }
      form.valueMin = minDisplay > 0 ? String(minDisplay) : "";
      form.valueMax = maxDisplay > 0 ? String(maxDisplay) : "";
    }
  }
  if (queryData.tendersource === "gem") {
    form.isGem = true;
  } else if (queryData.tendersource === "nongem") {
    form.isGem = false;
  } else {
    form.isGem = undefined;
  }
  return form;
}

/**
 * Plan-default BOQ advanced form merged with the user's main saved query for Tender BOQ or Result BOQ.
 */
export async function fetchMergedDashboardBoqFormFromMainQuery(
  userId,
  defaultFormDataForApi,
  { boqTab = "tender" } = {},
) {
  const base =
    defaultFormDataForApi && typeof defaultFormDataForApi === "object"
      ? { ...defaultFormDataForApi }
      : {};
  if (!userId) {
    return base;
  }
  try {
    const result = await fetchQueryModuleList(1, 100, userId);
    const list = result.data && Array.isArray(result.data) ? result.data : [];
    const main = findMainDashboardBoqQueryRow(list, boqTab);
    if (!main) {
      return base;
    }
    const mapped = mapQueryModuleRowToDashboardBoqFormData(main);
    return { ...base, ...mapped };
  } catch (e) {
    console.error("Error loading main query for dashboard BOQ:", e);
    return base;
  }
}

/**
 * Fetch user-scoped Query Module list â€“ GET /query-module/user
 * @param {string|number} userId - Required user id
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 100)
 * @returns {Promise<{ total: number, page: number, limit: number, data: Array }>}
 */
export async function fetchUserQueryModuleList(userId, page = 1, limit = 100) {
  if (typeof userId === "undefined" || userId == null || String(userId).trim() === "") {
    throw new Error("User ID is required to fetch user queries.");
  }

  try {
    const params = {
      user_id: String(userId).trim(),
      page,
      limit,
    };

    const response = await http.get("/query-module/user", { params });
    return {
      total: response?.data?.total ?? 0,
      page: response?.data?.page ?? page,
      limit: response?.data?.limit ?? limit,
      data: Array.isArray(response?.data?.data) ? response.data.data : [],
    };
  } catch (error) {
    console.error("Error fetching user query module list:", error);
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to fetch user queries. Please try again.";
    throw new Error(message);
  }
}

/**
 * Set Main Query â€“ PATCH /query-module/set-main-query/{queryId}?is_main={isMain}
 * @param {number|string} queryId - Query ID
 * @param {boolean|number} isMain - Whether to set as main query (default: true)
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function setMainQuery(queryId, isMain = true) {
  try {
    if (!queryId) {
      return { success: false, error: "Query ID is required." };
    }

    const response = await http.patch(`/query-module/set-main-query/${queryId}`, null, {
      params: {
        is_main: isMain ? 1 : 0,
      },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    const message =
      queryModuleClientErrorMessage(error) || "Failed to set main query. Please try again.";
    return { success: false, error: message };
  }
}

async function putQueryModuleIsMainLegacy(queryId, setAsMain = true) {
  const flag = setAsMain ? 1 : 0;
  try {
    const response = await http.put(`/query-module/${queryId}`, { ismainquery: flag });
    return { success: true, data: response?.data };
  } catch (putError) {
    const status = putError?.response?.status;
    const skipPatchFallback = status === 401 || status === 403;
    if (!skipPatchFallback) {
      const patchResult = await setMainQuery(queryId, setAsMain);
      if (patchResult.success) {
        return patchResult;
      }
      const patchErr =
        typeof patchResult.error === "string"
          ? patchResult.error
          : patchResult.error != null && patchResult.error !== ""
            ? String(patchResult.error)
            : "";
      return {
        success: false,
        error:
          queryModuleClientErrorMessage(putError) ||
          patchErr ||
          "Failed to update main query. Please try again.",
      };
    }
    return {
      success: false,
      error:
        queryModuleClientErrorMessage(putError) || "Failed to update main query. Please try again.",
    };
  }
}

/**
 * Set or clear this query as the user's main (favorite) query.
 * Sends a full PUT (same fields as edit flow) with `ismainquery` 1 or 0 when `queryListRow` and `userId`
 * are provided; otherwise falls back to partial PUT then PATCH {@link setMainQuery}.
 * @param {number|string} queryId - Query ID
 * @param {Object} [queryListRow] - Row from {@link fetchQueryModuleList} (recommended)
 * @param {string|number|null} [userId] - Current user id (recommended)
 * @param {boolean} [setAsMain=true] - `true` to set as main, `false` to remove from main
 * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
 */
export async function putQueryModuleIsMain(queryId, queryListRow, userId, setAsMain = true) {
  if (!queryId) {
    return { success: false, error: "Query ID is required." };
  }

  const hasContext =
    queryListRow &&
    typeof queryListRow === "object" &&
    userId != null &&
    String(userId).trim() !== "";

  if (hasContext) {
    const formData = mapQueryModuleListRowToUpdateFormData(queryListRow, userId);
    const mainFlag = setAsMain ? 1 : 0;
    const full = await updateQueryModule(queryId, { ...formData, ismainquery: mainFlag });
    if (full.success) {
      return full;
    }
    const legacy = await putQueryModuleIsMainLegacy(queryId, setAsMain);
    if (legacy.success) {
      return legacy;
    }
    const fullErr = typeof full.error === "string" ? full.error.trim() : "";
    const legErr = typeof legacy.error === "string" ? legacy.error.trim() : "";
    return {
      success: false,
      error: fullErr || legErr || "Failed to update main query. Please try again.",
    };
  }

  return putQueryModuleIsMainLegacy(queryId, setAsMain);
}

/**
 * Delete Query Module â€“ DELETE /query-module/{queryId}
 * @param {number|string} queryId - Query ID
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function deleteQueryModule(queryId) {
  try {
    if (!queryId) {
      return { success: false, error: "Query ID is required." };
    }

    await http.delete(`/query-module/${queryId}`);
    return { success: true };
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.detail ||
      (typeof error?.response?.data === "string" ? error.response.data : null) ||
      error?.message ||
      "Failed to delete query. Please try again.";
    return { success: false, error: message };
  }
}

/**
 * Submit offline payment details with receipt attachment.
 * @param {FormData} formData
 * @returns {Promise<Object>} API response
 */
export async function submitOfflinePayment(formData) {
  try {
    const response = await http.post("/user-payments/offline", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response?.data ?? response;
  } catch (error) {
    console.error("Error submitting offline payment:", error);
    if (error.response) {
      const detail = error.response?.data?.detail;
      throw {
        message: typeof detail === "string"
          ? detail
          : error.response?.data?.message || error.response?.data?.error || "Failed to submit offline payment",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

/**
 * Save user plans after offline payment submission.
 * This uses a separate backend endpoint from the online payment flow.
 * @param {Object} data
 * @param {number} data.userId
 * @param {Array} data.plans
 * @returns {Promise<Object>} API response
 */
export async function submitOfflineUserPlans(data = {}) {
  try {
    const plans = Array.isArray(data.plans) ? data.plans : [];
    const userId = data.userId != null ? Number(data.userId) : 0;
    const paymentid = data.paymentid != null ? Number(data.paymentid) : null;
    const ensureArray = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string" && value.trim() !== "") {
        return value.split(",").map((item) => item.trim()).filter(Boolean);
      }
      return [];
    };
    const ensureZoneArray = (value) => {
      if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
      if (typeof value === "string" && value.trim() !== "") return [value.trim()];
      return [];
    };
    const toStatetypeString = (value) => {
      if (value == null) return "";
      if (typeof value === "string") return value.trim();
      if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean).join(",");
      return String(value).trim();
    };

    const payload = {
      plans: plans.map((plan) => ({
        uId: userId || plan.uId || 0,
        pId: Number(plan.pId ?? plan.pid ?? 0),
        startdate: plan.startdate || "",
        enddate: plan.enddate || "",
        statetype: toStatetypeString(plan.statetype ?? plan.selectedstates),
        zone: ensureZoneArray(plan.zone),
        selectedcategories: ensureArray(plan.selectedcategories),
        servicecount: typeof plan.servicecount === "number" ? plan.servicecount : 0,
        amount: Number(plan.amount || 0),
        isactive: 1,
        activeform: 1,
        paymentid: typeof plan.paymentid === "number" ? plan.paymentid : paymentid,
      })),
    };

    const response = await http.post("/user-plans/offline/", payload);
    return response?.data ?? response;
  } catch (error) {
    console.error("Error saving offline user plans:", error);
    if (error.response) {
      const detail = error.response?.data?.detail;
      throw {
        message: typeof detail === "string"
          ? detail
          : error.response?.data?.message || error.response?.data?.error || "Failed to save offline user plans",
        status: error.response?.status,
        data: error.response?.data,
      };
    }
    throw {
      message: "Network error. Please try again.",
      status: 0,
    };
  }
}

export async function fetchDashboardAdvanceSearchTenders(payload = {}) {
  return fetchTendersAdvancedFilterDashboard({
    ...payload,
    _useDashboardAdvanceSearch: true,
  });
}

export async function fetchDashboardAdvanceSearchResults(payload = {}, tab = "today") {
  return fetchResultsAdvancedFilterDashboard({
    ...payload,
    _useDashboardAdvanceSearch: true,
    _dashboardAdvanceSearchResultTab: tab,
  });
}

export async function searchDashboardAdvanceSearchBoqLeads(payload, userId) {
  return searchDashboardBoqLeads(
    {
      ...(payload || {}),
      _useDashboardAdvanceSearch: true,
    },
    userId
  );
}
